// src/components/LearnAcademy.js
// Learning Academy V4 - COMPLETE 42 Modules + AI Tutors + Conversion System
// Full educational content preserved + AI integration + Premium upsells

import React, { useState, useRef, useEffect } from 'react';
import { X, Send, Brain, Zap, Lock, Sparkles, Gift, Crown, ArrowRight } from 'lucide-react';
import PodcastLibrary from './PodcastLibrary';
import VideoLibrary from './VideoLibrary';

// ============================================
// AI TUTOR CONFIGURATIONS
// ============================================

const AI_TUTORS = {
  main: {
    id: 'main',
    name: 'Professor IQ',
    avatar: '🎓',
    color: '#6366f1',
    greeting: "Hi! I'm Professor IQ, your personal investing mentor. Ask me anything about the markets, stocks, options, or any topic in the academy!",
    systemPrompt: `You are Professor IQ, a friendly and knowledgeable investment education assistant for InvestorQ Learning Academy. 
Your role: Explain investing concepts in simple, clear language. Use analogies and real-world examples. Be encouraging but honest about risks. Keep responses concise (2-3 paragraphs max). If asked about specific trades, remind users this is educational, not financial advice.
Personality: Patient, encouraging, uses analogies, occasionally uses relevant emojis.`
  },
  market: {
    id: 'market',
    name: 'Marcus Market',
    avatar: '📊',
    color: '#60a5fa',
    greeting: "Hey! I'm Marcus, your market analysis expert. I can explain indices, economic indicators, sentiment, and how markets move!",
    systemPrompt: `You are Marcus Market, an expert on market analysis for InvestorQ. Specialize in: Market indices (S&P 500, Dow, NASDAQ), Economic indicators (GDP, unemployment, inflation, Fed rates), Market sentiment and crowd psychology, How news moves markets. Keep explanations practical with current examples. Be concise.`
  },
  stocks: {
    id: 'stocks',
    name: 'Stella Stocks',
    avatar: '🔍',
    color: '#34d399',
    greeting: "Hi there! I'm Stella, your stock research specialist. Let me help you understand how to analyze companies!",
    systemPrompt: `You are Stella Stocks, a stock research expert for InvestorQ. Specialize in: Reading stock quotes and charts, Company fundamental analysis, Financial statements (income, balance sheet, cash flow), Sector and peer comparison. Use real company examples when helpful. Keep it practical and actionable.`
  },
  options: {
    id: 'options',
    name: 'Oscar Options',
    avatar: '📈',
    color: '#a78bfa',
    greeting: "Welcome! I'm Oscar, your options trading guide. Options can be complex, but I'll break them down step by step!",
    systemPrompt: `You are Oscar Options, an options trading educator for InvestorQ. Specialize in: Calls and puts basics, The Greeks (Delta, Gamma, Theta, Vega), GEX and market maker dynamics, Implied volatility and IV crush. Always emphasize risk management. Use payoff diagrams conceptually. Warn about leverage risks.`
  },
  portfolio: {
    id: 'portfolio',
    name: 'Portia Portfolio',
    avatar: '💼',
    color: '#818cf8',
    greeting: "Hello! I'm Portia, your portfolio management coach. Let's build and optimize your investment strategy!",
    systemPrompt: `You are Portia Portfolio, a portfolio management expert for InvestorQ. Specialize in: Portfolio construction and diversification, Asset allocation strategies, Performance measurement and benchmarking, Risk management and rebalancing. Focus on practical, actionable advice for retail investors.`
  },
  alerts: {
    id: 'alerts',
    name: 'Alex Alerts',
    avatar: '🔔',
    color: '#facc15',
    greeting: "Hey! I'm Alex, your watchlist and alerts specialist. I'll help you stay on top of your investments!",
    systemPrompt: `You are Alex Alerts, a specialist in watchlists and alerts for InvestorQ. Help with: Building effective watchlists, Setting price and news alerts, Staying informed without information overload. Keep advice practical and focused on discipline.`
  },
  altdata: {
    id: 'altdata',
    name: 'Dana Data',
    avatar: '🕵️',
    color: '#fb923c',
    greeting: "Welcome to the edge! I'm Dana, your alternative data analyst. Let's uncover signals others miss!",
    systemPrompt: `You are Dana Data, an alternative data expert for InvestorQ. Specialize in: Congress and insider trading signals, Institutional (13F) filings analysis, Unusual volume and options flow, Social sentiment analysis. Always note data limitations and delays. Emphasize these are signals to research, not trade blindly.`
  },
  etf: {
    id: 'etf',
    name: 'Ethan ETF',
    avatar: '📦',
    color: '#34d399',
    greeting: "Hi! I'm Ethan, your ETF expert. Let me show you the easiest way to diversify!",
    systemPrompt: `You are Ethan ETF, an ETF specialist for InvestorQ. Cover: ETF basics and types, Analyzing holdings and overlap, Comparing similar ETFs, Cost considerations (expense ratios). Recommend low-cost index funds for most investors. Be practical.`
  },
  global: {
    id: 'global',
    name: 'Gloria Global',
    avatar: '🌍',
    color: '#22d3ee',
    greeting: "Hello from around the world! I'm Gloria, your guide to international investing!",
    systemPrompt: `You are Gloria Global, an international investing expert for InvestorQ. Specialize in: Indian markets (NIFTY, SENSEX), Currency and forex basics, Cryptocurrency fundamentals, International ETFs and ADRs. Discuss currency risks and diversification benefits. Be balanced on crypto.`
  },
  practice: {
    id: 'practice',
    name: 'Priya Practice',
    avatar: '🎮',
    color: '#f472b6',
    greeting: "Ready to practice? I'm Priya, your simulation and AI tools guide!",
    systemPrompt: `You are Priya Practice, a paper trading and AI tools expert for InvestorQ. Help with: Paper trading best practices, Transitioning from paper to real money, Using AI for investment research, Avoiding common beginner mistakes. Emphasize that emotions change everything with real money.`
  },
  earnings: {
    id: 'earnings',
    name: 'Earl Earnings',
    avatar: '📅',
    color: '#f87171',
    greeting: "Earnings season is exciting! I'm Earl, your earnings analysis expert!",
    systemPrompt: `You are Earl Earnings, an earnings analysis specialist for InvestorQ. Cover: Earnings season timing and what to expect, Beat/miss dynamics and guidance importance, Analyzing earnings history, Post-earnings drift and reactions. Help users understand that reaction matters more than raw numbers.`
  }
};

const getTutorForCategory = (categoryId) => AI_TUTORS[categoryId] || AI_TUTORS.main;

const API_BASE = process.env.REACT_APP_API_URL || 'https://financial-analyst-backend-production-7175.up.railway.app/api';
const CHAT_ENDPOINT = `${API_BASE}/chat`;  // Uses chat.js routes

// ============================================
// FEATURE MAPPING - Connect modules to premium features
// ============================================

const FEATURE_MAP = {
  1: { feature: 'market', name: 'Live Market Dashboard', icon: '📊', route: '/market', tier: 'starter' },
  2: { feature: 'market', name: 'Real-Time Indices', icon: '📈', route: '/market', tier: 'starter' },
  3: { feature: 'fred', name: 'Economic Indicators', icon: '🏛️', route: '/market', tier: 'starter' },
  4: { feature: 'recession', name: 'Recession Tracker', icon: '⚠️', route: '/market', tier: 'pro' },
  5: { feature: 'news', name: 'Live News Feed', icon: '📰', route: '/market', tier: 'starter' },
  6: { feature: 'sentiment', name: 'Social Sentiment', icon: '👥', route: '/market', tier: 'pro' },
  7: { feature: 'search', name: 'Stock Screener', icon: '🔍', route: '/company', tier: 'starter' },
  8: { feature: 'quotes', name: 'Real-Time Quotes', icon: '💹', route: '/company', tier: 'starter' },
  9: { feature: 'charts', name: 'Interactive Charts', icon: '📉', route: '/company', tier: 'starter' },
  10: { feature: 'company', name: 'Company Deep Dive', icon: '🏢', route: '/company', tier: 'starter' },
  11: { feature: 'financials', name: 'Financial Analysis', icon: '📋', route: '/company', tier: 'pro' },
  12: { feature: 'peers', name: 'Peer Comparison', icon: '⚖️', route: '/company', tier: 'pro' },
  13: { feature: 'options', name: 'Options Chain', icon: '📞', route: '/options', tier: 'pro' },
  14: { feature: 'greeks', name: 'Greeks Calculator', icon: '🔤', route: '/options', tier: 'pro' },
  15: { feature: 'gex', name: 'GEX Dashboard', icon: '🎯', route: '/options', tier: 'pro' },
  16: { feature: 'pcr', name: 'Put/Call Ratio', icon: '📊', route: '/options', tier: 'pro' },
  17: { feature: 'iv', name: 'IV Analysis', icon: '🌊', route: '/options', tier: 'pro' },
  18: { feature: 'portfolio', name: 'Portfolio Tracker', icon: '💼', route: '/portfolio', tier: 'starter' },
  19: { feature: 'transactions', name: 'Transaction Logger', icon: '📝', route: '/portfolio', tier: 'starter' },
  20: { feature: 'holdings', name: 'Holdings Analysis', icon: '💎', route: '/portfolio', tier: 'starter' },
  21: { feature: 'performance', name: 'Performance Analytics', icon: '📈', route: '/portfolio', tier: 'pro' },
  22: { feature: 'risk', name: 'Risk Metrics', icon: '🎲', route: '/portfolio', tier: 'pro' },
  23: { feature: 'rebalance', name: 'Rebalancing Tools', icon: '⚖️', route: '/portfolio', tier: 'pro' },
  24: { feature: 'watchlist', name: 'Smart Watchlist', icon: '👀', route: '/watchlist', tier: 'starter' },
  25: { feature: 'alerts', name: 'Price Alerts', icon: '🔔', route: '/alerts', tier: 'starter' },
  26: { feature: 'newsalerts', name: 'News Alerts', icon: '📬', route: '/alerts', tier: 'pro' },
  27: { feature: 'congress', name: 'Congress Trades', icon: '🏛️', route: '/congress', tier: 'pro' },
  28: { feature: 'insider', name: 'Insider Trading', icon: '👔', route: '/insider', tier: 'pro' },
  29: { feature: 'whale', name: 'Whale Watcher', icon: '🐋', route: '/whale', tier: 'pro' },
  30: { feature: 'anomaly', name: 'Anomaly Detector', icon: '🔮', route: '/anomalies', tier: 'pro' },
  31: { feature: 'social', name: 'Sentiment Scanner', icon: '💬', route: '/sentiment', tier: 'pro' },
  32: { feature: 'etf', name: 'ETF Explorer', icon: '📦', route: '/etf', tier: 'starter' },
  33: { feature: 'etfxray', name: 'ETF X-Ray', icon: '🔬', route: '/etf', tier: 'pro' },
  34: { feature: 'etfcompare', name: 'ETF Comparator', icon: '🔄', route: '/etf', tier: 'pro' },
  35: { feature: 'india', name: 'India Markets', icon: '🇮🇳', route: '/india', tier: 'pro' },
  36: { feature: 'forex', name: 'Forex Tracker', icon: '💱', route: '/forex', tier: 'pro' },
  37: { feature: 'crypto', name: 'Crypto Dashboard', icon: '₿', route: '/crypto', tier: 'starter' },
  38: { feature: 'paper', name: 'Paper Trading', icon: '🎮', route: '/paper-trading', tier: 'starter' },
  39: { feature: 'ai', name: 'AI Assistant', icon: '🤖', route: '/ai', tier: 'pro' },
  40: { feature: 'earnings', name: 'Earnings Calendar', icon: '📅', route: '/earnings', tier: 'starter' },
  41: { feature: 'earningshistory', name: 'Earnings History', icon: '📜', route: '/earnings', tier: 'pro' },
  42: { feature: 'earningmoves', name: 'Post-Earnings Analysis', icon: '📊', route: '/earnings', tier: 'pro' }
};

const MILESTONES = [
  { modules: 1, badge: '🌱', title: 'First Step', reward: 'Unlocked: Stock Search Preview', type: 'badge' },
  { modules: 5, badge: '📚', title: 'Eager Learner', reward: '3-Day Pro Trial!', type: 'trial', days: 3 },
  { modules: 10, badge: '🎓', title: 'Knowledge Seeker', reward: '20% Off First Month', type: 'discount', percent: 20 },
  { modules: 20, badge: '🏆', title: 'Academy Graduate', reward: '30% Off + Free Week', type: 'discount', percent: 30 },
  { modules: 30, badge: '⭐', title: 'Master Investor', reward: 'Lifetime 25% Discount', type: 'lifetime', percent: 25 },
  { modules: 42, badge: '👑', title: 'InvestorQ Legend', reward: 'Free Premium Month!', type: 'free', days: 30 },
  { modules: 47, badge: '🏦', title: 'SMC Master', reward: 'Free Premium Month + Active Trader Badge!', type: 'free', days: 30 }
];

// ============================================
// UPGRADE PROMPT - Shows after completing modules
// ============================================

const UpgradePrompt = ({ moduleId, onClose, onTryFeature, onUpgrade }) => {
  const feature = FEATURE_MAP[moduleId];
  if (!feature) return null;

  return (
    <div style={{ position: 'fixed', bottom: '1.5rem', right: '1.5rem', width: '360px', background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)', borderRadius: '1rem', border: '1px solid rgba(99,102,241,0.3)', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.5)', overflow: 'hidden', zIndex: 100, animation: 'slideUp 0.3s ease-out' }}>
      <div style={{ background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)', padding: '1.25rem', position: 'relative' }}>
        <button onClick={onClose} style={{ position: 'absolute', top: '0.75rem', right: '0.75rem', background: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: '50%', width: '28px', height: '28px', cursor: 'pointer', color: 'white', fontSize: '1rem' }}>×</button>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <span style={{ fontSize: '2.5rem' }}>{feature.icon}</span>
          <div>
            <div style={{ fontSize: '0.75rem', opacity: 0.9, marginBottom: '0.25rem' }}>🎉 Great job learning!</div>
            <div style={{ fontWeight: '700', fontSize: '1.1rem' }}>Ready to Try It Live?</div>
          </div>
        </div>
      </div>
      <div style={{ padding: '1.25rem' }}>
        <p style={{ color: '#94a3b8', fontSize: '0.9rem', margin: '0 0 1rem', lineHeight: 1.5 }}>
          You just learned the concepts. Now practice with our <strong style={{ color: '#fff' }}>{feature.name}</strong>!
        </p>
        <div style={{ background: 'rgba(99,102,241,0.1)', border: '1px solid rgba(99,102,241,0.2)', borderRadius: '0.75rem', padding: '1rem', marginBottom: '1rem' }}>
          <div style={{ fontSize: '0.8rem', color: '#a5b4fc', marginBottom: '0.5rem' }}>✨ What you'll get:</div>
          <ul style={{ margin: 0, paddingLeft: '1.25rem', color: '#cbd5e1', fontSize: '0.85rem', lineHeight: 1.8 }}>
            <li>Real-time data updates</li>
            <li>Interactive charts & tools</li>
            <li>Apply what you just learned</li>
          </ul>
        </div>
        <div style={{ display: 'flex', gap: '0.75rem' }}>
          <button onClick={() => onTryFeature(feature)} style={{ flex: 1, padding: '0.875rem', background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)', border: 'none', borderRadius: '0.5rem', color: 'white', fontWeight: '600', cursor: 'pointer', fontSize: '0.9rem' }}>🚀 Try It Free</button>
          <button onClick={() => onUpgrade('pricing')} style={{ padding: '0.875rem 1rem', background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '0.5rem', color: '#fff', cursor: 'pointer', fontSize: '0.9rem' }}>See Plans</button>
        </div>
        <p style={{ textAlign: 'center', color: '#64748b', fontSize: '0.75rem', margin: '1rem 0 0' }}>No credit card required</p>
      </div>
    </div>
  );
};

// ============================================
// STICKY UPGRADE BANNER - Always visible at bottom
// ============================================

const StickyUpgradeBanner = ({ onUpgrade, completedCount }) => {
  const [isMinimized, setIsMinimized] = useState(false);
  const [currentOffer, setCurrentOffer] = useState(0);
  
  const offers = [
    { text: '🚀 Upgrade to Pro — Get Real-Time Data & AI Analysis', highlight: 'Start Free Trial' },
    { text: '📊 See Live Congress Trades & Whale Activity', highlight: 'Unlock Now' },
    { text: '💹 Practice Risk-Free with Paper Trading', highlight: 'Try Free' },
    { text: '🎯 Get Price Alerts & Never Miss a Move', highlight: 'Enable Alerts' },
  ];
  
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentOffer(prev => (prev + 1) % offers.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);
  
  if (isMinimized) {
    return (
      <button
        onClick={() => setIsMinimized(false)}
        style={{
          position: 'fixed',
          bottom: '1rem',
          left: '1rem',
          background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
          border: 'none',
          borderRadius: '50%',
          width: '50px',
          height: '50px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          boxShadow: '0 4px 20px rgba(99,102,241,0.5)',
          zIndex: 900,
          fontSize: '1.25rem'
        }}
      >
        👑
      </button>
    );
  }
  
  return (
    <div style={{
      position: 'fixed',
      bottom: 0,
      left: 0,
      right: 0,
      background: 'linear-gradient(90deg, rgba(99,102,241,0.98) 0%, rgba(139,92,246,0.98) 100%)',
      padding: '0.625rem 0.75rem',
      zIndex: 900,
      borderTop: '1px solid rgba(255,255,255,0.2)',
      boxShadow: '0 -4px 20px rgba(0,0,0,0.3)'
    }}>
      {/* Mobile-friendly single row layout */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '0.5rem',
        maxWidth: '600px',
        margin: '0 auto'
      }}>
        {/* Minimize button */}
        <button
          onClick={() => setIsMinimized(true)}
          style={{
            background: 'rgba(255,255,255,0.2)',
            border: 'none',
            borderRadius: '50%',
            width: '24px',
            height: '24px',
            minWidth: '24px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            color: 'white',
            fontSize: '0.7rem',
            flexShrink: 0
          }}
        >
          ▼
        </button>
        
        {/* Rotating offer text - truncated on mobile */}
        <div style={{ 
          color: 'white', 
          fontWeight: '600', 
          fontSize: '0.8rem',
          flex: 1,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          minWidth: 0
        }}>
          {offers[currentOffer].text}
        </div>
        
        {/* CTA Button - compact on mobile */}
        <button
          onClick={onUpgrade}
          style={{
            background: 'white',
            color: '#6366f1',
            border: 'none',
            borderRadius: '0.375rem',
            padding: '0.5rem 0.75rem',
            fontWeight: '700',
            fontSize: '0.75rem',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '0.25rem',
            whiteSpace: 'nowrap',
            flexShrink: 0
          }}
        >
          {offers[currentOffer].highlight} →
        </button>
      </div>
    </div>
  );
};

// ============================================
// FEATURE DATA PREVIEW - Shows actual feature data with blur/lock
// ============================================

const SAMPLE_FEATURE_DATA = {
  congressTrades: [
    { member: 'Nancy Pelosi', ticker: 'NVDA', action: 'BUY', amount: '$1M - $5M', date: '2 days ago' },
    { member: 'Dan Crenshaw', ticker: 'TSLA', action: 'SELL', amount: '$100K - $250K', date: '3 days ago' },
    { member: 'Tommy Tuberville', ticker: 'AAPL', action: 'BUY', amount: '$500K - $1M', date: '5 days ago' },
  ],
  whaleActivity: [
    { type: 'CALL', ticker: 'SPY', strike: '$580', premium: '$2.4M', sentiment: 'Bullish' },
    { type: 'PUT', ticker: 'QQQ', strike: '$480', premium: '$1.8M', sentiment: 'Bearish' },
    { type: 'CALL', ticker: 'NVDA', strike: '$950', premium: '$3.1M', sentiment: 'Bullish' },
  ],
  marketDashboard: {
    sp500: { value: '5,892.45', change: '+1.24%' },
    nasdaq: { value: '18,654.32', change: '+1.87%' },
    vix: { value: '14.23', change: '-8.2%' },
    fearGreed: 72,
  },
  optionsChain: [
    { strike: '$180', callBid: '4.50', callAsk: '4.65', putBid: '2.10', putAsk: '2.25', iv: '32%' },
    { strike: '$185', callBid: '2.80', callAsk: '2.95', putBid: '3.45', putAsk: '3.60', iv: '31%' },
    { strike: '$190', callBid: '1.40', callAsk: '1.55', putBid: '5.20', putAsk: '5.35', iv: '30%' },
  ],
  portfolioTracker: [
    { symbol: 'AAPL', shares: 50, avgCost: '$142.50', current: '$185.20', gain: '+29.9%' },
    { symbol: 'GOOGL', shares: 20, avgCost: '$125.00', current: '$165.80', gain: '+32.6%' },
    { symbol: 'MSFT', shares: 35, avgCost: '$310.00', current: '$385.40', gain: '+24.3%' },
  ]
};

const FeatureDataPreview = ({ featureType, onUpgrade }) => {
  const featureConfig = {
    congressTrades: {
      title: '🏛️ Congress Trading Activity',
      description: 'See what politicians are buying and selling',
      tier: 'Pro'
    },
    whaleActivity: {
      title: '🐋 Whale Options Flow',
      description: 'Track million-dollar options trades in real-time',
      tier: 'Premium'
    },
    marketDashboard: {
      title: '📊 Live Market Dashboard',
      description: 'Real-time indices, VIX, and market sentiment',
      tier: 'Pro'
    },
    optionsChain: {
      title: '📈 Options Chain Explorer',
      description: 'Full options data with Greeks and IV',
      tier: 'Options Add-On'
    },
    portfolioTracker: {
      title: '💼 Portfolio Tracker',
      description: 'Track your investments and performance',
      tier: 'Pro'
    }
  };
  
  const config = featureConfig[featureType];
  const data = SAMPLE_FEATURE_DATA[featureType];
  
  return (
    <div style={{
      background: 'rgba(30, 41, 59, 0.8)',
      borderRadius: '1rem',
      overflow: 'hidden',
      border: '1px solid rgba(99,102,241,0.3)'
    }}>
      {/* Header */}
      <div style={{
        background: 'linear-gradient(135deg, rgba(99,102,241,0.2) 0%, rgba(139,92,246,0.1) 100%)',
        padding: '0.875rem 1rem',
        borderBottom: '1px solid rgba(255,255,255,0.1)',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: '0.5rem'
      }}>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontWeight: '700', color: '#fff', fontSize: '0.9rem' }}>{config.title}</div>
          <div style={{ fontSize: '0.75rem', color: '#94a3b8', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{config.description}</div>
        </div>
        <div style={{
          background: config.tier === 'Premium' 
            ? 'linear-gradient(135deg, #f59e0b, #d97706)' 
            : config.tier === 'Options Add-On'
              ? 'linear-gradient(135deg, #ec4899, #be185d)'
              : 'linear-gradient(135deg, #6366f1, #8b5cf6)',
          padding: '0.25rem 0.5rem',
          borderRadius: '0.75rem',
          fontSize: '0.65rem',
          fontWeight: '700',
          color: 'white',
          whiteSpace: 'nowrap',
          flexShrink: 0
        }}>
          {config.tier}
        </div>
      </div>
      
      {/* Data Preview with Lock Overlay */}
      <div style={{ position: 'relative', minHeight: '180px' }}>
        {/* Blurred Data */}
        <div style={{
          padding: '0.75rem',
          filter: 'blur(3px)',
          opacity: 0.5,
          pointerEvents: 'none'
        }}>
          {featureType === 'congressTrades' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
              {data.map((trade, i) => (
                <div key={i} style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  padding: '0.5rem',
                  background: 'rgba(15,23,42,0.5)',
                  borderRadius: '0.375rem',
                  fontSize: '0.75rem'
                }}>
                  <span style={{ color: '#fff' }}>{trade.member}</span>
                  <span style={{ color: trade.action === 'BUY' ? '#22c55e' : '#ef4444' }}>{trade.action} {trade.ticker}</span>
                </div>
              ))}
            </div>
          )}
          
          {featureType === 'whaleActivity' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              {data.map((whale, i) => (
                <div key={i} style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  padding: '0.75rem',
                  background: 'rgba(15,23,42,0.5)',
                  borderRadius: '0.5rem',
                  fontSize: '0.85rem'
                }}>
                  <span style={{ color: whale.type === 'CALL' ? '#22c55e' : '#ef4444' }}>{whale.type}</span>
                  <span style={{ color: '#fff' }}>{whale.ticker} {whale.strike}</span>
                  <span style={{ color: '#a5b4fc' }}>{whale.premium}</span>
                </div>
              ))}
            </div>
          )}
          
          {featureType === 'marketDashboard' && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '0.75rem' }}>
              {[
                { label: 'S&P 500', ...data.sp500 },
                { label: 'NASDAQ', ...data.nasdaq },
                { label: 'VIX', ...data.vix },
                { label: 'Fear & Greed', value: data.fearGreed, change: 'Greed' }
              ].map((item, i) => (
                <div key={i} style={{
                  padding: '0.75rem',
                  background: 'rgba(15,23,42,0.5)',
                  borderRadius: '0.5rem',
                  textAlign: 'center'
                }}>
                  <div style={{ fontSize: '0.75rem', color: '#94a3b8' }}>{item.label}</div>
                  <div style={{ fontSize: '1.25rem', fontWeight: '700', color: '#fff' }}>{item.value}</div>
                  <div style={{ fontSize: '0.8rem', color: item.change?.startsWith('+') ? '#22c55e' : item.change?.startsWith('-') ? '#ef4444' : '#94a3b8' }}>{item.change}</div>
                </div>
              ))}
            </div>
          )}
          
          {featureType === 'optionsChain' && (
            <table style={{ width: '100%', fontSize: '0.8rem', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ color: '#64748b' }}>
                  <th style={{ padding: '0.5rem', textAlign: 'left' }}>Strike</th>
                  <th style={{ padding: '0.5rem', textAlign: 'right' }}>Call</th>
                  <th style={{ padding: '0.5rem', textAlign: 'right' }}>Put</th>
                  <th style={{ padding: '0.5rem', textAlign: 'right' }}>IV</th>
                </tr>
              </thead>
              <tbody>
                {data.map((row, i) => (
                  <tr key={i} style={{ borderTop: '1px solid rgba(255,255,255,0.05)' }}>
                    <td style={{ padding: '0.5rem', color: '#fff' }}>{row.strike}</td>
                    <td style={{ padding: '0.5rem', textAlign: 'right', color: '#22c55e' }}>{row.callBid}</td>
                    <td style={{ padding: '0.5rem', textAlign: 'right', color: '#ef4444' }}>{row.putBid}</td>
                    <td style={{ padding: '0.5rem', textAlign: 'right', color: '#a5b4fc' }}>{row.iv}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          
          {featureType === 'portfolioTracker' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              {data.map((holding, i) => (
                <div key={i} style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '0.75rem',
                  background: 'rgba(15,23,42,0.5)',
                  borderRadius: '0.5rem',
                  fontSize: '0.85rem'
                }}>
                  <span style={{ color: '#fff', fontWeight: '600' }}>{holding.symbol}</span>
                  <span style={{ color: '#94a3b8' }}>{holding.shares} shares</span>
                  <span style={{ color: '#22c55e', fontWeight: '600' }}>{holding.gain}</span>
                </div>
              ))}
            </div>
          )}
        </div>
        
        {/* Unlock Overlay */}
        <div style={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'rgba(15, 23, 42, 0.75)',
          padding: '1rem'
        }}>
          <div style={{
            background: 'rgba(99,102,241,0.2)',
            borderRadius: '50%',
            padding: '0.75rem',
            marginBottom: '0.5rem'
          }}>
            <Lock size={24} color="#a5b4fc" />
          </div>
          <div style={{ color: '#fff', fontWeight: '600', fontSize: '0.9rem', marginBottom: '0.25rem' }}>Premium Feature</div>
          <div style={{ color: '#94a3b8', fontSize: '0.75rem', marginBottom: '0.75rem' }}>Unlock with {config.tier}</div>
          <button
            onClick={onUpgrade}
            style={{
              background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
              border: 'none',
              borderRadius: '0.5rem',
              padding: '0.5rem 1rem',
              color: 'white',
              fontWeight: '600',
              fontSize: '0.8rem',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.375rem',
              boxShadow: '0 4px 15px rgba(99,102,241,0.4)'
            }}
          >
            <Sparkles size={14} /> Unlock Now
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================
// PREMIUM FEATURE SPOTLIGHT - Rotating showcase
// ============================================

const PremiumFeatureSpotlight = ({ onUpgrade }) => {
  const [currentFeature, setCurrentFeature] = useState(0);
  
  const features = [
    {
      icon: '📊',
      title: 'Live Market Dashboard',
      description: 'Real-time S&P 500, NASDAQ, VIX, and sentiment indicators',
      stat: '15+ indicators',
      tier: 'Pro'
    },
    {
      icon: '🏛️',
      title: 'Congress Trades',
      description: 'Track what politicians are buying before everyone else',
      stat: 'Updated daily',
      tier: 'Pro'
    },
    {
      icon: '🐋',
      title: 'Whale Watcher',
      description: 'Follow million-dollar options trades in real-time',
      stat: '$100M+ tracked',
      tier: 'Premium'
    },
    {
      icon: '📈',
      title: 'Options Chain',
      description: 'Full options data with Greeks, IV, and GEX analysis',
      stat: 'All expirations',
      tier: 'Options'
    },
    {
      icon: '💼',
      title: 'Portfolio Tracker',
      description: 'Track investments & analyze performance',
      stat: 'Unlimited holdings',
      tier: 'Pro'
    },
    {
      icon: '🤖',
      title: 'AI Assistant',
      description: 'Ask questions about any stock or strategy',
      stat: 'Unlimited chats',
      tier: 'Pro'
    }
  ];
  
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentFeature(prev => (prev + 1) % features.length);
    }, 4000);
    return () => clearInterval(interval);
  }, []);
  
  const feature = features[currentFeature];
  
  return (
    <div style={{
      background: 'linear-gradient(135deg, rgba(99,102,241,0.15) 0%, rgba(168,85,247,0.1) 100%)',
      border: '1px solid rgba(99,102,241,0.3)',
      borderRadius: '1rem',
      padding: '1.25rem',
      marginBottom: '1.5rem'
    }}>
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'flex-start',
        marginBottom: '1rem'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <Sparkles size={16} color="#a5b4fc" />
          <span style={{ fontSize: '0.8rem', color: '#a5b4fc', fontWeight: '600' }}>PREMIUM FEATURE</span>
        </div>
        <div style={{
          display: 'flex',
          gap: '0.25rem'
        }}>
          {features.map((_, i) => (
            <div
              key={i}
              onClick={() => setCurrentFeature(i)}
              style={{
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                background: i === currentFeature ? '#6366f1' : 'rgba(255,255,255,0.2)',
                cursor: 'pointer',
                transition: 'all 0.3s ease'
              }}
            />
          ))}
        </div>
      </div>
      
      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem' }}>
        <div style={{
          width: '56px',
          height: '56px',
          borderRadius: '0.75rem',
          background: 'linear-gradient(135deg, rgba(99,102,241,0.3), rgba(139,92,246,0.2))',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '1.75rem'
        }}>
          {feature.icon}
        </div>
        <div>
          <div style={{ fontWeight: '700', color: '#fff', fontSize: '1.1rem' }}>{feature.title}</div>
          <div style={{ fontSize: '0.85rem', color: '#94a3b8' }}>{feature.description}</div>
        </div>
      </div>
      
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        padding: '0.75rem 1rem',
        background: 'rgba(15,23,42,0.5)',
        borderRadius: '0.5rem',
        marginBottom: '1rem'
      }}>
        <span style={{ color: '#22c55e', fontWeight: '600', fontSize: '0.9rem' }}>{feature.stat}</span>
        <span style={{
          background: feature.tier === 'Premium' 
            ? 'rgba(245,158,11,0.2)' 
            : feature.tier === 'Options'
              ? 'rgba(236,72,153,0.2)'
              : 'rgba(99,102,241,0.2)',
          color: feature.tier === 'Premium' 
            ? '#fbbf24' 
            : feature.tier === 'Options'
              ? '#f472b6'
              : '#a5b4fc',
          padding: '0.25rem 0.75rem',
          borderRadius: '1rem',
          fontSize: '0.75rem',
          fontWeight: '600'
        }}>
          {feature.tier}
        </span>
      </div>
      
      <button
        onClick={onUpgrade}
        style={{
          width: '100%',
          padding: '0.875rem',
          background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
          border: 'none',
          borderRadius: '0.5rem',
          color: 'white',
          fontWeight: '700',
          fontSize: '0.95rem',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '0.5rem'
        }}
      >
        Unlock All Features <ArrowRight size={18} />
      </button>
    </div>
  );
};

// ============================================
// MINI CHART PREVIEW - Shows sample chart data
// ============================================

const MiniChartPreview = ({ onUpgrade }) => {
  // Simulated stock price data
  const data = [45, 48, 46, 52, 58, 55, 62, 68, 65, 72, 78, 75, 82, 88, 92];
  const max = Math.max(...data);
  const min = Math.min(...data);
  const width = 280; // Fixed width for SVG calculations
  
  // Generate path points with actual numbers
  const getY = (val) => 80 - ((val - min) / (max - min)) * 70;
  const getX = (i) => (i / (data.length - 1)) * width;
  
  const linePath = data.map((d, i) => `${i === 0 ? 'M' : 'L'} ${getX(i)} ${getY(d)}`).join(' ');
  const areaPath = `${linePath} L ${width} 80 L 0 80 Z`;
  
  return (
    <div style={{
      background: 'rgba(30, 41, 59, 0.8)',
      borderRadius: '1rem',
      padding: '1.25rem',
      border: '1px solid rgba(99,102,241,0.3)',
      position: 'relative',
      overflow: 'hidden'
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <div>
          <div style={{ fontWeight: '700', color: '#fff' }}>📈 AAPL Live Chart</div>
          <div style={{ fontSize: '0.8rem', color: '#94a3b8' }}>Real-time price action</div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontWeight: '700', color: '#22c55e', fontSize: '1.25rem' }}>$185.42</div>
          <div style={{ fontSize: '0.8rem', color: '#22c55e' }}>+2.34%</div>
        </div>
      </div>
      
      {/* Mini SVG Chart */}
      <div style={{ position: 'relative', height: '80px', marginBottom: '1rem' }}>
        <svg width="100%" height="80" viewBox={`0 0 ${width} 80`} preserveAspectRatio="none" style={{ filter: 'blur(2px)' }}>
          <defs>
            <linearGradient id="chartGradient" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="#22c55e" stopOpacity="0.3" />
              <stop offset="100%" stopColor="#22c55e" stopOpacity="0" />
            </linearGradient>
          </defs>
          <path d={areaPath} fill="url(#chartGradient)" />
          <path d={linePath} fill="none" stroke="#22c55e" strokeWidth="2" />
        </svg>
        
        {/* Lock overlay */}
        <div style={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'rgba(15,23,42,0.5)'
        }}>
          <div style={{
            background: 'rgba(99,102,241,0.9)',
            borderRadius: '0.5rem',
            padding: '0.5rem 1rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            color: 'white',
            fontSize: '0.85rem',
            fontWeight: '600'
          }}>
            <Lock size={14} /> Live Data Locked
          </div>
        </div>
      </div>
      
      <button
        onClick={onUpgrade}
        style={{
          width: '100%',
          padding: '0.75rem',
          background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
          border: 'none',
          borderRadius: '0.5rem',
          color: 'white',
          fontWeight: '600',
          fontSize: '0.9rem',
          cursor: 'pointer'
        }}
      >
        🔓 Unlock Real-Time Charts
      </button>
    </div>
  );
};

// ============================================
// MILESTONE CELEBRATION - Shows when hitting milestones
// ============================================

const MilestoneCelebration = ({ milestone, onClose, onClaimReward }) => (
  <div style={{ 
    position: 'fixed', 
    inset: 0, 
    background: 'rgba(0,0,0,0.9)', 
    display: 'flex', 
    alignItems: 'center', 
    justifyContent: 'center', 
    zIndex: 1000,
    animation: 'fadeIn 0.3s ease'
  }}>
    {/* Confetti-like particles */}
    {[...Array(20)].map((_, i) => (
      <div
        key={i}
        style={{
          position: 'absolute',
          width: `${Math.random() * 10 + 5}px`,
          height: `${Math.random() * 10 + 5}px`,
          borderRadius: Math.random() > 0.5 ? '50%' : '2px',
          background: ['#6366f1', '#a855f7', '#ec4899', '#22c55e', '#f59e0b'][Math.floor(Math.random() * 5)],
          left: `${Math.random() * 100}%`,
          top: `${Math.random() * 100}%`,
          opacity: 0.6,
          animation: `float ${2 + Math.random() * 3}s ease-in-out infinite`,
          animationDelay: `${Math.random() * 2}s`
        }}
      />
    ))}
    
    <div style={{ 
      background: 'linear-gradient(180deg, rgba(30,41,59,0.98) 0%, rgba(15,23,42,0.98) 100%)',
      backdropFilter: 'blur(20px)',
      WebkitBackdropFilter: 'blur(20px)',
      borderRadius: '2rem', 
      padding: '3rem 2.5rem', 
      textAlign: 'center', 
      maxWidth: '420px',
      width: '90%',
      border: '1px solid rgba(99,102,241,0.3)', 
      boxShadow: '0 0 80px rgba(99,102,241,0.3), 0 25px 60px rgba(0,0,0,0.4)',
      position: 'relative',
      overflow: 'hidden',
      animation: 'scaleIn 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)'
    }}>
      {/* Glow effect behind badge */}
      <div style={{
        position: 'absolute',
        top: '50px',
        left: '50%',
        transform: 'translateX(-50%)',
        width: '150px',
        height: '150px',
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(99,102,241,0.4) 0%, transparent 70%)',
        filter: 'blur(30px)',
        animation: 'pulse 2s ease infinite'
      }} />
      
      {/* Badge */}
      <div style={{ 
        fontSize: '5rem', 
        marginBottom: '1.5rem', 
        animation: 'bounce 1s ease infinite',
        filter: 'drop-shadow(0 10px 20px rgba(0,0,0,0.3))',
        position: 'relative',
        zIndex: 1
      }}>
        {milestone.badge}
      </div>
      
      {/* Celebration text */}
      <div style={{ 
        fontSize: '0.9rem', 
        color: '#a5b4fc', 
        marginBottom: '0.5rem',
        textTransform: 'uppercase',
        letterSpacing: '0.1em',
        fontWeight: '600'
      }}>
        🎉 Achievement Unlocked
      </div>
      
      <h2 style={{ 
        fontSize: '2rem', 
        fontWeight: '800',
        margin: '0 0 0.75rem', 
        background: 'linear-gradient(135deg, #fff 0%, #a5b4fc 50%, #c4b5fd 100%)', 
        WebkitBackgroundClip: 'text', 
        WebkitTextFillColor: 'transparent',
        letterSpacing: '-0.02em'
      }}>
        {milestone.title}!
      </h2>
      
      <p style={{ 
        color: '#94a3b8', 
        margin: '0 0 2rem',
        fontSize: '1.05rem'
      }}>
        You've completed <strong style={{ color: '#fff' }}>{milestone.modules} modules</strong>!
      </p>
      
      {/* Reward box */}
      <div style={{ 
        background: 'linear-gradient(135deg, rgba(34,197,94,0.15) 0%, rgba(34,197,94,0.05) 100%)', 
        border: '1px solid rgba(34,197,94,0.3)', 
        borderRadius: '1rem', 
        padding: '1.5rem', 
        marginBottom: '1.5rem',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <div style={{
          position: 'absolute',
          top: '-20px',
          right: '-20px',
          width: '80px',
          height: '80px',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(34,197,94,0.3) 0%, transparent 70%)',
          filter: 'blur(15px)'
        }} />
        <div style={{ 
          color: '#4ade80', 
          fontSize: '0.8rem', 
          marginBottom: '0.5rem',
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          fontWeight: '600'
        }}>
          🎁 Your Reward
        </div>
        <div style={{ 
          color: '#fff', 
          fontWeight: '700', 
          fontSize: '1.25rem',
          position: 'relative',
          zIndex: 1
        }}>
          {milestone.reward}
        </div>
      </div>
      
      {/* CTA Buttons */}
      <button 
        onClick={onClaimReward} 
        className="btn-hover"
        style={{ 
          width: '100%', 
          padding: '1.125rem', 
          background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)', 
          border: 'none', 
          borderRadius: '0.875rem', 
          color: 'white', 
          fontWeight: '700', 
          cursor: 'pointer', 
          fontSize: '1.05rem', 
          marginBottom: '1rem',
          boxShadow: '0 8px 25px rgba(99,102,241,0.4)',
          transition: 'all 0.2s ease'
        }}
      >
        🎉 Claim Reward
      </button>
      
      <button 
        onClick={onClose} 
        style={{ 
          background: 'transparent', 
          border: 'none', 
          color: '#64748b', 
          cursor: 'pointer', 
          fontSize: '0.9rem',
          padding: '0.5rem 1rem',
          transition: 'color 0.2s ease'
        }}
      >
        Continue Learning →
      </button>
    </div>
  </div>
);

// ============================================
// INLINE FEATURE TEASER - Shows in module content
// ============================================

const InlineFeatureTeaser = ({ moduleId, onTryFeature }) => {
  const feature = FEATURE_MAP[moduleId];
  if (!feature) return null;

  return (
    <div style={{ background: 'linear-gradient(135deg, rgba(99,102,241,0.15) 0%, rgba(168,85,247,0.1) 100%)', border: '1px solid rgba(99,102,241,0.3)', borderRadius: '0.75rem', padding: '1.25rem', margin: '1.5rem 0', display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
      <span style={{ fontSize: '2rem' }}>{feature.icon}</span>
      <div style={{ flex: 1, minWidth: '200px' }}>
        <div style={{ fontWeight: '600', color: '#fff', marginBottom: '0.25rem' }}>💡 Try This Feature: {feature.name}</div>
        <div style={{ fontSize: '0.85rem', color: '#94a3b8' }}>Put what you're learning into practice</div>
      </div>
      <button onClick={() => onTryFeature(feature)} style={{ padding: '0.625rem 1.25rem', background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)', border: 'none', borderRadius: '0.5rem', color: 'white', fontWeight: '600', cursor: 'pointer', fontSize: '0.875rem', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>Try Free <ArrowRight size={16} /></button>
    </div>
  );
};

// ============================================
// PROGRESS WITH UPGRADE - Shows progress + next reward
// ============================================

const ProgressWithUpgrade = ({ completed, total, onUpgrade }) => {
  const percent = Math.round((completed / total) * 100);
  const nextMilestone = MILESTONES.find(m => m.modules > completed);
  const recentMilestone = [...MILESTONES].reverse().find(m => m.modules <= completed);

  return (
    <div style={{ 
      background: 'linear-gradient(135deg, rgba(30,41,59,0.9) 0%, rgba(15,23,42,0.95) 100%)',
      borderRadius: '1.25rem', 
      padding: '1.5rem', 
      marginBottom: '2rem',
      border: '1px solid rgba(255,255,255,0.08)',
      boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* Decorative gradient */}
      <div style={{ position: 'absolute', top: '-50px', right: '-50px', width: '150px', height: '150px', borderRadius: '50%', background: 'radial-gradient(circle, rgba(99,102,241,0.15) 0%, transparent 70%)', filter: 'blur(20px)' }} />
      
      <div style={{ position: 'relative', zIndex: 1 }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <div style={{ 
              width: '40px', 
              height: '40px', 
              borderRadius: '0.75rem',
              background: 'linear-gradient(135deg, rgba(99,102,241,0.3), rgba(168,85,247,0.2))',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <Gift size={20} color="#a5b4fc" />
            </div>
            <div>
              <span style={{ fontWeight: '700', fontSize: '1.1rem', display: 'block' }}>Your Learning Journey</span>
              <span style={{ fontSize: '0.8rem', color: '#64748b' }}>Keep going to unlock rewards!</span>
            </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <span style={{ 
              fontSize: '1.75rem', 
              fontWeight: '800',
              background: 'linear-gradient(135deg, #6366f1, #a855f7)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }}>{percent}%</span>
            <span style={{ fontSize: '0.8rem', color: '#64748b', display: 'block' }}>{completed}/{total} modules</span>
          </div>
        </div>
        
        {/* Progress bar */}
        <div style={{ 
          background: 'rgba(51,65,85,0.5)', 
          borderRadius: '1rem', 
          height: '14px', 
          marginBottom: '1.25rem', 
          overflow: 'hidden',
          position: 'relative',
          boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.2)'
        }}>
          <div style={{ 
            background: 'linear-gradient(90deg, #6366f1, #8b5cf6, #a855f7)', 
            width: `${percent}%`, 
            height: '100%', 
            borderRadius: '1rem', 
            transition: 'width 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
            boxShadow: '0 0 20px rgba(99,102,241,0.5)',
            position: 'relative'
          }}>
            {/* Shimmer effect */}
            <div style={{ 
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.3) 50%, transparent 100%)',
              backgroundSize: '200% 100%',
              animation: 'shimmer 2s infinite'
            }} />
          </div>
          
          {/* Milestone markers */}
          {MILESTONES.map(m => (
            <div 
              key={m.modules}
              style={{ 
                position: 'absolute',
                left: `${(m.modules / total) * 100}%`,
                top: '50%',
                transform: 'translate(-50%, -50%)',
                width: completed >= m.modules ? '18px' : '14px',
                height: completed >= m.modules ? '18px' : '14px',
                borderRadius: '50%',
                background: completed >= m.modules ? '#22c55e' : 'rgba(100,116,139,0.8)',
                border: '2px solid #0f172a',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '0.6rem',
                zIndex: 2,
                boxShadow: completed >= m.modules ? '0 0 10px rgba(34,197,94,0.5)' : 'none',
                transition: 'all 0.3s ease'
              }}
            >
              {completed >= m.modules ? '✓' : ''}
            </div>
          ))}
        </div>
        
        {/* Next milestone info */}
        {nextMilestone && (
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            background: 'rgba(34,197,94,0.1)',
            border: '1px solid rgba(34,197,94,0.2)',
            borderRadius: '0.75rem',
            padding: '0.875rem 1rem',
            marginBottom: '1rem'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <span style={{ fontSize: '1.5rem' }}>{nextMilestone.badge}</span>
              <div>
                <span style={{ fontWeight: '600', fontSize: '0.9rem', display: 'block' }}>{nextMilestone.title}</span>
                <span style={{ fontSize: '0.8rem', color: '#94a3b8' }}>{nextMilestone.modules - completed} more modules to go</span>
              </div>
            </div>
            <div style={{ 
              background: 'rgba(34,197,94,0.2)',
              padding: '0.5rem 0.875rem',
              borderRadius: '0.5rem',
              fontSize: '0.8rem',
              color: '#4ade80',
              fontWeight: '600'
            }}>
              🎁 {nextMilestone.reward}
            </div>
          </div>
        )}
        
        {/* Upgrade CTA */}
        {percent >= 10 && (
          <button 
            onClick={onUpgrade} 
            className="btn-hover"
            style={{ 
              width: '100%', 
              padding: '1rem', 
              background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', 
              border: 'none', 
              borderRadius: '0.75rem', 
              color: 'white', 
              cursor: 'pointer', 
              fontWeight: '700', 
              fontSize: '0.95rem',
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center', 
              gap: '0.5rem',
              boxShadow: '0 4px 20px rgba(99,102,241,0.4)',
              transition: 'all 0.2s ease'
            }}
          >
            <Sparkles size={18} /> Ready to go Pro? Start your free trial
          </button>
        )}
      </div>
    </div>
  );
};

// ============================================
// PRICING TABLE COMPONENT
// ============================================

const PricingTable = ({ onSelectTier, onClose }) => {
  const tiers = [
    { 
      key: 'free', 
      name: 'Free', 
      price: 0, 
      icon: '🌱', 
      tagline: 'Basic features to get started',
      features: ['Market Dashboard', 'Basic Stock Quotes', 'Limited Watchlist (5 stocks)', 'Basic News Feed'], 
      highlight: false 
    },
    { 
      key: 'pro', 
      name: 'Pro', 
      price: 9.99, 
      icon: '💎', 
      tagline: 'Everything you need for serious investing',
      features: ['Everything in Free, plus:', 'ETF X-Ray & Comparison', 'AI Investment Assistant', 'Unlimited Watchlist', 'Portfolio Tracking', 'Recession Dashboard', 'Congress Trades Tracker'], 
      highlight: true, 
      badge: 'Most Popular' 
    },
    { 
      key: 'premium', 
      name: 'Premium', 
      price: 19.99, 
      icon: '👑', 
      tagline: 'For professional investors',
      features: ['Everything in Pro, plus:', 'Real-time Alerts', 'API Access', 'Priority Support', 'Early Access to New Features', 'Custom Screeners', 'Export to CSV/Excel'], 
      highlight: false, 
      badge: 'Best Value' 
    }
  ];

  const handleSelectTier = (tierKey) => {
    if (tierKey === 'free') {
      onClose();
      return;
    }
    window.location.href = '/settings?tab=subscription';
  };

  return (
    <div style={{ 
      position: 'fixed', 
      inset: 0, 
      background: 'rgba(0,0,0,0.95)', 
      zIndex: 1000, 
      overflowY: 'auto',
      WebkitOverflowScrolling: 'touch'
    }}>
      <div style={{ 
        minHeight: '100%',
        padding: '1rem',
        paddingTop: '2rem',
        paddingBottom: '2rem',
        boxSizing: 'border-box'
      }}>
        {/* Close button - fixed position */}
        <button 
          onClick={onClose} 
          style={{ 
            position: 'fixed', 
            top: '1rem', 
            right: '1rem', 
            background: 'rgba(255,255,255,0.1)', 
            border: '1px solid rgba(255,255,255,0.2)',
            borderRadius: '50%', 
            width: '40px', 
            height: '40px', 
            cursor: 'pointer', 
            color: '#fff', 
            fontSize: '1.25rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1001
          }}
        >
          ×
        </button>
        
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '1.5rem', paddingTop: '1rem' }}>
          <div style={{ 
            display: 'inline-flex', 
            alignItems: 'center', 
            gap: '0.5rem', 
            background: 'rgba(99,102,241,0.15)', 
            border: '1px solid rgba(99,102,241,0.3)', 
            borderRadius: '2rem', 
            padding: '0.4rem 0.875rem', 
            marginBottom: '0.75rem',
            fontSize: '0.8rem',
            color: '#a5b4fc'
          }}>
            <Sparkles size={14} /> 7-Day Free Trial
          </div>
          <h2 style={{ 
            fontSize: '1.75rem', 
            fontWeight: '800',
            margin: '0 0 0.5rem',
            color: '#fff'
          }}>
            Choose Your Plan
          </h2>
          <p style={{ color: '#94a3b8', fontSize: '0.9rem', margin: 0 }}>
            Unlock powerful investing tools
          </p>
        </div>
        
        {/* Pricing cards - stacked on mobile */}
        <div style={{ 
          display: 'flex', 
          flexDirection: 'column',
          alignItems: 'center',
          gap: '1.25rem',
          maxWidth: '400px',
          margin: '0 auto'
        }}>
          {tiers.map(tier => (
            <div 
              key={tier.key} 
              style={{ 
                background: tier.highlight 
                  ? 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)' 
                  : 'rgba(30,41,59,0.95)', 
                borderRadius: '1rem', 
                padding: '1.25rem', 
                border: tier.highlight ? '2px solid rgba(255,255,255,0.2)' : '1px solid rgba(255,255,255,0.1)', 
                width: '100%',
                boxSizing: 'border-box',
                marginTop: tier.badge ? '0.5rem' : 0
              }}
            >
              {/* Badge - inline, not absolute */}
              {tier.badge && (
                <div style={{ 
                  display: 'inline-block',
                  background: tier.highlight ? '#22c55e' : 'linear-gradient(135deg, #f59e0b, #f97316)', 
                  padding: '0.25rem 0.75rem', 
                  borderRadius: '1rem', 
                  fontSize: '0.7rem', 
                  fontWeight: '700',
                  color: 'white',
                  marginBottom: '0.75rem'
                }}>
                  {tier.badge}
                </div>
              )}
              
              {/* Plan header */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
                <span style={{ fontSize: '2rem' }}>{tier.icon}</span>
                <div>
                  <h3 style={{ 
                    margin: 0, 
                    fontSize: '1.25rem',
                    fontWeight: '700',
                    color: '#fff'
                  }}>
                    {tier.name}
                  </h3>
                  <p style={{ 
                    margin: 0, 
                    fontSize: '0.8rem', 
                    color: tier.highlight ? 'rgba(255,255,255,0.8)' : '#94a3b8' 
                  }}>
                    {tier.tagline}
                  </p>
                </div>
              </div>
              
              {/* Price */}
              <div style={{ marginBottom: '1rem' }}>
                <span style={{ 
                  fontSize: '2.25rem', 
                  fontWeight: '800',
                  color: '#fff'
                }}>
                  ${tier.price}
                </span>
                <span style={{ 
                  fontSize: '0.9rem', 
                  color: tier.highlight ? 'rgba(255,255,255,0.7)' : '#64748b'
                }}>
                  /mo
                </span>
              </div>
              
              {/* Features */}
              <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 1rem', fontSize: '0.8rem' }}>
                {tier.features.map((f, i) => (
                  <li 
                    key={i} 
                    style={{ 
                      padding: '0.4rem 0', 
                      color: tier.highlight ? 'rgba(255,255,255,0.9)' : '#cbd5e1',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem'
                    }}
                  >
                    <span style={{ color: tier.highlight ? '#fff' : '#22c55e' }}>✓</span>
                    {f}
                  </li>
                ))}
              </ul>
              
              {/* CTA Button */}
              <button 
                onClick={() => handleSelectTier(tier.key)} 
                style={{ 
                  width: '100%', 
                  padding: '0.875rem', 
                  background: tier.key === 'free' 
                    ? 'rgba(51,65,85,0.8)' 
                    : tier.highlight 
                      ? 'white' 
                      : 'linear-gradient(135deg, #6366f1, #8b5cf6)', 
                  color: tier.highlight && tier.key !== 'free' ? '#6366f1' : 'white', 
                  border: 'none', 
                  borderRadius: '0.625rem', 
                  fontWeight: '700',
                  fontSize: '0.9rem',
                  cursor: 'pointer'
                }}
              >
                {tier.key === 'free' ? 'Current Plan' : 'Start Free Trial →'}
              </button>
            </div>
          ))}
        </div>
        
        {/* Options Add-on Banner */}
        <div style={{
          marginTop: '1.5rem',
          maxWidth: '400px',
          marginLeft: 'auto',
          marginRight: 'auto',
          background: 'rgba(245,158,11,0.1)',
          border: '1px solid rgba(245,158,11,0.3)',
          borderRadius: '0.75rem',
          padding: '1rem'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem', flexWrap: 'wrap' }}>
            <span style={{ fontSize: '1rem' }}>📈</span>
            <span style={{ fontWeight: '700', color: '#fff', fontSize: '0.95rem' }}>Options Trading Add-On</span>
            <span style={{ background: '#f59e0b', color: '#000', padding: '0.125rem 0.375rem', borderRadius: '0.25rem', fontSize: '0.65rem', fontWeight: '700' }}>$79.99/mo</span>
          </div>
          <p style={{ margin: '0 0 0.75rem', color: '#94a3b8', fontSize: '0.8rem' }}>Full options chain, GEX analysis, Greeks & IV</p>
          <button 
            onClick={() => window.location.href = '/settings?tab=subscription'}
            style={{
              background: 'linear-gradient(135deg, #f59e0b, #f97316)',
              border: 'none',
              borderRadius: '0.5rem',
              padding: '0.625rem 1rem',
              color: 'white',
              fontWeight: '600',
              fontSize: '0.85rem',
              cursor: 'pointer',
              width: '100%'
            }}
          >
            Learn More
          </button>
        </div>
        
        {/* Footer note */}
        <p style={{ 
          textAlign: 'center', 
          color: '#64748b', 
          fontSize: '0.75rem', 
          marginTop: '1.25rem',
          padding: '0 1rem'
        }}>
          ✨ No credit card required • Cancel anytime
        </p>
      </div>
    </div>
  );
};

// ============================================
// AI CHAT WIDGET COMPONENT
// ============================================

const AIChatWidget = ({ tutor, moduleContext, onClose }) => {
  const [messages, setMessages] = useState([{ role: 'assistant', content: tutor.greeting }]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);
  useEffect(() => { setMessages([{ role: 'assistant', content: tutor.greeting }]); }, [tutor.name, tutor.greeting]);

  const sendMessage = async () => {
    if (!input.trim() || isLoading) return;
    const userMessage = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setIsLoading(true);

    try {
      const contextPrompt = moduleContext 
        ? `${tutor.systemPrompt}\n\nContext: User is learning about "${moduleContext}". Reference this when relevant.`
        : tutor.systemPrompt;

      const response = await fetch(CHAT_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          messages: [
            { role: 'system', content: contextPrompt },
            ...messages.filter(m => m.role !== 'assistant' || messages.indexOf(m) > 0),
            { role: 'user', content: userMessage }
          ],
          context: { module: moduleContext, tutor: tutor.name }
        })
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      
      const data = await response.json();
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: data.message || data.choices?.[0]?.message?.content || "I'm having trouble responding. Please try again!"
      }]);
    } catch (error) {
      console.error('Chat error:', error);
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: `Sorry, I couldn't connect (${error.message}). Please try again! 🔌`
      }]);
    }
    setIsLoading(false);
  };

  const quickQuestions = {
    main: ["What should I learn first?", "Explain P/E ratio simply", "How do I start investing?"],
    market: ["What moves the market?", "Explain the yield curve", "What's a good VIX level?"],
    stocks: ["How to read a stock chart?", "What's a good P/E ratio?", "Explain market cap"],
    options: ["What's a call option?", "Explain theta decay", "What is IV crush?"],
    portfolio: ["How to diversify?", "What's a good allocation?", "When to rebalance?"],
    alerts: ["How many stocks to watch?", "Best alert strategy?", "Filter news noise?"],
    altdata: ["Follow Congress trades?", "What's insider buying?", "13F filing delays?"],
    etf: ["SPY vs VOO?", "What's expense ratio?", "ETF vs mutual fund?"],
    global: ["Invest in India how?", "Currency risk?", "Crypto allocation?"],
    practice: ["Paper trade how long?", "Real money when?", "Use AI for research?"],
    earnings: ["When is earnings?", "Beat vs miss?", "What's guidance?"]
  };

  // Widget is only rendered when parent showChat is true

  return (
    <div style={{ 
      position: 'fixed', 
      bottom: '1.5rem', 
      right: '1.5rem', 
      width: '400px', 
      maxHeight: '600px', 
      background: 'linear-gradient(180deg, rgba(30,41,59,0.98) 0%, rgba(15,23,42,0.98) 100%)',
      backdropFilter: 'blur(20px)',
      WebkitBackdropFilter: 'blur(20px)',
      borderRadius: '1.5rem', 
      border: '1px solid rgba(255,255,255,0.1)', 
      boxShadow: `0 25px 60px -15px rgba(0,0,0,0.5), 0 0 40px ${tutor.color}15`, 
      display: 'flex', 
      flexDirection: 'column', 
      zIndex: 1000, 
      overflow: 'hidden',
      animation: 'scaleIn 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)'
    }}>
      {/* Header */}
      <div style={{ 
        background: `linear-gradient(135deg, ${tutor.color}30 0%, ${tutor.color}10 100%)`,
        borderBottom: '1px solid rgba(255,255,255,0.08)', 
        padding: '1.25rem', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <div style={{ 
            width: '52px', 
            height: '52px', 
            borderRadius: '50%', 
            background: `linear-gradient(135deg, ${tutor.color}80, ${tutor.color}50)`, 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            fontSize: '1.75rem',
            border: `3px solid ${tutor.color}40`,
            boxShadow: `0 4px 15px ${tutor.color}40`,
            position: 'relative'
          }}>
            {tutor.avatar}
            <div style={{ 
              position: 'absolute', 
              bottom: '2px', 
              right: '2px', 
              width: '12px', 
              height: '12px', 
              borderRadius: '50%', 
              background: '#22c55e', 
              border: '2px solid #1e293b',
              boxShadow: '0 0 8px rgba(34,197,94,0.6)'
            }} />
          </div>
          <div>
            <div style={{ fontWeight: '700', color: 'white', fontSize: '1.05rem' }}>{tutor.name}</div>
            <div style={{ fontSize: '0.8rem', color: '#94a3b8', display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
              <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#22c55e', display: 'inline-block' }}></span>
              AI Tutor • Online
            </div>
          </div>
        </div>
        <button 
          onClick={onClose} 
          style={{ 
            background: 'rgba(255,255,255,0.08)', 
            border: '1px solid rgba(255,255,255,0.1)', 
            borderRadius: '50%', 
            width: '36px', 
            height: '36px', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            cursor: 'pointer', 
            color: '#94a3b8',
            transition: 'all 0.2s ease'
          }}
        >
          <X size={18} />
        </button>
      </div>
      
      {/* Messages */}
      <div style={{ 
        flex: 1, 
        overflowY: 'auto', 
        padding: '1.25rem', 
        display: 'flex', 
        flexDirection: 'column', 
        gap: '1rem', 
        maxHeight: '350px',
        background: 'linear-gradient(180deg, transparent 0%, rgba(99,102,241,0.03) 100%)'
      }}>
        {messages.map((msg, i) => (
          <div 
            key={i} 
            style={{ 
              display: 'flex', 
              justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start',
              animation: 'fadeIn 0.3s ease'
            }}
          >
            {msg.role === 'assistant' && (
              <div style={{ 
                width: '32px', 
                height: '32px', 
                borderRadius: '50%', 
                background: `linear-gradient(135deg, ${tutor.color}60, ${tutor.color}40)`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '1rem',
                marginRight: '0.75rem',
                flexShrink: 0
              }}>
                {tutor.avatar}
              </div>
            )}
            <div style={{ 
              maxWidth: '80%', 
              padding: '0.875rem 1.125rem', 
              borderRadius: msg.role === 'user' ? '1.25rem 1.25rem 0.375rem 1.25rem' : '1.25rem 1.25rem 1.25rem 0.375rem', 
              background: msg.role === 'user' 
                ? `linear-gradient(135deg, ${tutor.color}, ${tutor.color}cc)` 
                : 'rgba(51,65,85,0.6)', 
              color: 'white', 
              fontSize: '0.9rem', 
              lineHeight: '1.6',
              boxShadow: msg.role === 'user' ? `0 4px 15px ${tutor.color}30` : '0 2px 8px rgba(0,0,0,0.2)',
              border: msg.role === 'user' ? 'none' : '1px solid rgba(255,255,255,0.05)'
            }}>
              {msg.content}
            </div>
          </div>
        ))}
        {isLoading && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <div style={{ 
              width: '32px', 
              height: '32px', 
              borderRadius: '50%', 
              background: `linear-gradient(135deg, ${tutor.color}60, ${tutor.color}40)`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '1rem'
            }}>
              {tutor.avatar}
            </div>
            <div style={{ 
              background: 'rgba(51,65,85,0.6)', 
              borderRadius: '1.25rem', 
              padding: '1rem 1.25rem',
              display: 'flex', 
              gap: '0.375rem'
            }}>
              {[0, 1, 2].map(i => (
                <div 
                  key={i}
                  style={{ 
                    width: '8px', 
                    height: '8px', 
                    borderRadius: '50%', 
                    background: tutor.color, 
                    animation: `bounce 1.2s infinite ${i * 0.15}s`
                  }} 
                />
              ))}
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>
      
      {/* Quick Questions */}
      {messages.length <= 2 && (
        <div style={{ 
          padding: '0.75rem 1.25rem', 
          display: 'flex', 
          gap: '0.5rem', 
          flexWrap: 'wrap',
          borderTop: '1px solid rgba(255,255,255,0.05)'
        }}>
          {(quickQuestions[tutor.id] || quickQuestions.main).map((q, i) => (
            <button 
              key={i} 
              onClick={() => setInput(q)} 
              style={{ 
                padding: '0.5rem 0.875rem', 
                background: 'rgba(99,102,241,0.1)', 
                border: '1px solid rgba(99,102,241,0.25)', 
                borderRadius: '2rem', 
                color: '#a5b4fc', 
                fontSize: '0.8rem', 
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                whiteSpace: 'nowrap'
              }}
            >
              {q}
            </button>
          ))}
        </div>
      )}
      
      {/* Input */}
      <div style={{ 
        padding: '1rem 1.25rem', 
        borderTop: '1px solid rgba(255,255,255,0.08)', 
        display: 'flex', 
        gap: '0.75rem',
        background: 'rgba(15,23,42,0.5)'
      }}>
        <input 
          type="text" 
          value={input} 
          onChange={(e) => setInput(e.target.value)} 
          onKeyPress={(e) => e.key === 'Enter' && sendMessage()} 
          placeholder={`Ask ${tutor.name.split(' ')[0]}...`} 
          style={{ 
            flex: 1, 
            padding: '0.875rem 1.125rem', 
            background: 'rgba(30,41,59,0.8)', 
            border: '1px solid rgba(255,255,255,0.1)', 
            borderRadius: '0.75rem', 
            color: 'white', 
            fontSize: '0.95rem', 
            outline: 'none',
            transition: 'all 0.2s ease'
          }} 
        />
        <button 
          onClick={sendMessage} 
          disabled={isLoading || !input.trim()} 
          style={{ 
            padding: '0.875rem 1rem', 
            background: input.trim() ? `linear-gradient(135deg, ${tutor.color}, ${tutor.color}cc)` : 'rgba(51,65,85,0.5)', 
            border: 'none', 
            borderRadius: '0.75rem', 
            color: 'white', 
            cursor: input.trim() ? 'pointer' : 'not-allowed', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            transition: 'all 0.2s ease',
            boxShadow: input.trim() ? `0 4px 15px ${tutor.color}40` : 'none'
          }}
        >
          <Send size={20} />
        </button>
      </div>
    </div>
  );
};

const FloatingChatButton = ({ tutor, onClick }) => (
  <button 
    onClick={onClick} 
    className="btn-hover"
    style={{ 
      position: 'fixed', 
      bottom: '1.5rem', 
      right: '1.5rem', 
      width: '64px', 
      height: '64px', 
      borderRadius: '50%', 
      background: `linear-gradient(135deg, ${tutor.color}, ${tutor.color}cc)`, 
      border: '3px solid rgba(255,255,255,0.2)', 
      boxShadow: `0 8px 32px ${tutor.color}50, 0 0 20px ${tutor.color}30`, 
      cursor: 'pointer', 
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'center', 
      fontSize: '1.75rem', 
      zIndex: 999,
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      animation: 'float 3s ease-in-out infinite'
    }}
  >
    {tutor.avatar}
    {/* Notification dot */}
    <div style={{
      position: 'absolute',
      top: '2px',
      right: '2px',
      width: '14px',
      height: '14px',
      borderRadius: '50%',
      background: '#22c55e',
      border: '2px solid #0f172a',
      boxShadow: '0 0 8px rgba(34,197,94,0.6)'
    }} />
  </button>
);

// ============================================
// AI PRACTICE SCENARIO
// ============================================

const PracticeScenario = ({ category, tutor }) => {
  const [scenario, setScenario] = useState(null);
  const [userAnswer, setUserAnswer] = useState('');
  const [feedback, setFeedback] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const generateScenario = async () => {
    setIsLoading(true); setScenario(null); setFeedback(null); setUserAnswer('');
    try {
      const response = await fetch(CHAT_ENDPOINT, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          messages: [{ role: 'user', content: `Generate a ${category} investing practice scenario for beginners. Respond with ONLY JSON in this format: {"situation": "2-3 sentence description", "data": ["data point 1", "data point 2", "data point 3"], "question": "What would you do?"}` }],
          context: { requestType: 'scenario', category }
        })
      });
      
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      
      const data = await response.json();
      const content = data.message || data.choices?.[0]?.message?.content || '';
      const jsonMatch = content.match(/\{[\s\S]*\}/);
      if (jsonMatch) setScenario(JSON.parse(jsonMatch[0]));
      else setScenario({ situation: 'Could not generate scenario. Please try again!', data: [], question: 'What would you do?' });
    } catch (error) { 
      console.error('Scenario error:', error); 
      setScenario({ situation: `Error: ${error.message}. Please try again!`, data: [], question: 'What would you do?' });
    }
    setIsLoading(false);
  };

  const submitAnswer = async () => {
    if (!userAnswer.trim()) return;
    setIsLoading(true);
    try {
      const response = await fetch(CHAT_ENDPOINT, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          messages: [{ role: 'user', content: `You are ${tutor.name}, evaluating this answer to a ${category} practice scenario:\n\nScenario: ${scenario.situation}\nData: ${scenario.data?.join(', ')}\nQuestion: ${scenario.question}\n\nStudent's Answer: ${userAnswer}\n\nGive constructive feedback in 2-3 sentences. Be encouraging but point out any mistakes.` }],
          context: { requestType: 'feedback', tutor: tutor.name }
        })
      });
      
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      
      const data = await response.json();
      setFeedback(data.message || data.choices?.[0]?.message?.content || "Great effort! Keep practicing.");
    } catch (error) { 
      console.error('Feedback error:', error);
      setFeedback(`Couldn't evaluate (${error.message}). Try again!`); 
    }
    setIsLoading(false);
  };

  return (
    <div style={{ background: 'rgba(99,102,241,0.1)', border: '1px solid rgba(99,102,241,0.3)', borderRadius: '0.75rem', padding: '1.25rem', marginTop: '1rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}><Brain size={20} color="#a5b4fc" /><h4 style={{ margin: 0, color: '#a5b4fc' }}>AI Practice Scenario</h4></div>
      {!scenario ? (
        <button onClick={generateScenario} disabled={isLoading} style={{ padding: '0.75rem 1.5rem', background: '#6366f1', border: 'none', borderRadius: '0.5rem', color: 'white', cursor: isLoading ? 'wait' : 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem' }}><Zap size={18} />{isLoading ? 'Generating...' : 'Generate Practice Scenario'}</button>
      ) : (
        <div>
          <div style={{ color: '#cbd5e1', marginBottom: '1rem' }}>
            <p style={{ marginBottom: '0.75rem' }}>{scenario.situation}</p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '0.75rem' }}>{scenario.data?.map((d, i) => <span key={i} style={{ padding: '0.25rem 0.75rem', background: '#334155', borderRadius: '0.25rem', fontSize: '0.85rem' }}>{d}</span>)}</div>
            <p style={{ fontWeight: '500', color: '#fff' }}>{scenario.question}</p>
          </div>
          {!feedback ? (
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <input type="text" value={userAnswer} onChange={(e) => setUserAnswer(e.target.value)} placeholder="Your answer..." style={{ flex: 1, padding: '0.75rem', background: '#0f172a', border: '1px solid #334155', borderRadius: '0.5rem', color: 'white' }} />
              <button onClick={submitAnswer} disabled={isLoading} style={{ padding: '0.75rem 1rem', background: '#22c55e', border: 'none', borderRadius: '0.5rem', color: 'white', cursor: 'pointer' }}>Submit</button>
            </div>
          ) : (
            <div style={{ background: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.3)', borderRadius: '0.5rem', padding: '1rem', marginTop: '0.5rem' }}>
              <div style={{ fontWeight: '500', color: '#34d399', marginBottom: '0.5rem' }}>{tutor.avatar} {tutor.name}'s Feedback:</div>
              <p style={{ color: '#cbd5e1', margin: 0 }}>{feedback}</p>
              <button onClick={generateScenario} style={{ marginTop: '1rem', padding: '0.5rem 1rem', background: '#334155', border: 'none', borderRadius: '0.5rem', color: 'white', cursor: 'pointer' }}>Try Another</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ============================================
// DATA STRUCTURES
// ============================================

const LEARNING_PATHS = [
  { id: 'beginner', name: 'Complete Beginner', icon: '🌱', description: "Start here if you're new to investing", modules: [1, 2, 7, 8, 9, 5, 18, 19, 20, 32, 38], color: '#22c55e', bgColor: 'rgba(34, 197, 94, 0.1)', borderColor: 'rgba(34, 197, 94, 0.3)', totalTime: '90 min' },
  { id: 'portfolio', name: 'Portfolio Master', icon: '💼', description: 'Master portfolio management & tracking', modules: [18, 19, 20, 21, 22, 23, 34, 38], color: '#3b82f6', bgColor: 'rgba(59, 130, 246, 0.1)', borderColor: 'rgba(59, 130, 246, 0.3)', totalTime: '75 min' },
  { id: 'options', name: 'Options Trader', icon: '📈', description: 'Learn options, Greeks, and GEX', modules: [9, 13, 14, 15, 16, 17], color: '#a855f7', bgColor: 'rgba(168, 85, 247, 0.1)', borderColor: 'rgba(168, 85, 247, 0.3)', totalTime: '85 min' },
  { id: 'smartmoney', name: 'Smart Money Tracker', icon: '🕵️', description: 'Follow Congress, insiders & whales', modules: [27, 28, 29, 30, 6], color: '#f59e0b', bgColor: 'rgba(245, 158, 11, 0.1)', borderColor: 'rgba(245, 158, 11, 0.3)', totalTime: '55 min' },
  { id: 'global', name: 'Global Investor', icon: '🌍', description: 'India, Forex, Crypto & ETFs', modules: [35, 36, 37, 32, 34], color: '#06b6d4', bgColor: 'rgba(6, 182, 212, 0.1)', borderColor: 'rgba(6, 182, 212, 0.3)', totalTime: '55 min' },
  { id: 'economic', name: 'Economic Analyst', icon: '📊', description: 'Master economic indicators & recession signals', modules: [3, 4, 5, 6], color: '#f43f5e', bgColor: 'rgba(244, 63, 94, 0.1)', borderColor: 'rgba(244, 63, 94, 0.3)', totalTime: '45 min' },
  { id: 'smc', name: 'SMC/ICT Masterclass', icon: '🏦', description: 'Smart Money Concepts — trade like institutions', modules: [43, 44, 45, 46, 47], color: '#f59e0b', bgColor: 'rgba(245, 158, 11, 0.1)', borderColor: 'rgba(245, 158, 11, 0.3)', totalTime: '78 min' }
];

const CATEGORIES = [
  { id: 'market', name: 'Market Analysis', icon: '📊', color: '#60a5fa' },
  { id: 'stocks', name: 'Stock Research', icon: '🔍', color: '#34d399' },
  { id: 'options', name: 'Options Trading', icon: '📈', color: '#a78bfa' },
  { id: 'portfolio', name: 'Portfolio Management', icon: '💼', color: '#818cf8' },
  { id: 'alerts', name: 'Watchlist & Alerts', icon: '🔔', color: '#facc15' },
  { id: 'altdata', name: 'Alternative Data', icon: '👁️', color: '#fb923c' },
  { id: 'etf', name: 'ETFs & Funds', icon: '💰', color: '#34d399' },
  { id: 'global', name: 'Global Markets', icon: '🌍', color: '#22d3ee' },
  { id: 'practice', name: 'Practice & AI', icon: '🧪', color: '#f472b6' },
  { id: 'earnings', name: 'Earnings', icon: '📅', color: '#f87171' }
];

const MODULES = [
  { id: 1, title: 'What is the Stock Market?', icon: '📈', description: 'Understanding market basics', category: 'market', difficulty: 'Beginner', duration: '8 min' },
  { id: 2, title: 'Understanding Market Indices', icon: '📊', description: 'S&P 500, Dow, NASDAQ explained', category: 'market', difficulty: 'Beginner', duration: '10 min' },
  { id: 3, title: 'Economic Indicators Explained', icon: '🏛️', description: 'GDP, CPI, unemployment & more', category: 'market', difficulty: 'Intermediate', duration: '15 min' },
  { id: 4, title: 'Is a Recession Coming?', icon: '⚠️', description: 'Reading recession signals', category: 'market', difficulty: 'Intermediate', duration: '12 min' },
  { id: 5, title: 'How News Moves Markets', icon: '📰', description: 'News impact on stock prices', category: 'market', difficulty: 'Beginner', duration: '8 min' },
  { id: 6, title: 'Social Sentiment & Crowd Psychology', icon: '👥', description: 'How crowds move markets', category: 'market', difficulty: 'Intermediate', duration: '10 min' },
  { id: 7, title: 'How to Search & Find Stocks', icon: '🔍', description: 'Finding investment opportunities', category: 'stocks', difficulty: 'Beginner', duration: '6 min' },
  { id: 8, title: 'Reading Stock Quotes', icon: '💹', description: 'Understanding price displays', category: 'stocks', difficulty: 'Beginner', duration: '8 min' },
  { id: 9, title: 'Understanding Price Charts', icon: '📉', description: 'Technical analysis basics', category: 'stocks', difficulty: 'Beginner', duration: '12 min' },
  { id: 10, title: 'Company Profile Deep Dive', icon: '🏢', description: 'Researching companies', category: 'stocks', difficulty: 'Intermediate', duration: '10 min' },
  { id: 11, title: 'Financial Statements 101', icon: '📋', description: 'Income, balance sheet, cash flow', category: 'stocks', difficulty: 'Intermediate', duration: '15 min' },
  { id: 12, title: 'Sector & Peer Comparison', icon: '⚖️', description: 'Comparing similar companies', category: 'stocks', difficulty: 'Intermediate', duration: '10 min' },
  { id: 13, title: 'Options 101: Calls & Puts', icon: '📞', description: 'Options contract basics', category: 'options', difficulty: 'Advanced', duration: '20 min' },
  { id: 14, title: 'The Greeks Decoded', icon: '🔤', description: 'Delta, Gamma, Theta, Vega', category: 'options', difficulty: 'Advanced', duration: '15 min' },
  { id: 15, title: 'What is GEX (Gamma Exposure)?', icon: '🎯', description: 'Market maker positioning', category: 'options', difficulty: 'Advanced', duration: '15 min' },
  { id: 16, title: 'Put/Call Ratio as Sentiment', icon: '📊', description: 'Options-based market sentiment', category: 'options', difficulty: 'Advanced', duration: '10 min' },
  { id: 17, title: 'Implied Volatility & IV Crush', icon: '🌊', description: 'Volatility trading concepts', category: 'options', difficulty: 'Advanced', duration: '12 min' },
  { id: 18, title: 'Building Your First Portfolio', icon: '🏗️', description: 'Portfolio construction basics', category: 'portfolio', difficulty: 'Beginner', duration: '10 min' },
  { id: 19, title: 'Recording Transactions', icon: '📝', description: 'Tracking buys and sells', category: 'portfolio', difficulty: 'Beginner', duration: '8 min' },
  { id: 20, title: 'Understanding Your Holdings', icon: '💎', description: 'Analyzing your positions', category: 'portfolio', difficulty: 'Beginner', duration: '8 min' },
  { id: 21, title: 'Portfolio Performance Analytics', icon: '📈', description: 'Measuring returns', category: 'portfolio', difficulty: 'Intermediate', duration: '12 min' },
  { id: 22, title: 'Measuring Portfolio Risk', icon: '🎲', description: 'Risk metrics explained', category: 'portfolio', difficulty: 'Intermediate', duration: '12 min' },
  { id: 23, title: 'When & How to Rebalance', icon: '⚖️', description: 'Maintaining allocation', category: 'portfolio', difficulty: 'Intermediate', duration: '10 min' },
  { id: 24, title: 'Building an Effective Watchlist', icon: '👀', description: 'Tracking potential investments', category: 'alerts', difficulty: 'Beginner', duration: '6 min' },
  { id: 25, title: 'Setting Price Alerts', icon: '🔔', description: 'Get notified at key levels', category: 'alerts', difficulty: 'Beginner', duration: '5 min' },
  { id: 26, title: 'News Alerts & Staying Informed', icon: '📬', description: 'Information management', category: 'alerts', difficulty: 'Beginner', duration: '5 min' },
  { id: 27, title: 'Following Congress Trades', icon: '🏛️', description: 'Politician stock activity', category: 'altdata', difficulty: 'Advanced', duration: '12 min' },
  { id: 28, title: 'Insider Trading Signals', icon: '👔', description: 'Executive buying patterns', category: 'altdata', difficulty: 'Advanced', duration: '10 min' },
  { id: 29, title: 'Whale Watching (Institutional)', icon: '🐋', description: 'Following big money', category: 'altdata', difficulty: 'Advanced', duration: '12 min' },
  { id: 30, title: 'Spotting Market Anomalies', icon: '🔮', description: 'Unusual activity detection', category: 'altdata', difficulty: 'Advanced', duration: '10 min' },
  { id: 31, title: 'Social Media Sentiment', icon: '💬', description: 'Social signals analysis', category: 'altdata', difficulty: 'Intermediate', duration: '8 min' },
  { id: 32, title: 'ETFs Explained', icon: '📦', description: 'Exchange-traded fund basics', category: 'etf', difficulty: 'Beginner', duration: '10 min' },
  { id: 33, title: "ETF X-Ray: What's Inside?", icon: '🔬', description: 'ETF holdings analysis', category: 'etf', difficulty: 'Intermediate', duration: '8 min' },
  { id: 34, title: 'Comparing ETFs', icon: '🔄', description: 'ETF comparison strategies', category: 'etf', difficulty: 'Intermediate', duration: '8 min' },
  { id: 35, title: 'Investing in India', icon: '🇮🇳', description: 'Indian market overview', category: 'global', difficulty: 'Intermediate', duration: '15 min' },
  { id: 36, title: 'Currency & Forex Basics', icon: '💱', description: 'Foreign exchange intro', category: 'global', difficulty: 'Intermediate', duration: '12 min' },
  { id: 37, title: 'Crypto Fundamentals', icon: '₿', description: 'Cryptocurrency basics', category: 'global', difficulty: 'Intermediate', duration: '12 min' },
  { id: 38, title: 'Paper Trading: Practice Risk-Free', icon: '🎮', description: 'Virtual trading practice', category: 'practice', difficulty: 'Beginner', duration: '10 min' },
  { id: 39, title: 'Using AI for Research', icon: '🤖', description: 'AI-powered analysis', category: 'practice', difficulty: 'Intermediate', duration: '8 min' },
  { id: 40, title: 'Understanding Earnings Season', icon: '📅', description: 'Earnings calendar guide', category: 'earnings', difficulty: 'Intermediate', duration: '10 min' },
  { id: 41, title: 'Analyzing Earnings History', icon: '📜', description: 'Historical earnings patterns', category: 'earnings', difficulty: 'Intermediate', duration: '10 min' },
  { id: 42, title: 'Post-Earnings Move Analysis', icon: '📊', description: 'Earnings reaction strategies', category: 'earnings', difficulty: 'Advanced', duration: '10 min' },
  { id: 43, title: 'SMC: Market Structure (BOS & CHoCH)', icon: '🏗️', description: 'Break of Structure & Change of Character', category: 'smc', difficulty: 'Advanced', duration: '15 min' },
  { id: 44, title: 'SMC: Liquidity & Stop Hunts', icon: '💧', description: 'Buy-side, sell-side & equal highs/lows', category: 'smc', difficulty: 'Advanced', duration: '14 min' },
  { id: 45, title: 'SMC: Order Blocks & Fair Value Gaps', icon: '📦', description: 'Institutional entry zones & imbalances', category: 'smc', difficulty: 'Advanced', duration: '16 min' },
  { id: 46, title: 'SMC: Institutional Concepts & Kill Zones', icon: '🏦', description: 'Power of 3, OTE, IPDA & session timing', category: 'smc', difficulty: 'Advanced', duration: '15 min' },
  { id: 47, title: 'SMC: Building a Full Trade Setup', icon: '🎯', description: 'HTF to LTF confluence & risk management', category: 'smc', difficulty: 'Advanced', duration: '18 min' }
];

// ============================================
// FULL MODULE CONTENT - ALL 42 MODULES
// ============================================

const MODULE_CONTENT = {
  1: {
    title: 'What is the Stock Market?',
    subtitle: 'Understanding the basics of stock ownership and market mechanics',
    sections: [
      { id: 'intro', title: '💡 The Big Picture', type: 'text', content: `The stock market is where people buy and sell small pieces of companies called "shares" or "stocks."

When you buy a share of Apple, you literally own a tiny piece of Apple Inc. If Apple does well and makes more money, your piece becomes more valuable. If Apple struggles, your piece loses value.

**Why does the stock market exist?**

• **For companies:** They raise money by selling shares instead of taking loans
• **For investors:** You can grow wealth by owning pieces of successful businesses
• **For the economy:** It helps money flow to where it's needed most` },
      { id: 'analogy', title: '🎯 Think of it Like a Pizza Shop', type: 'analogy', content: `Imagine a pizza shop needs $10,000 to expand. Instead of borrowing from a bank, the owner sells 1,000 "shares" at $10 each.

You buy 10 shares for $100. You now own 1% of the pizza shop!

**If the shop does well:**
• More customers → more profit
• Your 1% becomes worth more
• Your $100 might become $150

**If the shop struggles:**
• Fewer customers → less profit
• Your 1% becomes worth less
• Your $100 might become $70` },
      { id: 'demo', title: '📊 Interactive: Supply & Demand', type: 'demo', demoType: 'supply-demand' },
      { id: 'keypoint', title: '⭐ Key Insight', type: 'highlight', content: `Stock prices change every second based on supply and demand.

**More buyers than sellers → Price goes UP**
**More sellers than buyers → Price goes DOWN**

It's that simple at its core!` },
      { id: 'realworld', title: '🌍 Real World Example', type: 'example', content: `**Apple (AAPL) Stock Journey:**

• 1980 IPO: $22 per share
• 2007 (iPhone launch): ~$12 (split-adjusted)
• 2020: ~$130
• 2024: ~$180

If you invested $1,000 in Apple in 2007, it would be worth over **$15,000** today!` }
    ],
    quiz: [
      { q: 'What does owning a stock mean?', options: ['You own a tiny piece of the company', 'You work for the company', 'You loaned money to the company', 'You can visit the company HQ'], correct: 0, explanation: 'Stocks represent ownership. When you buy shares, you become a part-owner.' },
      { q: 'What happens when MORE people want to buy a stock than sell it?', options: ['Price stays the same', 'Price goes DOWN', 'Price goes UP', 'Trading stops'], correct: 2, explanation: 'High demand + limited supply = rising prices. Basic economics!' },
      { q: 'Why do companies sell stock?', options: ['To give away ownership', 'To raise money without loans', 'Government requires it', 'To make the market bigger'], correct: 1, explanation: 'Companies sell shares to raise capital for growth without debt.' }
    ],
    takeaway: 'The stock market lets you own pieces of businesses. Prices move based on supply and demand.'
  },

  2: {
    title: 'Understanding Market Indices',
    subtitle: 'How the S&P 500, Dow Jones, and NASDAQ work',
    sections: [
      { id: 'intro', title: '💡 What is an Index?', type: 'text', content: `A market index is like a **report card** for a group of stocks. Instead of checking 500 individual stocks, you can look at one number to see how "the market" is doing.

**The Big Three US Indices:**

📈 **S&P 500** — The most important!
• Tracks 500 largest US companies
• Represents ~80% of US stock market value
• When people say "the market," they mean this

📊 **Dow Jones Industrial Average**
• Only 30 large companies
• Older and more famous, but less representative

💻 **NASDAQ Composite**
• Heavy on tech companies (Apple, Microsoft, Google)
• More volatile — bigger ups and downs` },
      { id: 'demo', title: '📊 Interactive: Compare the Indices', type: 'demo', demoType: 'indices-comparison' },
      { id: 'analogy', title: '🎯 Think of it Like Your GPA', type: 'analogy', content: `**Your GPA:** Average of all your class grades

**S&P 500:** Average of 500 company "grades" (stock prices)

Just like your GPA gives a quick summary of your academic performance, the S&P 500 gives a quick summary of how American businesses are doing.` },
      { id: 'warning', title: '⚠️ Common Mistake', type: 'warning', content: `**Don't confuse POINTS with PERCENTAGES!**

"Dow drops 500 points!" sounds scary...

But if the Dow is at 35,000, that's only a **1.4% drop**.

**Always look at percentage change**, not points!` },
      { id: 'keypoint', title: '⭐ Key Insight', type: 'highlight', content: `Most professional money managers **FAIL** to beat the S&P 500 over time.

That's why many investors just buy an S&P 500 index fund (like SPY or VOO) instead of picking individual stocks!` }
    ],
    quiz: [
      { q: 'Which index is the best overall measure of the US stock market?', options: ['Dow Jones (30 stocks)', 'S&P 500 (500 stocks)', 'NASDAQ (tech-heavy)', 'Russell 2000'], correct: 1, explanation: 'The S&P 500 covers 500 large companies = ~80% of US market value.' },
      { q: 'If the Dow drops 300 points when it\'s at 30,000, what\'s the percentage drop?', options: ['3%', '10%', '1%', '0.3%'], correct: 2, explanation: '300 ÷ 30,000 = 0.01 = 1%. Always calculate the percentage!' },
      { q: 'Which index has the most tech companies?', options: ['S&P 500', 'Dow Jones', 'NASDAQ', 'Russell 2000'], correct: 2, explanation: 'NASDAQ is heavily weighted toward technology companies.' }
    ],
    takeaway: 'Indices summarize market performance. The S&P 500 is the most important benchmark.'
  },

  3: {
    title: 'Economic Indicators Explained',
    subtitle: 'GDP, unemployment, inflation, and Fed rates',
    sections: [
      { id: 'intro', title: '💡 The Economy\'s Vital Signs', type: 'text', content: `Just like doctors check your temperature and blood pressure, economists use specific numbers to measure the economy's health.

**The Key Indicators:**

📊 **GDP (Gross Domestic Product)**
• Total value of everything produced in the US
• Growing GDP = healthy economy
• Shrinking GDP = possible recession

👷 **Unemployment Rate**
• Percentage of people looking for work
• Below 4% = very strong job market
• Above 6% = economy struggling

💰 **Inflation (CPI)**
• How fast prices are rising
• 2% = Fed's target (healthy)
• Above 5% = problem territory

🏦 **Federal Funds Rate**
• Interest rate the Fed controls
• Higher rates = slow economy, fight inflation
• Lower rates = stimulate growth` },
      { id: 'demo', title: '📊 Interactive: Economic Dashboard', type: 'demo', demoType: 'economic-indicators' },
      { id: 'relationships', title: '🔗 How They Connect', type: 'text', content: `**The Fed's Balancing Act:**

When inflation is HIGH:
• Fed RAISES rates → Borrowing costs more → People spend less → Prices stabilize

When economy is WEAK:
• Fed LOWERS rates → Borrowing is cheaper → People spend more → Economy grows

**The tricky part:** The Fed must balance fighting inflation against causing unemployment.` },
      { id: 'keypoint', title: '⭐ Key Insight', type: 'highlight', content: `**"Bad" news can be "good" for stocks!**

If unemployment rises → Fed might cut rates → Stocks often go UP

This is why markets sometimes rally on bad economic news. The market is always looking ahead to what the Fed will do next.` }
    ],
    quiz: [
      { q: 'What does GDP measure?', options: ['Government debt', 'Total economic output', 'Stock market value', 'Number of jobs'], correct: 1, explanation: 'GDP = Gross Domestic Product = total value of goods & services produced.' },
      { q: 'What inflation rate does the Fed target?', options: ['0%', '2%', '5%', '10%'], correct: 1, explanation: 'The Fed targets 2% inflation as "healthy" for the economy.' },
      { q: 'What typically happens to stocks when the Fed cuts rates?', options: ['They fall', 'They rise', 'No effect', 'They freeze'], correct: 1, explanation: 'Lower rates make borrowing cheaper, stimulating growth and typically boosting stocks.' }
    ],
    takeaway: 'Economic indicators help predict market direction. Watch GDP, unemployment, inflation, and Fed rates.'
  },

  4: {
    title: 'Is a Recession Coming?',
    subtitle: 'Key signals and recession indicators to watch',
    sections: [
      { id: 'intro', title: '💡 What is a Recession?', type: 'text', content: `A recession is when the economy shrinks for an extended period. The official definition:

**Two consecutive quarters of negative GDP growth**

During recessions:
• Companies earn less → stock prices fall
• Unemployment rises
• Consumer spending drops

**The good news:** Recessions are temporary and historically last 6-18 months. Markets often recover before the recession officially ends!` },
      { id: 'demo', title: '📊 Interactive: Recession Indicators', type: 'demo', demoType: 'recession-dashboard' },
      { id: 'signals', title: '🚨 Warning Signals to Watch', type: 'text', content: `**Yield Curve Inversion** (Most famous predictor!)
• When short-term bonds pay MORE than long-term bonds
• Has predicted every recession since 1955
• Usually 12-18 months before recession starts

**Rising Unemployment Claims**
• Weekly jobless claims trending up

**Manufacturing PMI Below 50**
• Purchasing Managers Index below 50 = contraction

**Consumer Confidence Falling**
• People feel pessimistic, spend less` },
      { id: 'warning', title: '⚠️ Don\'t Panic!', type: 'warning', content: `**Timing the market is nearly impossible.**

Missing the 10 best days in the market cuts returns in HALF.
Those best days often happen during scary times.

**Better strategy:** Stay invested, stay diversified, have an emergency fund.` },
      { id: 'keypoint', title: '⭐ Key Insight', type: 'highlight', content: `The stock market typically bottoms out **6 months BEFORE** a recession ends.

By the time good news arrives, prices have already recovered. This is why selling during panics often backfires!` }
    ],
    quiz: [
      { q: 'What officially defines a recession?', options: ['Stock market crash', 'Two quarters of negative GDP', 'Unemployment above 10%', 'Fed raising rates'], correct: 1, explanation: 'A recession is officially two consecutive quarters of shrinking GDP.' },
      { q: 'What is a yield curve inversion?', options: ['Stocks falling 20%', 'Short-term bonds paying more than long-term', 'GDP going negative', 'Inflation above 5%'], correct: 1, explanation: 'Yield curve inverts when short-term yields exceed long-term yields — a recession warning.' },
      { q: 'When does the stock market typically bottom during a recession?', options: ['When recession starts', 'At the worst economic data', 'Before the recession ends', 'After full recovery'], correct: 2, explanation: 'Markets are forward-looking and often bottom 6 months before recessions end.' }
    ],
    takeaway: 'Watch yield curves and unemployment claims for recession signals, but don\'t try to time the market.'
  },

  5: {
    title: 'How News Moves Markets',
    subtitle: 'Understanding market reactions to news events',
    sections: [
      { id: 'intro', title: '💡 News and Stock Prices', type: 'text', content: `The stock market reacts to news constantly. But here's the key insight:

**Markets react to SURPRISES, not just news.**

If everyone expected Apple to have great earnings, and they do — the stock might not move much. But if they're even BETTER than expected, the stock jumps.

**Types of market-moving news:**
📊 **Economic Data** — Jobs reports, GDP, inflation
🏢 **Company News** — Earnings, product launches, CEO changes
🏛️ **Federal Reserve** — Rate decisions, policy statements
🌍 **Geopolitical** — Elections, wars, trade deals` },
      { id: 'expectations', title: '🎯 The Expectations Game', type: 'analogy', content: `Imagine a student who usually gets C's brings home a B+. Parents are thrilled!

Now imagine a straight-A student brings home a B+. Parents might be disappointed.

**Stocks work the same way:**
• Company misses earnings by 1¢ → Stock drops 10%
• Company beats earnings by 1¢ → Stock jumps 5%

**Always compare news to what was EXPECTED.**` },
      { id: 'demo', title: '📊 Interactive: News Impact', type: 'demo', demoType: 'news-impact' },
      { id: 'timing', title: '⏰ "Buy the Rumor, Sell the News"', type: 'text', content: `This famous saying captures a common pattern:

**Before the news:** Traders buy in anticipation
**When news arrives:** They sell to lock in profits

**Example:** Apple iPhone launch
• Months before: Stock rises on excitement
• Launch day: Stock might actually fall!
• Why? The good news was already "priced in"` },
      { id: 'keypoint', title: '⭐ Key Insight', type: 'highlight', content: `**"The market has already priced it in"**

By the time you read news in headlines, professional traders have already acted. The current stock price reflects all known information.

Day-trading on news is extremely difficult — you're competing against algorithms that react in milliseconds.` }
    ],
    quiz: [
      { q: 'Markets react most strongly to:', options: ['Any good news', 'Any bad news', 'Surprises vs expectations', 'Headlines'], correct: 2, explanation: 'It\'s not whether news is good or bad, but whether it\'s better or worse than expected.' },
      { q: 'What does "priced in" mean?', options: ['The stock is expensive', 'Expected news is already reflected in the price', 'The stock has a target price', 'Analysts have rated it'], correct: 1, explanation: 'When news is "priced in," the stock has already moved based on expectations.' },
      { q: '"Buy the rumor, sell the news" means:', options: ['Only buy on rumors', 'Prices often rise before expected news and fall when it arrives', 'News is unreliable', 'Always sell on news'], correct: 1, explanation: 'Stocks often run up before anticipated events, then sell off when the news actually hits.' }
    ],
    takeaway: 'Markets react to surprises, not just news. If everyone expected it, it\'s already priced in.'
  },

  6: {
    title: 'Social Sentiment & Crowd Psychology',
    subtitle: 'How emotions drive market movements',
    sections: [
      { id: 'intro', title: '💡 The Role of Emotion', type: 'text', content: `Markets aren't just driven by fundamentals — they're driven by human emotions.

**The two dominant emotions:**

😨 **Fear** — Causes panic selling
• "I need to get out before it gets worse!"
• Creates market bottoms when everyone sells

🤑 **Greed** — Causes buying frenzies
• "I need to get in before I miss out!"
• Creates market tops when everyone buys

**Warren Buffett's advice:**
"Be fearful when others are greedy, and greedy when others are fearful."` },
      { id: 'demo', title: '📊 Interactive: Fear & Greed Index', type: 'demo', demoType: 'fear-greed' },
      { id: 'indicators', title: '📈 Sentiment Indicators', type: 'text', content: `**How we measure crowd sentiment:**

📊 **VIX (Fear Index)**
• Measures expected volatility
• High VIX (>30) = Fear
• Low VIX (<15) = Complacency

🐂🐻 **Bull/Bear Surveys**
• What % of investors are bullish vs bearish

📱 **Social Media Sentiment**
• AI analyzes millions of posts

💰 **Put/Call Ratio**
• More puts (bearish) or calls (bullish)?` },
      { id: 'contrarian', title: '🔄 Contrarian Thinking', type: 'highlight', content: `**The contrarian approach:**

When EVERYONE is bullish → Be cautious (market may be topped)
When EVERYONE is bearish → Look for opportunities (market may bottom)

**Why it works:**
If everyone who wants to buy has already bought → no more buyers → prices fall` },
      { id: 'warning', title: '⚠️ Important Nuance', type: 'warning', content: `Sentiment is ONE tool, not the only tool.

• Sentiment can stay extreme for a long time
• "The market can stay irrational longer than you can stay solvent"
• Combine sentiment with other analysis` }
    ],
    quiz: [
      { q: 'What does a high VIX reading indicate?', options: ['Market is calm', 'Fear and high volatility expected', 'Economy is strong', 'Time to sell everything'], correct: 1, explanation: 'VIX is the "fear index" — high readings mean investors expect big price swings.' },
      { q: 'What does "contrarian investing" mean?', options: ['Following the crowd', 'Going against extreme sentiment', 'Only buying tech stocks', 'Avoiding all risk'], correct: 1, explanation: 'Contrarians do the opposite of the crowd at extremes — buying fear, selling greed.' },
      { q: 'According to Buffett, when should you be greedy?', options: ['When stocks are rising', 'When others are greedy', 'When others are fearful', 'Never'], correct: 2, explanation: '"Be fearful when others are greedy, and greedy when others are fearful."' }
    ],
    takeaway: 'Market sentiment swings between fear and greed. Extreme readings often signal turning points.'
  },

  7: {
    title: 'How to Search & Find Stocks',
    subtitle: 'Finding investment opportunities efficiently',
    sections: [
      { id: 'intro', title: '💡 Finding Stocks to Research', type: 'text', content: `With thousands of stocks available, how do you find ones worth researching?

**Common approaches:**

🔍 **Screening** — Filter by specific criteria
• Price, market cap, P/E ratio, dividend yield

📰 **News & Trends** — What's making headlines?
• New products, industry shifts

👀 **Your Daily Life** — Products you use and love
• Peter Lynch: "Buy what you know"

📊 **Sector Analysis** — Top-down approach
• Which industries are growing?` },
      { id: 'demo', title: '📊 Interactive: Stock Screener', type: 'demo', demoType: 'stock-screener' },
      { id: 'criteria', title: '🎯 Common Screening Criteria', type: 'text', content: `**For Value Investors:**
• Low P/E ratio (under 15)
• Low Price-to-Book (under 1.5)
• High dividend yield (over 3%)

**For Growth Investors:**
• Revenue growth > 20% annually
• Earnings growth > 15% annually

**For Income Investors:**
• Dividend yield > 3%
• Dividend growth streak > 10 years

**For Safety:**
• Market cap > $10B (large cap)
• Low debt-to-equity` },
      { id: 'keypoint', title: '⭐ Key Insight', type: 'highlight', content: `**Screening finds candidates — research confirms them.**

A screener might show 50 stocks that match your criteria. That's just the starting point!

Next: Read about each company, understand their business, check financial health.` }
    ],
    quiz: [
      { q: 'What is a stock screener?', options: ['A tool to buy stocks', 'A filter to find stocks matching criteria', 'A price alert', 'A portfolio tracker'], correct: 1, explanation: 'Stock screeners let you filter thousands of stocks by specific criteria.' },
      { q: '"Buy what you know" is advice from:', options: ['Warren Buffett', 'Peter Lynch', 'Benjamin Graham', 'Ray Dalio'], correct: 1, explanation: 'Peter Lynch encouraged finding investment ideas in products you use daily.' },
      { q: 'For value investing, you typically look for:', options: ['High P/E ratios', 'Low P/E ratios', 'No dividends', 'Small companies only'], correct: 1, explanation: 'Value investors seek undervalued stocks, often indicated by low P/E ratios.' }
    ],
    takeaway: 'Use screeners to find candidates, then research deeply before investing.'
  },

  8: {
    title: 'Reading Stock Quotes',
    subtitle: 'Understanding the numbers on a stock page',
    sections: [
      { id: 'intro', title: '💡 Anatomy of a Stock Quote', type: 'text', content: `When you look up a stock, you see lots of numbers. Here's what they mean:

**Price Information:**
• **Last Price** — Most recent trade price
• **Change** — Difference from yesterday's close ($ and %)
• **Bid** — Highest price buyers will pay
• **Ask** — Lowest price sellers will accept
• **Spread** — Gap between bid and ask

**Volume Information:**
• **Volume** — Shares traded today
• **Avg Volume** — Typical daily volume` },
      { id: 'demo', title: '📊 Interactive: Stock Quote', type: 'demo', demoType: 'stock-quote' },
      { id: 'ranges', title: '📈 Price Ranges', type: 'text', content: `**Today's Range:** Lowest and highest price today
• Near the high? Buyers in control
• Near the low? Sellers in control

**52-Week Range:** Lowest and highest in past year
• Near 52-week high? Stock has momentum
• Near 52-week low? Either undervalued OR problem` },
      { id: 'metrics', title: '📊 Key Metrics', type: 'text', content: `**Market Cap** — Total value of all shares
• Mega cap: >$200B
• Large cap: $10B-$200B
• Mid cap: $2B-$10B
• Small cap: <$2B

**P/E Ratio** — Price ÷ Earnings per share
• How much you pay for $1 of earnings

**Dividend Yield** — Annual dividend ÷ Price
• What % return from dividends` },
      { id: 'keypoint', title: '⭐ Key Insight', type: 'highlight', content: `**The bid-ask spread matters for trading costs!**

Tight spread (e.g., $100.00 - $100.02) = Good liquidity
Wide spread (e.g., $10.00 - $10.50) = Costly to trade

Stick to stocks with tight spreads and high volume.` }
    ],
    quiz: [
      { q: 'What is the "bid" price?', options: ['Price you must pay', 'Highest price buyers offer', 'Yesterday\'s close', 'Average price'], correct: 1, explanation: 'The bid is the highest price current buyers are willing to pay.' },
      { q: 'A P/E ratio of 15 means:', options: ['Stock costs $15', 'You pay $15 for every $1 of earnings', 'Stock has 15% growth', 'Dividend is 15%'], correct: 1, explanation: 'P/E = Price/Earnings. A P/E of 15 means paying $15 for each $1 the company earns.' },
      { q: 'What does high trading volume indicate?', options: ['High price', 'Lots of investor interest', 'Good company', 'Time to sell'], correct: 1, explanation: 'High volume means many shares are changing hands — lots of buying and selling activity.' }
    ],
    takeaway: 'Stock quotes tell you price, volume, and valuation. Focus on P/E, volume, and the 52-week range.'
  },

  9: {
    title: 'Understanding Price Charts',
    subtitle: 'Reading candlesticks, trends, and support/resistance',
    sections: [
      { id: 'intro', title: '💡 Why Charts Matter', type: 'text', content: `Charts show you what's happening with a stock at a glance:

• **Direction** — Is it going up, down, or sideways?
• **Momentum** — Is the move speeding up or slowing?
• **Volatility** — How much does it swing around?
• **Key levels** — Where does it tend to stop or reverse?

**Chart types:**
📈 **Line charts** — Simple, shows closing prices
🕯️ **Candlesticks** — Most popular, shows open/high/low/close` },
      { id: 'demo', title: '📊 Interactive: Candlestick Chart', type: 'demo', demoType: 'candlestick-chart' },
      { id: 'candlesticks', title: '🕯️ Reading Candlesticks', type: 'text', content: `Each candlestick shows 4 prices for one time period:

**Green candle (Price went UP):**
• Bottom of body = Open
• Top of body = Close

**Red candle (Price went DOWN):**
• Top of body = Open
• Bottom of body = Close

**Wicks show the high and low**

• Long body = Strong move
• Small body = Indecision` },
      { id: 'support', title: '🎯 Support & Resistance', type: 'text', content: `**Support** — A price floor where buyers step in
• Stock falls to this level and bounces

**Resistance** — A price ceiling where sellers appear
• Stock rises to this level and falls back

**Trading tip:** Buy near support, sell near resistance.` },
      { id: 'keypoint', title: '⭐ Key Insight', type: 'highlight', content: `**The trend is your friend.**

• Stocks in uptrends tend to keep going up
• Stocks in downtrends tend to keep going down

**Simple trend identification:**
Higher highs + Higher lows = Uptrend ✅
Lower highs + Lower lows = Downtrend ⬇️` }
    ],
    quiz: [
      { q: 'A green candlestick means:', options: ['Stock went up that day', 'Stock went down', 'High volume', 'Buy signal'], correct: 0, explanation: 'Green candles show the closing price was higher than the opening price.' },
      { q: 'What is "support"?', options: ['A price ceiling', 'A price floor where buyers step in', 'An analyst recommendation', 'The highest price'], correct: 1, explanation: 'Support is a price level where demand tends to be strong enough to prevent further declines.' },
      { q: 'An uptrend is identified by:', options: ['Lower lows', 'Higher highs and higher lows', 'Constant price', 'Red candles'], correct: 1, explanation: 'Uptrends make higher highs (peaks) and higher lows (troughs) over time.' }
    ],
    takeaway: 'Charts reveal trends, momentum, and key levels. Learn candlesticks and support/resistance.'
  },

  10: {
    title: 'Company Profile Deep Dive',
    subtitle: 'Researching a company\'s business and competitive position',
    sections: [
      { id: 'intro', title: '💡 Know What You Own', type: 'text', content: `Before investing in any stock, understand:

**The Basics:**
• What does the company DO?
• How does it MAKE MONEY?
• Who are its CUSTOMERS?
• Who are its COMPETITORS?

**The Edge:**
• What makes it SPECIAL? (competitive advantage)
• Can competitors copy it easily?

**Warren Buffett's rule:**
"Never invest in a business you cannot understand."` },
      { id: 'moat', title: '🏰 The "Moat" Concept', type: 'text', content: `A "moat" is a sustainable competitive advantage.

**Types of Moats:**

🔒 **Brand Power** — Apple, Nike, Coca-Cola
📡 **Network Effects** — Facebook, Visa
💰 **Cost Advantages** — Walmart, Costco
🔄 **Switching Costs** — Microsoft, Salesforce
⚖️ **Regulatory** — Utilities, pharma patents` },
      { id: 'demo', title: '📊 Interactive: Company Analysis', type: 'demo', demoType: 'company-profile' },
      { id: 'research', title: '🔍 Where to Research', type: 'text', content: `**Free Resources:**
• Company's investor relations page
• SEC filings (10-K annual, 10-Q quarterly)
• Earnings call transcripts

**Key questions:**
1. Is revenue growing?
2. Are margins stable or improving?
3. What's management's strategy?
4. What risks exist?` },
      { id: 'keypoint', title: '⭐ Key Insight', type: 'highlight', content: `**A great business at a fair price beats a fair business at a great price.**

Focus first on business quality:
• Strong competitive position
• Growing market
• Capable management
• Healthy finances` }
    ],
    quiz: [
      { q: 'What is an economic "moat"?', options: ['A water feature', 'A sustainable competitive advantage', 'The company\'s debt', 'Its market cap'], correct: 1, explanation: 'A moat protects a company from competition and maintains profitability.' },
      { q: 'Which is an example of network effects?', options: ['Coca-Cola', 'Facebook', 'Walmart', 'Johnson & Johnson'], correct: 1, explanation: 'Facebook becomes more valuable as more people join — that\'s network effects.' },
      { q: 'Where can you find official company financial reports?', options: ['Wikipedia', 'SEC filings (10-K, 10-Q)', 'Twitter', 'News articles'], correct: 1, explanation: 'Public companies must file reports with the SEC.' }
    ],
    takeaway: 'Understand the business before buying. Look for sustainable competitive advantages (moats).'
  },

  11: {
    title: 'Financial Statements 101',
    subtitle: 'Income statement, balance sheet, and cash flow basics',
    sections: [
      { id: 'intro', title: '💡 The Three Key Statements', type: 'text', content: `Every public company reports three main financial statements:

📊 **Income Statement** (Profit & Loss)
• Shows: Revenue, expenses, profit
• Answers: "Did the company make money?"

📋 **Balance Sheet**
• Shows: Assets, liabilities, equity
• Answers: "What does the company own and owe?"

💰 **Cash Flow Statement**
• Shows: Where cash came from and went
• Answers: "Is the company generating real cash?"` },
      { id: 'income', title: '📊 Income Statement', type: 'text', content: `**Revenue** (Top Line) — Total sales

**Gross Profit** = Revenue - Cost of Goods Sold

**Operating Income** = Gross Profit - Operating Expenses

**Net Income** (Bottom Line) — Final profit after ALL expenses, taxes` },
      { id: 'demo', title: '📊 Interactive: Financial Statements', type: 'demo', demoType: 'financial-statements' },
      { id: 'cashflow', title: '💰 Cash Flow Basics', type: 'text', content: `**Why cash flow matters:**
A company can report profits but still run out of cash!

**Three sections:**
🏭 **Operating** — Cash from running the business
📦 **Investing** — Buying/selling equipment
🏦 **Financing** — Borrowing, dividends, buybacks

**Free Cash Flow = Operating CF - Capital Expenditures**
This is cash available for shareholders!` },
      { id: 'keypoint', title: '⭐ Key Insight', type: 'highlight', content: `**"Revenue is vanity, profit is sanity, but cash is king."**

A company can:
• Have growing revenue but no profit
• Have accounting profit but no cash

**Always check free cash flow!**` }
    ],
    quiz: [
      { q: 'Which statement shows if a company made a profit?', options: ['Balance sheet', 'Income statement', 'Cash flow statement', 'Annual report'], correct: 1, explanation: 'The income statement shows revenue minus expenses = profit (or loss).' },
      { q: 'What does the balance sheet equation state?', options: ['Revenue = Expenses + Profit', 'Assets = Liabilities + Equity', 'Cash In = Cash Out', 'Price = Value'], correct: 1, explanation: 'Assets (what you own) = Liabilities (what you owe) + Equity (what\'s left).' },
      { q: 'Why is free cash flow important?', options: ['It\'s the same as revenue', 'Shows actual cash available for shareholders', 'Banks require it', 'It\'s optional'], correct: 1, explanation: 'Free cash flow is what\'s left after running the business — available for dividends, buybacks.' }
    ],
    takeaway: 'Understand all three financial statements. Focus on free cash flow as the truest measure.'
  },

  12: {
    title: 'Sector & Peer Comparison',
    subtitle: 'Comparing companies within their industry',
    sections: [
      { id: 'intro', title: '💡 Why Compare to Peers?', type: 'text', content: `A P/E ratio of 25 might be:
• Expensive for a utility company
• Cheap for a fast-growing tech company

**Context matters!** Compare companies to others in the same industry.

**The 11 Stock Market Sectors:**
1. Technology
2. Healthcare
3. Financials
4. Consumer Discretionary
5. Consumer Staples
6. Industrials
7. Energy
8. Utilities
9. Real Estate
10. Materials
11. Communication Services` },
      { id: 'demo', title: '📊 Interactive: Peer Comparison', type: 'demo', demoType: 'peer-comparison' },
      { id: 'metrics', title: '📈 Key Comparison Metrics', type: 'text', content: `**Valuation:**
• P/E Ratio — Cheaper or more expensive than peers?
• P/S Ratio — Price to sales
• EV/EBITDA — Enterprise value to earnings

**Profitability:**
• Gross Margin — Pricing power
• Operating Margin — Efficiency
• ROE — Return on equity

**Growth:**
• Revenue Growth Rate
• Earnings Growth Rate` },
      { id: 'keypoint', title: '⭐ Key Insight', type: 'highlight', content: `**Premium valuations need premium reasons.**

If a stock trades at 2x the industry P/E, ask:
• Is it growing 2x faster?
• Does it have a stronger moat?
• Is it more profitable?

If not, it might be overvalued.` }
    ],
    quiz: [
      { q: 'Why compare a stock to industry peers?', options: ['All stocks should have same P/E', 'Valuation is relative to sector norms', 'To find the cheapest', 'Regulation requires it'], correct: 1, explanation: 'Different industries have different typical valuations.' },
      { q: 'Which metric best compares profitability?', options: ['Stock price', 'Market cap', 'Operating margin', 'Volume'], correct: 2, explanation: 'Operating margin shows what percentage of revenue becomes profit.' },
      { q: 'A stock at 2x industry P/E is:', options: ['Always overvalued', 'Always a buy', 'May be justified if growing faster', 'Impossible'], correct: 2, explanation: 'Premium valuations can be justified by faster growth or better margins.' }
    ],
    takeaway: 'Always compare valuation to industry peers. Premium valuations need premium reasons.'
  },

  13: { title: 'Options 101: Calls & Puts', subtitle: 'Understanding the basics of options contracts', sections: [
    { id: 'intro', title: '💡 What is an Option?', type: 'text', content: `An option is a **contract** that gives you the right (but not obligation) to buy or sell a stock at a specific price by a specific date.\n\n**Two Types:**\n\n📈 **CALL Option** — Right to BUY at strike price. Profit if stock goes UP.\n\n📉 **PUT Option** — Right to SELL at strike price. Profit if stock goes DOWN.\n\n**Key Terms:**\n• **Strike Price** — The price you can buy/sell at\n• **Expiration** — When the option expires\n• **Premium** — Price you pay for the option` },
    { id: 'analogy', title: '🎯 Think of it Like a House Deposit', type: 'analogy', content: `You find a house for $300,000. You pay $5,000 for an option to buy it at that price within 6 months.\n\n**If house rises to $350,000:** Exercise option, buy for $300K, sell for $350K = $45K profit!\n\n**If house drops to $250,000:** Walk away, lose only $5K deposit` },
    { id: 'demo', title: '📊 Interactive: Option Payoff', type: 'demo', demoType: 'option-payoff' },
    { id: 'warning', title: '⚠️ Options Risk Warning', type: 'warning', content: `**Options are NOT for beginners!**\n\n• You can lose 100% of your investment\n• Options expire worthless all the time\n• Time works against option buyers\n\n**Start with:** Paper trading, small positions, understand Greeks first.` }
  ], quiz: [
    { q: 'A CALL option gives you the right to:', options: ['Sell stock at strike', 'Buy stock at strike', 'Get dividends', 'Vote at meetings'], correct: 1, explanation: 'A call option = right to BUY at the strike price.' },
    { q: 'If you buy a put option, you profit when:', options: ['Stock goes up', 'Stock goes down', 'Stock stays flat', 'Dividends paid'], correct: 1, explanation: 'Put options increase in value when the stock falls.' },
    { q: 'Maximum loss when buying an option?', options: ['Unlimited', 'Strike price', 'The premium paid', 'Stock price'], correct: 2, explanation: 'When buying options, the most you can lose is the premium.' }
  ], takeaway: 'Calls bet on stocks going up, puts bet on stocks going down. Options are leveraged and risky.' },

  14: { title: 'The Greeks Decoded', subtitle: 'Delta, Gamma, Theta, Vega explained simply', sections: [
    { id: 'intro', title: '💡 What Are "The Greeks"?', type: 'text', content: `The Greeks are measures of how an option's price changes.\n\n**The Main Greeks:**\n• **Delta** (Δ) — Price sensitivity to stock moves\n• **Gamma** (Γ) — Delta's rate of change\n• **Theta** (Θ) — Time decay (enemy of buyers!)\n• **Vega** (V) — Volatility sensitivity` },
    { id: 'delta', title: '📈 Delta — Price Sensitivity', type: 'text', content: `**Delta tells you how much the option moves when stock moves $1.**\n\n**Call options:** Delta 0 to 1 (Delta 0.50 = gains $0.50 when stock rises $1)\n\n**Put options:** Delta -1 to 0\n\n**Quick tip:** Delta ≈ Probability option expires in-the-money` },
    { id: 'demo', title: '📊 Interactive: The Greeks', type: 'demo', demoType: 'greeks-visual' },
    { id: 'keypoint', title: '⭐ Key Insight', type: 'highlight', content: `**As an option buyer, you fight TWO enemies:**\n\n1. **Theta** — Time is always against you\n2. **Vega** — Volatility dropping hurts you\n\nThe stock needs to move ENOUGH and FAST ENOUGH!` }
  ], quiz: [
    { q: 'What does Delta measure?', options: ['Time decay', 'How much option moves when stock moves $1', 'Volatility', 'Interest rates'], correct: 1, explanation: 'Delta measures the option\'s price change for a $1 move in the stock.' },
    { q: 'Theta is negative for buyers because:', options: ['Stock prices fall', 'Options lose value over time', 'Interest rates rise', 'Volatility increases'], correct: 1, explanation: 'Theta represents time decay — options lose value as expiration approaches.' },
    { q: 'When does "IV crush" happen?', options: ['Before earnings', 'After earnings', 'At market open', 'Monthly'], correct: 1, explanation: 'IV crush is the volatility drop after an event like earnings.' }
  ], takeaway: 'Delta = direction, Theta = time decay, Vega = volatility. Option buyers fight theta and vega.' },

  15: { title: 'What is GEX (Gamma Exposure)?', subtitle: 'How market makers influence stock movements', sections: [
    { id: 'intro', title: '💡 Understanding GEX', type: 'text', content: `**GEX (Gamma Exposure)** measures how much hedging market makers need to do.\n\n**High Positive GEX:** Market makers buy dips, sell rips → Stabilizing effect, lower volatility\n\n**Negative GEX:** Market makers sell into weakness, buy into strength → Amplifying effect, big moves possible` },
    { id: 'demo', title: '📊 Interactive: GEX Levels', type: 'demo', demoType: 'gex-visual' },
    { id: 'levels', title: '🎯 Key GEX Levels', type: 'text', content: `**GEX Flip Point:** Price where GEX switches from positive to negative\n• Above = Stable, mean-reverting\n• Below = Volatile, trending\n\n**Max Gamma Strike:** Price with highest gamma concentration — acts as a magnet` },
    { id: 'keypoint', title: '⭐ Key Insight', type: 'highlight', content: `**GEX explains "unusual" market behavior.**\n\nWhy do stocks pin to round numbers on expiration?\nWhy do selloffs accelerate suddenly?\n\nIt's often market makers hedging gamma!` }
  ], quiz: [
    { q: 'What is GEX?', options: ['A stock ticker', 'Gamma Exposure - market maker hedging', 'An economic indicator', 'A type of option'], correct: 1, explanation: 'GEX = Gamma Exposure, measuring market maker hedging needs.' },
    { q: 'Positive GEX tends to:', options: ['Amplify moves', 'Dampen volatility', 'Cause crashes', 'Increase volume'], correct: 1, explanation: 'In positive gamma, market makers buy dips and sell rips — stabilizing.' },
    { q: 'The GEX flip point is:', options: ['Where options expire', 'Where GEX changes from positive to negative', 'Maximum price', 'Opening price'], correct: 1, explanation: 'The flip point is where market behavior transitions.' }
  ], takeaway: 'GEX explains how market maker hedging affects prices. Positive = stable, Negative = volatile.' },

  16: { title: 'Put/Call Ratio as Sentiment', subtitle: 'Using options data to gauge market sentiment', sections: [
    { id: 'intro', title: '💡 The Put/Call Ratio', type: 'text', content: `The Put/Call Ratio compares bearish bets (puts) to bullish bets (calls).\n\n**Formula:** Put Volume ÷ Call Volume\n\n**Interpretation:**\n• Ratio > 1.0 = More puts (bearish sentiment)\n• Ratio < 1.0 = More calls (bullish sentiment)\n\n**It's a CONTRARIAN indicator:** Extreme bearishness often marks bottoms` },
    { id: 'demo', title: '📊 Interactive: Put/Call Analysis', type: 'demo', demoType: 'put-call-ratio' },
    { id: 'keypoint', title: '⭐ Key Insight', type: 'highlight', content: `**Use Put/Call ratio with other indicators, not alone.**\n\nBest signals come when:\n• Put/Call at extremes\n• Combined with price at support/resistance\n• VIX also at extremes` }
  ], quiz: [
    { q: 'A Put/Call ratio above 1.2 suggests:', options: ['Extreme bullishness', 'Extreme bearishness/fear', 'Normal conditions', 'Time to panic'], correct: 1, explanation: 'High put/call = bearish sentiment. As contrarian indicator, may signal buying opportunity.' },
    { q: 'The Put/Call ratio is a:', options: ['Leading indicator', 'Contrarian indicator', 'Economic indicator', 'Lagging indicator'], correct: 1, explanation: 'Extreme readings often signal the opposite move.' },
    { q: 'Low Put/Call (<0.5) might indicate:', options: ['Time to buy aggressively', 'Potential market top', 'Market crash', 'Recession'], correct: 1, explanation: 'Very low put/call = extreme bullishness. Contrarian: possible top forming.' }
  ], takeaway: 'Put/Call ratio measures sentiment. Use as contrarian indicator at extremes.' },

  17: { title: 'Implied Volatility & IV Crush', subtitle: 'Understanding volatility pricing', sections: [
    { id: 'intro', title: '💡 What is Implied Volatility?', type: 'text', content: `**Implied Volatility (IV)** is the market's expectation of how much a stock will move.\n\n• Higher IV = Market expects big moves = Expensive options\n• Lower IV = Market expects small moves = Cheap options` },
    { id: 'demo', title: '📊 Interactive: IV Chart', type: 'demo', demoType: 'iv-chart' },
    { id: 'ivcrush', title: '💥 The IV Crush', type: 'text', content: `**IV Crush = Sudden drop in implied volatility**\n\nMost common after earnings:\n\n**Before earnings:** Uncertainty HIGH, IV spikes\n**After earnings:** Uncertainty RESOLVED, IV drops\n\n**The Trap:** Stock rises 3%, but IV drops 40% → Your call LOSES money despite being right!` },
    { id: 'keypoint', title: '⭐ Key Insight', type: 'highlight', content: `**In options, being right about direction isn't enough.**\n\nYou also need to be right about:\n• Timing (theta decay)\n• Volatility (vega/IV)\n• Magnitude (how much it moves)` }
  ], quiz: [
    { q: 'High Implied Volatility means:', options: ['Stock is expensive', 'Options are expensive', 'Stock will definitely move', 'Good time to buy options'], correct: 1, explanation: 'High IV = market expects big moves = options cost more.' },
    { q: 'IV Crush typically happens:', options: ['Market open', 'After earnings', 'End of year', 'Fed meetings'], correct: 1, explanation: 'IV crush is the sharp volatility drop after an event is resolved.' },
    { q: 'You buy calls, stock rises 5%, but calls lose money. Why?', options: ['Stock didn\'t move enough', 'IV crush offset gains', 'You bought puts', 'Theta decay'], correct: 1, explanation: 'IV crush can cause losses even when direction was correct!' }
  ], takeaway: 'IV affects option prices. Watch for IV crush after earnings — being right on direction isn\'t enough!' },

  18: { title: 'Building Your First Portfolio', subtitle: 'Diversification, allocation, and the core-satellite approach', sections: [
    { id: 'intro', title: '💡 What is a Portfolio?', type: 'text', content: `A portfolio is the collection of ALL your investments.\n\n**Why organization matters:**\n1. See your total wealth at a glance\n2. Understand your risk exposure\n3. Track performance over time\n4. Make smarter decisions` },
    { id: 'strategy', title: '📊 The Core-Satellite Approach', type: 'text', content: `**CORE (70-80% of portfolio)**\nBroad market index funds: S&P 500 ETF (SPY or VOO), Total Market ETF (VTI)\n\n**SATELLITE (20-30% of portfolio)**\nIndividual picks you believe in: Single stocks, Sector ETFs\n\nThis gives market returns PLUS chance to outperform.` },
    { id: 'demo', title: '📊 Interactive: Build a Portfolio', type: 'demo', demoType: 'portfolio-builder' },
    { id: 'warning', title: '⚠️ The #1 Beginner Mistake', type: 'warning', content: `**Putting too much in one stock!**\n\nEven if you LOVE a company, never put more than **5-10%** in any single stock.` },
    { id: 'keypoint', title: '⭐ Key Insight', type: 'highlight', content: `Studies show **asset allocation** determines about **90%** of your returns over time.\n\nIndividual stock picking matters much less than most people think!` }
  ], quiz: [
    { q: 'What is diversification?', options: ['Buying only tech', 'Spreading investments to reduce risk', 'Investing all at once', 'Selling when stocks drop'], correct: 1, explanation: 'Diversification = spreading investments so one bad pick doesn\'t ruin you.' },
    { q: 'Good maximum for any single stock?', options: ['50%', '25%', '5-10%', '1%'], correct: 2, explanation: 'Most advisors recommend no more than 5-10% in any single stock.' },
    { q: 'In core-satellite, what goes in "core"?', options: ['Risky bets', 'Broad index funds', 'Cash only', 'Single stocks'], correct: 1, explanation: 'The core = stable, diversified index funds.' }
  ], takeaway: 'Build diversified portfolio with stable core (index funds) and smaller satellite positions.' },

  19: { title: 'Recording Transactions', subtitle: 'Tracking buys, sells, and cost basis', sections: [
    { id: 'intro', title: '💡 Why Track Transactions?', type: 'text', content: `Every time you buy or sell, record it.\n\n📊 **Performance tracking** — Know your true returns\n💰 **Cost basis for taxes** — IRS needs profit/loss\n📈 **Decision analysis** — What worked? What didn't?` },
    { id: 'demo', title: '📊 Interactive: Transaction Log', type: 'demo', demoType: 'transaction-log' },
    { id: 'costbasis', title: '💵 Understanding Cost Basis', type: 'text', content: `**Cost Basis = What you paid + fees**\n\nYour cost basis is your "break-even" price.\n\n**When you sell:** Profit = Sale price - Cost basis` },
    { id: 'keypoint', title: '⭐ Key Insight', type: 'highlight', content: `**Your broker tracks this — but verify!**\n\nBrokers provide cost basis info, but transfers may lose data. Keep your own records as backup!` }
  ], quiz: [
    { q: 'What is "cost basis"?', options: ['Current price', 'What you paid for shares', 'Dividend amount', 'Market cap'], correct: 1, explanation: 'Cost basis = purchase price + fees. Your break-even point.' },
    { q: 'FIFO means:', options: ['First In, First Out', 'Fees In, Fees Out', 'Full Investment', 'Fast In, Fast Out'], correct: 0, explanation: 'FIFO = First In, First Out. Oldest shares assumed sold first.' },
    { q: 'Why track cost basis?', options: ['To impress friends', 'Calculate taxes correctly', 'Brokers require it', 'No reason'], correct: 1, explanation: 'Cost basis determines taxable gain or loss when you sell.' }
  ], takeaway: 'Track all transactions. Cost basis is essential for taxes and true performance.' },

  20: { title: 'Understanding Your Holdings', subtitle: 'Analyzing what\'s in your portfolio', sections: [
    { id: 'intro', title: '💡 Portfolio Holdings Analysis', type: 'text', content: `Your holdings are investments you currently own. Review regularly to:\n\n• See total value at a glance\n• Identify concentration risks\n• Track winners and losers\n• Decide what to buy/sell next` },
    { id: 'demo', title: '📊 Interactive: Holdings View', type: 'demo', demoType: 'holdings-view' },
    { id: 'analysis', title: '🔍 What to Look For', type: 'text', content: `**Concentration Risk:** One stock > 10% of portfolio?\n\n**Winners Running:** Gains concentrated in one stock?\n\n**Losers Dragging:** Positions down 30%+? Is the thesis still valid?` },
    { id: 'keypoint', title: '⭐ Key Insight', type: 'highlight', content: `**Unrealized vs Realized:**\n\n**Unrealized** — Paper profit/loss (not taxable)\n**Realized** — You sold (taxable event)\n\nThis is why "buy and hold" is tax-efficient!` }
  ], quiz: [
    { q: 'An unrealized gain is:', options: ['Cash in account', 'Paper profit you haven\'t sold', 'Tax deduction', 'Realized loss'], correct: 1, explanation: 'Unrealized = you still own it. Only becomes taxable when sold.' },
    { q: 'If one stock becomes 25% of portfolio:', options: ['Buy more', 'Do nothing', 'Consider trimming', 'Sell everything'], correct: 2, explanation: 'Over-concentration is risky. Consider trimming to reduce risk.' },
    { q: 'How often review holdings?', options: ['Hourly', 'Daily', 'Monthly with quarterly deep dive', 'Only when selling'], correct: 2, explanation: 'Monthly reviews keep you informed without over-trading.' }
  ], takeaway: 'Review holdings regularly for concentration and drift. Unrealized gains aren\'t taxed until sold.' },

  21: { title: 'Portfolio Performance Analytics', subtitle: 'Measuring returns, benchmarks, and alpha', sections: [
    { id: 'intro', title: '💡 Measuring Performance', type: 'text', content: `"How am I doing?" seems simple, but measuring returns correctly is tricky!\n\n**Always compare to a benchmark!**\n"My portfolio is up 15%!"\n• S&P 500 up 20%? You underperformed\n• S&P 500 up 10%? You beat the market!` },
    { id: 'demo', title: '📊 Interactive: Performance', type: 'demo', demoType: 'performance-chart' },
    { id: 'alpha', title: '⭐ Alpha: The Holy Grail', type: 'text', content: `**Alpha = Your return - Expected return**\n\n• Positive alpha = You beat benchmark (skill!)\n• Negative alpha = You trailed\n\n**The hard truth:** Most professionals generate NEGATIVE alpha. After fees, >80% of active funds trail benchmarks.` },
    { id: 'keypoint', title: '⭐ Key Insight', type: 'highlight', content: `**Sharpe Ratio** — Return per unit of volatility\nAbove 1.0 is good. Above 2.0 is excellent!` }
  ], quiz: [
    { q: 'What is "alpha"?', options: ['Total return', 'Return above/below expected for risk', 'Highest return', 'First investment'], correct: 1, explanation: 'Alpha = actual return minus expected return.' },
    { q: 'Time-weighted return is best for:', options: ['Measuring deposits', 'Comparing to benchmarks', 'Calculating taxes', 'Daily tracking'], correct: 1, explanation: 'Time-weighted removes cash flow effects for fair comparison.' },
    { q: 'Most active funds:', options: ['Beat benchmarks', 'Trail benchmarks', 'Match exactly', 'No benchmarks'], correct: 1, explanation: 'Research shows >80% of active funds underperform after fees.' }
  ], takeaway: 'Compare to benchmarks. Most fail to beat index funds.' },

  22: { title: 'Measuring Portfolio Risk', subtitle: 'Volatility, beta, drawdowns, and risk metrics', sections: [
    { id: 'intro', title: '💡 What is Investment Risk?', type: 'text', content: `Risk = uncertainty of returns.\n\n**Types of risk:**\n📊 **Market Risk** — Whole market falls\n🏢 **Company Risk** — Specific company problems (diversifiable)\n💰 **Inflation Risk** — Money loses purchasing power` },
    { id: 'demo', title: '📊 Interactive: Risk Metrics', type: 'demo', demoType: 'risk-metrics' },
    { id: 'metrics', title: '📏 Key Risk Metrics', type: 'text', content: `**Volatility** — How much returns vary\n\n**Beta** — Sensitivity to market moves (Beta 1.0 = moves with market)\n\n**Maximum Drawdown** — Largest peak-to-trough decline` },
    { id: 'keypoint', title: '⭐ Key Insight', type: 'highlight', content: `If your portfolio dropped 30%, would you:\nA) Panic sell (bad!)\nB) Stay the course (good)\nC) Buy more (great!)\n\nIf A, you're taking too much risk!` }
  ], quiz: [
    { q: 'Beta measures:', options: ['Total return', 'How much stock moves vs market', 'Dividend yield', 'Company size'], correct: 1, explanation: 'Beta = sensitivity to market.' },
    { q: 'Maximum drawdown shows:', options: ['Daily returns', 'Largest historical decline', 'Average volatility', 'Best case'], correct: 1, explanation: 'Max drawdown = worst peak-to-trough decline.' },
    { q: 'If 30% drop would make you panic sell:', options: ['Buy more stocks', 'Reduce risk in portfolio', 'Check prices more', 'Ignore it'], correct: 1, explanation: 'If you can\'t stomach volatility, reduce risk.' }
  ], takeaway: 'Know your risk tolerance. Best portfolio is one you can hold through stress.' },

  23: { title: 'When & How to Rebalance', subtitle: 'Maintaining your target allocation', sections: [
    { id: 'intro', title: '💡 What is Rebalancing?', type: 'text', content: `**Rebalancing** = Adjusting portfolio back to target allocations.\n\n**Example:**\nStarting: 60% stocks / 40% bonds\nAfter rally: 75% stocks / 25% bonds\n\nYour risk INCREASED without you doing anything!` },
    { id: 'demo', title: '📊 Interactive: Rebalancing', type: 'demo', demoType: 'rebalance-tool' },
    { id: 'methods', title: '📅 Rebalancing Methods', type: 'text', content: `**Calendar:** Set schedule (quarterly, annually)\n**Threshold:** Rebalance when allocation drifts 5%+\n**Hybrid:** Check quarterly, only rebalance if drifted 5%+` },
    { id: 'keypoint', title: '⭐ Key Insight', type: 'highlight', content: `**Rebalancing forces "buy low, sell high."**\n\nSell your best performers, buy your worst performers.\nCounter-intuitive but it works!` }
  ], quiz: [
    { q: 'Rebalancing means:', options: ['Buying more winners', 'Returning to target allocation', 'Selling everything', 'Adding money'], correct: 1, explanation: 'Rebalancing = adjusting back to intended target allocation.' },
    { q: 'How often should most investors rebalance?', options: ['Daily', 'Weekly', 'Annually or semi-annually', 'Never'], correct: 2, explanation: 'Annual or semi-annual is enough for most people.' },
    { q: 'Rebalancing forces you to:', options: ['Buy high, sell low', 'Buy low, sell high', 'Only buy', 'Only sell'], correct: 1, explanation: 'Sell winners (high) and buy laggards (low).' }
  ], takeaway: 'Rebalance periodically. It forces disciplined "buy low, sell high" behavior.' },

  24: { title: 'Building an Effective Watchlist', subtitle: 'Organizing stocks you want to track', sections: [
    { id: 'intro', title: '💡 What is a Watchlist?', type: 'text', content: `A watchlist is a curated list of stocks you want to monitor — without owning them yet.\n\n🎯 **Track opportunities** — Stocks to buy at lower price\n📊 **Monitor competition** — Watch competitors\n⏰ **Stay ready** — Know what to buy when market dips` },
    { id: 'demo', title: '📊 Interactive: Watchlist', type: 'demo', demoType: 'watchlist-builder' },
    { id: 'keypoint', title: '⭐ Key Insight', type: 'highlight', content: `**Best investors have a shopping list ready.**\n\nWhen the market drops: Amateurs panic and sell. Pros buy from their watchlist.\n\nCrashes are sales! Know what to buy BEFORE the sale starts.` }
  ], quiz: [
    { q: 'A watchlist is for:', options: ['Stocks you own', 'Stocks you\'re monitoring', 'Stocks you\'ve sold', 'All stocks'], correct: 1, explanation: 'A watchlist tracks stocks you\'re interested in but don\'t own.' },
    { q: 'A good watchlist size is:', options: ['100+ stocks', '20-30 stocks', '5 stocks', '1 stock'], correct: 1, explanation: '20-30 is manageable. Too many becomes overwhelming.' },
    { q: 'Why prepare watchlist before a crash?', options: ['Sell faster', 'Know what to buy when prices drop', 'Crashes never happen', 'Tax purposes'], correct: 1, explanation: 'A ready list means buying quality stocks on sale without panic.' }
  ], takeaway: 'Maintain focused watchlist of 20-30 stocks. Be ready when opportunities arise.' },

  25: { title: 'Setting Price Alerts', subtitle: 'Getting notified at key price levels', sections: [
    { id: 'intro', title: '💡 Why Use Price Alerts?', type: 'text', content: `Price alerts notify you when a stock reaches a specific price.\n\n⏰ **Time saver** — No constant checking\n😴 **Sleep well** — Set it and forget it\n🎯 **Discipline** — Stick to your plan` },
    { id: 'demo', title: '📊 Interactive: Alert Setup', type: 'demo', demoType: 'price-alert' },
    { id: 'keypoint', title: '⭐ Key Insight', type: 'highlight', content: `**Alerts enforce your plan when emotions run high.**\n\nYour calm past self guides your emotional future self!` }
  ], quiz: [
    { q: 'Price alerts help you:', options: ['Trade more', 'Stick to your plan', 'Guarantee profits', 'Avoid losses'], correct: 1, explanation: 'Alerts notify at key levels so you execute pre-planned strategy.' },
    { q: 'When setting buy alert:', options: ['Set exactly at target', 'Set above target', 'Set slightly below target', 'Don\'t use alerts'], correct: 2, explanation: 'Set alerts before your target — prices move fast.' },
    { q: 'Alerts help with:', options: ['Only greed', 'Only fear', 'Both greed and fear', 'Neither'], correct: 2, explanation: 'Alerts help take profits (greed) and cut losses (fear).' }
  ], takeaway: 'Use alerts to enforce your plan. Your calm self makes better decisions.' },

  26: { title: 'News Alerts & Staying Informed', subtitle: 'Getting relevant news without noise', sections: [
    { id: 'intro', title: '💡 The News Dilemma', type: 'text', content: `**What matters:**\n✅ Earnings announcements\n✅ Major company news (M&A, leadership)\n✅ Regulatory changes\n\n**What doesn't:**\n❌ Hourly market commentary\n❌ Pundit predictions\n❌ Social media hype` },
    { id: 'keypoint', title: '⭐ Key Insight', type: 'highlight', content: `**By the time you read it, it's priced in.**\n\nHeadlines move markets in milliseconds. Your edge isn't speed. It's understanding and long-term perspective.` }
  ], quiz: [
    { q: 'What news matters most?', options: ['Pundit predictions', 'Earnings and major announcements', 'Daily commentary', 'Social media'], correct: 1, explanation: 'Earnings and significant events actually impact fundamentals.' },
    { q: 'Why is most news unhelpful?', options: ['Usually wrong', 'Already priced in', 'Markets closed', 'Doesn\'t matter'], correct: 1, explanation: 'Markets react in milliseconds. You\'re always late.' },
    { q: 'Best news filter is asking:', options: ['Is this interesting?', 'Is this actionable for my thesis?', 'Is this exciting?', 'Do others care?'], correct: 1, explanation: 'If news doesn\'t change your thesis, it\'s noise.' }
  ], takeaway: 'Set targeted alerts. Filter aggressively — most news is noise.' },

  27: { title: 'Following Congress Trades', subtitle: 'Tracking what politicians buy and sell', sections: [
    { id: 'intro', title: '💡 Why Track Congress?', type: 'text', content: `Members of Congress must disclose trades within 45 days.\n\n**The theory:** They have access to non-public info. Trades might signal upcoming policy.` },
    { id: 'demo', title: '📊 Interactive: Congress Tracker', type: 'demo', demoType: 'congress-trades' },
    { id: 'warning', title: '⚠️ Important Caveats', type: 'warning', content: `**Don't blindly follow!**\n\n1. **45-day delay** — Info is old\n2. **Unclear motives** — Could be tax planning\n3. **No context** — Don't know their portfolio\n\nUse as ONE input, not your whole strategy.` },
    { id: 'keypoint', title: '⭐ Key Insight', type: 'highlight', content: `One trade = noise. Multiple members + unusual timing = potential signal.\n\nUse to find ideas, then do your own research.` }
  ], quiz: [
    { q: 'Congress must disclose trades within:', options: ['24 hours', '7 days', '45 days', '1 year'], correct: 2, explanation: '45 days — a significant delay.' },
    { q: 'High-signal Congressional trade:', options: ['Small index purchase', 'Multiple members buying same stock', 'Spouse buying any stock', 'Any trade'], correct: 1, explanation: 'Multiple members making similar trades is noteworthy.' },
    { q: 'Congress trades best used for:', options: ['Copying exactly', 'Generating ideas to research', 'Day trading', 'Nothing'], correct: 1, explanation: 'Use for idea generation, not sole strategy.' }
  ], takeaway: 'Congress tracking surfaces ideas, but 45-day delay means do your own research.' },

  28: { title: 'Insider Trading Signals', subtitle: 'Following legal insider buying and selling', sections: [
    { id: 'intro', title: '💡 Legal Insider Trading', type: 'text', content: `**Insiders** = Executives, directors, large shareholders\n\nThey MUST report trades to SEC within 2 business days (Form 4).\n\n**Key insight:** BUYING is more meaningful than selling.\n• Selling: Many reasons (taxes, diversification)\n• Buying: Only ONE reason — they think it will rise!` },
    { id: 'demo', title: '📊 Interactive: Insider Activity', type: 'demo', demoType: 'insider-trades' },
    { id: 'clusters', title: '🎯 Cluster Buying', type: 'highlight', content: `**Cluster buying = Multiple insiders buying within short period**\n\nThis is the strongest signal! Three executives buying in the same week = high conviction across leadership.\n\nStudies show stocks with cluster buying outperform by 5-10% annually.` }
  ], quiz: [
    { q: 'Why is insider BUYING more significant?', options: ['More common', 'Selling has many reasons, buying has one', 'Buying is illegal', 'No difference'], correct: 1, explanation: 'Insiders only buy when they think stock will rise.' },
    { q: 'What is "cluster buying"?', options: ['One big purchase', 'Multiple insiders buying together', 'Buying in 100s', 'Computer trading'], correct: 1, explanation: 'Cluster buying = multiple insiders buying around same time.' },
    { q: 'Most informative insider trades?', options: ['Junior employees', 'CEO and CFO', 'Former employees', 'Consultants'], correct: 1, explanation: 'C-suite executives have best view of company.' }
  ], takeaway: 'Insider buying, especially cluster buying by C-suite, is one of strongest bullish signals.' },

  29: { title: 'Whale Watching (Institutional)', subtitle: 'Tracking hedge funds and major investors', sections: [
    { id: 'intro', title: '💡 What Are 13F Filings?', type: 'text', content: `**13F Filings** = Quarterly reports showing what big investors own\n\n**Who files:** Anyone managing $100M+ in stocks — hedge funds, mutual funds, pension funds` },
    { id: 'demo', title: '📊 Interactive: Fund Holdings', type: 'demo', demoType: 'whale-holdings' },
    { id: 'limits', title: '⚠️ 13F Limitations', type: 'warning', content: `1. **45-day delay** — Data is old\n2. **No short positions** — Only shows longs\n3. **No context** — Could be a hedge` },
    { id: 'keypoint', title: '⭐ Key Insight', type: 'highlight', content: `**Multiple top investors buying same stock = worth investigating.**\n\nOne whale = Could be anything. Five whales = Pattern worth researching.` }
  ], quiz: [
    { q: '13F filings required from:', options: ['All investors', 'Managers with $100M+ stocks', 'Only Buffett', 'Retail investors'], correct: 1, explanation: 'Investment managers with $100M+ must file 13F quarterly.' },
    { q: 'Main 13F limitation:', options: ['Expensive', '45 days delayed', 'Inaccurate', 'Too detailed'], correct: 1, explanation: '45-day delay means data is old when published.' },
    { q: '13F data best for:', options: ['Day trading', 'Generating ideas', 'Exact entry points', 'Short selling'], correct: 1, explanation: 'Use to find what smart money buys, then research.' }
  ], takeaway: '13F shows what big investors own. Use for ideas, not copying — data is 45 days old.' },

  30: { title: 'Spotting Market Anomalies', subtitle: 'Unusual volume, price movements, and patterns', sections: [
    { id: 'intro', title: '💡 What Are Anomalies?', type: 'text', content: `**Anomalies** = Unusual market behavior that deviates from normal\n\n**Types to watch:**\n• Unusual volume spikes\n• Large options activity\n• Price divergences\n• Dark pool transactions` },
    { id: 'demo', title: '📊 Interactive: Anomaly Scanner', type: 'demo', demoType: 'anomaly-scanner' },
    { id: 'keypoint', title: '⭐ Key Insight', type: 'highlight', content: `**Anomalies are smoke, not fire.**\n\nUnusual activity means something is happening — but you don't know what!\n\nUse anomalies to prompt research, not as trade signals.` }
  ], quiz: [
    { q: 'Unusual volume might indicate:', options: ['Nothing', 'Big players accumulating/distributing', 'Market closed', 'Weekends'], correct: 1, explanation: 'Volume spikes mean big players entering or exiting.' },
    { q: 'High volume + rising price suggests:', options: ['Selling pressure', 'Accumulation (buying)', 'Indecision', 'Low interest'], correct: 1, explanation: 'High volume with rising prices = buyers in control.' },
    { q: 'Anomalies should be used to:', options: ['Trade immediately', 'Prompt further research', 'Panic sell', 'Ignore completely'], correct: 1, explanation: 'Anomalies signal something happening — research before acting.' }
  ], takeaway: 'Unusual volume or options activity is a signal to investigate, not automatic trade.' },

  31: { title: 'Social Media Sentiment', subtitle: 'Using social signals without getting burned', sections: [
    { id: 'intro', title: '💡 Social Media as Data', type: 'text', content: `**Potential value:** Early product feedback, brand sentiment shifts, viral trends\n\n**Dangers:** Manipulation (pump & dump), echo chambers, FOMO-inducing` },
    { id: 'demo', title: '📊 Interactive: Sentiment', type: 'demo', demoType: 'social-sentiment' },
    { id: 'warning', title: '⚠️ The Meme Stock Trap', type: 'warning', content: `Most people who buy meme stock hype: Buy near the top, "diamond hands" through crash, lose money.\n\n**Rule:** If it's trending, you're probably late.` },
    { id: 'keypoint', title: '⭐ Key Insight', type: 'highlight', content: `**Social media is a contrary indicator at extremes.**\n\n• Maximum hype = Often near the top\n• Maximum despair = Often near the bottom` }
  ], quiz: [
    { q: 'Social sentiment most useful as:', options: ['Primary signal', 'Contrarian indicator at extremes', 'Guaranteed winners', 'Insider tips'], correct: 1, explanation: 'Extreme sentiment often marks turning points.' },
    { q: 'When stock is trending on Reddit:', options: ['Early', 'Late', 'Right on time', 'Only one'], correct: 1, explanation: 'By the time something trends, early move happened.' },
    { q: 'Look for in social sentiment:', options: ['Most posts', 'Sentiment shifts and quality sources', 'Anonymous tips', 'Rocket emojis'], correct: 1, explanation: 'Look for changes in direction and credible sources.' }
  ], takeaway: 'Social media provides early signals, but max hype = you\'re late. Use as contrarian indicator.' },

  32: { title: 'ETFs Explained', subtitle: 'Exchange-Traded Funds: The easiest way to diversify', sections: [
    { id: 'intro', title: '💡 What is an ETF?', type: 'text', content: `An **ETF (Exchange-Traded Fund)** is a basket of investments you can buy with a single purchase.\n\n**Example: SPY (S&P 500 ETF)**\n• One share = tiny piece of 500 companies\n• Instant diversification\n• Trades like a stock\n\n**Why ETFs are popular:**\n• Low cost (fees often under 0.1%)\n• Instant diversification\n• Tax efficient` },
    { id: 'demo', title: '📊 Interactive: ETF Holdings', type: 'demo', demoType: 'etf-holdings' },
    { id: 'keypoint', title: '⭐ Key Insight', type: 'highlight', content: `**Warren Buffett's advice for most people:**\n\n"Put 90% in a low-cost S&P 500 index fund."\n\nFor most investors, 2-3 ETFs beats complicated stock picking!` }
  ], quiz: [
    { q: 'What is an ETF?', options: ['A single stock', 'A basket of investments', 'A type of bond', 'Savings account'], correct: 1, explanation: 'ETF = basket of stocks/bonds you buy as one unit.' },
    { q: 'What does SPY track?', options: ['NASDAQ', 'S&P 500', 'Dow Jones', 'Russell 2000'], correct: 1, explanation: 'SPY is the most popular S&P 500 ETF.' },
    { q: 'Why are ETFs popular?', options: ['High risk', 'Low cost & diversification', 'Guaranteed returns', 'No fees'], correct: 1, explanation: 'ETFs offer instant diversification at very low cost.' }
  ], takeaway: 'ETFs let you buy hundreds of stocks in one purchase. Easiest way to diversify.' },

  33: { title: "ETF X-Ray: What's Inside?", subtitle: 'Understanding ETF holdings and concentration', sections: [
    { id: 'intro', title: '💡 Looking Inside ETFs', type: 'text', content: `Not all ETFs are equal! Looking inside reveals:\n\n**Concentration risk:** How much in top 10 holdings?\n**Sector exposure:** Which industries dominate?\n**Hidden overlap:** Own QQQ and XLK? Lots of Apple overlap!` },
    { id: 'demo', title: '📊 Interactive: ETF X-Ray', type: 'demo', demoType: 'etf-xray' },
    { id: 'costs', title: '💰 Understanding Costs', type: 'text', content: `**Expense Ratio:** Annual fee as % of assets\n\nImpact over 30 years on $100K:\n• 0.03% → $746,000\n• 1.00% → $574,000\n\n**Low fees = $172K more!**` },
    { id: 'keypoint', title: '⭐ Key Insight', type: 'highlight', content: `**Always check what you're buying.**\n\nTwo "diversified" ETFs might have massive overlap!\nX-ray your ETFs to understand TRUE exposure.` }
  ], quiz: [
    { q: 'Why is ETF overlap a problem?', options: ['ETFs don\'t overlap', 'Less diversified than you think', 'Increases returns', 'Brokers require it'], correct: 1, explanation: 'Multiple ETFs with same holdings doesn\'t add diversification.' },
    { q: 'What does expense ratio measure?', options: ['One-time fee', 'Annual cost as % of assets', 'Trading fee', 'Dividend yield'], correct: 1, explanation: 'Expense ratio is annual management fee.' },
    { q: 'Lower expense ratios result in:', options: ['Lower returns', 'Higher returns over time', 'No difference', 'More risk'], correct: 1, explanation: 'Lower fees = more money invested = higher returns over decades.' }
  ], takeaway: 'Check ETF holdings for concentration and overlap. Low fees compound into big savings.' },

  34: { title: 'Comparing ETFs', subtitle: 'Choosing between similar ETFs', sections: [
    { id: 'intro', title: '💡 ETF Comparison Factors', type: 'text', content: `**Key factors:**\n💰 **Expense Ratio** — Lower is better\n📊 **Tracking Error** — How closely follows index\n💧 **Liquidity** — Trading volume, bid-ask spread\n📈 **AUM** — Bigger = more stable` },
    { id: 'demo', title: '📊 Interactive: ETF Comparison', type: 'demo', demoType: 'etf-comparison' },
    { id: 'keypoint', title: '⭐ Key Insight', type: 'highlight', content: `**For long-term: Expense ratio is king.**\n\nThe difference between 0.03% and 0.09% seems tiny, but over 30 years on $100K, it's ~$8,000!\n\nUnless actively trading, choose lowest-cost option.` }
  ], quiz: [
    { q: 'Most important factor for long-term ETF investors:', options: ['Brand name', 'Expense ratio', 'Daily volume', 'Number of holdings'], correct: 1, explanation: 'For buy-and-hold, expense ratio has biggest impact.' },
    { q: 'SPY vs VOO — when is SPY better?', options: ['Always', 'Active trading (more liquid)', 'Long-term investing', 'Never'], correct: 1, explanation: 'SPY has higher volume and tighter spreads for frequent trading.' },
    { q: 'What is tracking error?', options: ['Price of ETF', 'How closely ETF follows index', 'Trading fee', 'Number of holdings'], correct: 1, explanation: 'Tracking error measures how closely returns match the index.' }
  ], takeaway: 'When choosing similar ETFs, prioritize low expense ratios for long-term, liquidity for trading.' },

  35: { title: 'Investing in India', subtitle: 'Understanding the Indian stock market', sections: [
    { id: 'intro', title: '💡 Why India?', type: 'text', content: `India is the world's fastest-growing major economy.\n\n**The opportunity:**\n• 1.4 billion people (largest population)\n• Growing middle class\n• Young population\n\n**Key indices:**\n• **NIFTY 50** — India's S&P 500\n• **SENSEX** — 30 largest companies` },
    { id: 'demo', title: '📊 Interactive: India Dashboard', type: 'demo', demoType: 'india-market' },
    { id: 'access', title: '🌏 How to Invest', type: 'text', content: `**From the US:**\n\n📈 **India ETFs:** INDA (iShares MSCI India)\n\n🏢 **ADRs:** Infosys (INFY), HDFC Bank (HDB), ICICI Bank (IBN)` },
    { id: 'keypoint', title: '⭐ Key Insight', type: 'highlight', content: `**India is a long-term growth story.**\n\nExpect volatility! Position 5-10% max. Currency risk is real!` }
  ], quiz: [
    { q: 'What is the NIFTY 50?', options: ['US index', 'India\'s main stock index', 'A cryptocurrency', 'A bond fund'], correct: 1, explanation: 'NIFTY 50 is India\'s primary stock market index.' },
    { q: 'Easiest way to invest in India from US:', options: ['Flying to Mumbai', 'India ETFs like INDA', 'Rupee trading', 'Real estate'], correct: 1, explanation: 'ETFs provide easy, liquid exposure without complexity.' },
    { q: 'What makes India attractive?', options: ['Shrinking population', 'Growing middle class and young workforce', 'Low growth', 'Strong US dollar'], correct: 1, explanation: 'India\'s demographics and growth make it attractive long-term.' }
  ], takeaway: 'India offers long-term growth. Use ETFs or ADRs; expect volatility and currency risk.' },

  36: { title: 'Currency & Forex Basics', subtitle: 'Understanding exchange rates and currency impact', sections: [
    { id: 'intro', title: '💡 Why Currency Matters', type: 'text', content: `When investing internationally, **currency matters as much as stock performance!**\n\n**Strong dollar** = Bad for international returns\n**Weak dollar** = Good for international returns` },
    { id: 'demo', title: '📊 Interactive: Currency Rates', type: 'demo', demoType: 'forex-rates' },
    { id: 'keypoint', title: '⭐ Key Insight', type: 'highlight', content: `**Currency risk usually not worth hedging for long-term.**\n\nCurrencies mean-revert over time, hedging costs money, and predicting currency moves is hard.` }
  ], quiz: [
    { q: 'A strong dollar is generally:', options: ['Good for international investments', 'Bad for international investments', 'No effect', 'Good for exports'], correct: 1, explanation: 'Strong dollar means foreign investments worth less in dollars.' },
    { q: 'Currency-hedged ETFs:', options: ['Eliminate stock risk', 'Reduce currency impact', 'Guarantee profits', 'Always better'], correct: 1, explanation: 'Currency-hedged ETFs aim to remove exchange rate impact.' },
    { q: 'For long-term investors, currency risk is:', options: ['Critical to eliminate', 'Usually acceptable without hedging', 'Impossible to manage', 'Guaranteed loss'], correct: 1, explanation: 'Over long periods, currency movements tend to average out.' }
  ], takeaway: 'Currency impacts international returns. For long-term, accepting risk is often fine.' },

  37: { title: 'Crypto Fundamentals', subtitle: 'Understanding Bitcoin, Ethereum, and the crypto ecosystem', sections: [
    { id: 'intro', title: '💡 What is Cryptocurrency?', type: 'text', content: `Cryptocurrency is **digital money** secured by cryptography.\n\n**Key concepts:**\n• **Blockchain** — Distributed ledger\n• **Bitcoin** — First and largest (digital gold)\n• **Ethereum** — Programmable (smart contracts)\n\n**As an asset class:**\n• Highly volatile (50%+ swings normal)\n• Regulatory uncertainty` },
    { id: 'demo', title: '📊 Interactive: Crypto Prices', type: 'demo', demoType: 'crypto-prices' },
    { id: 'warning', title: '⚠️ Crypto Risks', type: 'warning', content: `**Unique risks:**\n• **Volatility** — 50-80% drops happen\n• **Security** — Hacks, lost keys, scams\n• **No intrinsic value** — Pure supply/demand\n\n**Golden rule:** Only invest what you'd be OK losing entirely.` }
  ], quiz: [
    { q: 'Bitcoin is best described as:', options: ['Company stock', 'Digital currency/store of value', 'A bond', 'Government money'], correct: 1, explanation: 'Bitcoin is decentralized digital currency.' },
    { q: 'Reasonable crypto allocation:', options: ['50%', '1-5%', '0%', '100%'], correct: 1, explanation: 'Due to high volatility, limit crypto to 1-5% of portfolio.' },
    { q: 'Main difference between stocks and crypto:', options: ['Crypto always better', 'Crypto has no underlying cash flows', 'Stocks more volatile', 'No difference'], correct: 1, explanation: 'Crypto has no earnings, dividends, or cash flows.' }
  ], takeaway: 'Crypto is highly speculative. Position small (1-5%), only invest what you can lose.' },

  38: { title: 'Paper Trading: Practice Risk-Free', subtitle: 'Learning to trade without risking real money', sections: [
    { id: 'intro', title: '💡 What is Paper Trading?', type: 'text', content: `**Paper trading** = Trading with fake money to learn.\n\n**Benefits:**\n• No financial risk\n• Test strategies before going live\n• Build discipline and habits\n• Learn platform mechanics` },
    { id: 'demo', title: '📊 Interactive: Paper Trade', type: 'demo', demoType: 'paper-trade' },
    { id: 'warning', title: '⚠️ Limitations', type: 'warning', content: `**Not exactly like real trading:**\n\n• **No emotions** — Real money creates fear/greed\n• **Perfect fills** — Real market has slippage\n\n**Transition strategy:** Paper trade until profitable, then start with tiny real money positions.` }
  ], quiz: [
    { q: 'Paper trading uses:', options: ['Real money, real stocks', 'Fake money, real market data', 'Real money, fake data', 'No data'], correct: 1, explanation: 'Paper trading simulates trading with fake money using real data.' },
    { q: 'Main limitation of paper trading:', options: ['Too expensive', 'No emotional pressure', 'Markets move differently', 'Strategies don\'t work'], correct: 1, explanation: 'Real money involves emotions paper trading doesn\'t replicate.' },
    { q: 'Transition from paper to real:', options: ['All at once', 'Start tiny and scale up', 'Never transition', 'Only volatile days'], correct: 1, explanation: 'Start small with real money, prove yourself, then increase.' }
  ], takeaway: 'Paper trade before risking real money. Emotions change everything when real money involved.' },

  39: { title: 'Using AI for Research', subtitle: 'Leveraging AI tools for investment analysis', sections: [
    { id: 'intro', title: '💡 AI in Investment Research', type: 'text', content: `AI can accelerate research, but it's a tool — not a replacement for judgment.\n\n**What AI can help with:**\n• Summarizing earnings calls\n• Explaining complex concepts\n• Analyzing financial statements\n• Generating investment ideas\n\n**What AI cannot do:**\n• Predict the future reliably\n• Replace human judgment` },
    { id: 'warning', title: '⚠️ AI Limitations', type: 'warning', content: `**Critical limitations:**\n\n🔄 **Knowledge cutoffs** — AI doesn't know recent events\n❌ **Hallucinations** — AI can state wrong facts confidently\n📊 **No real-time data** — Can't give current prices` },
    { id: 'keypoint', title: '⭐ Key Insight', type: 'highlight', content: `**AI is a research assistant, not portfolio manager.**\n\nUse AI to: Learn faster, generate ideas, challenge your thinking\n\nDon't use AI to: Make decisions for you, replace due diligence` }
  ], quiz: [
    { q: 'AI best used in investing for:', options: ['Making decisions', 'Predicting prices', 'Research acceleration', 'Replacing advisors'], correct: 2, explanation: 'AI excels at summarizing and explaining — not predicting.' },
    { q: 'What is AI "hallucination"?', options: ['AI dreaming', 'AI stating incorrect info confidently', 'AI crash', 'AI humor'], correct: 1, explanation: 'AI can generate confident but incorrect information.' },
    { q: 'When using AI for research:', options: ['Trust everything', 'Never use', 'Verify facts and use judgment', 'Only entertainment'], correct: 2, explanation: 'AI is powerful but has limits. Verify and apply judgment.' }
  ], takeaway: 'AI accelerates research but isn\'t a crystal ball. Use as tool while maintaining judgment.' },

  40: { title: 'Understanding Earnings Season', subtitle: 'When companies report and how markets react', sections: [
    { id: 'intro', title: '💡 What is Earnings Season?', type: 'text', content: `**Earnings season** = When most companies report quarterly results.\n\n**Timing:** Q1 (Apr-May), Q2 (Jul-Aug), Q3 (Oct-Nov), Q4 (Jan-Feb)\n\n**What's reported:** Revenue, EPS, Forward guidance` },
    { id: 'demo', title: '📊 Interactive: Earnings Calendar', type: 'demo', demoType: 'earnings-calendar' },
    { id: 'keypoint', title: '⭐ Key Insight', type: 'highlight', content: `**The reaction is often more important than the earnings.**\n\nA stock that falls on a beat → Market expected more\nA stock that rises on a miss → Low expectations priced in` }
  ], quiz: [
    { q: 'Earnings season happens:', options: ['Once per year', 'Four times per year', 'Monthly', 'Weekly'], correct: 1, explanation: 'Earnings season occurs quarterly.' },
    { q: 'What matters most for price reaction?', options: ['Raw numbers', 'Beat/miss vs expectations', 'CEO personality', 'Trading volume'], correct: 1, explanation: 'Stocks react to whether results beat or miss expectations.' },
    { q: 'Forward guidance is:', options: ['Historical data', 'Company\'s outlook', 'Analyst opinions', 'Government regulations'], correct: 1, explanation: 'Guidance is management\'s view of future performance.' }
  ], takeaway: 'Earnings season is quarterly. Focus on beat/miss vs expectations and guidance.' },

  41: { title: 'Analyzing Earnings History', subtitle: 'Learning from past earnings patterns', sections: [
    { id: 'intro', title: '💡 Why History Matters', type: 'text', content: `Earnings history reveals patterns:\n\n**What to look for:**\n• Consistent beats or misses?\n• Revenue growth trend\n• Margin expansion or contraction\n• Management credibility (guidance accuracy)` },
    { id: 'demo', title: '📊 Interactive: Earnings History', type: 'demo', demoType: 'earnings-history' },
    { id: 'keypoint', title: '⭐ Key Insight', type: 'highlight', content: `**Trend matters more than single quarter.**\n\nOne bad quarter ≠ Sell\nConsistent deterioration = Warning sign\n\nLook at 8-12 quarters to see the real picture.` }
  ], quiz: [
    { q: 'How many quarters to review?', options: ['1 quarter', '2-3 quarters', '8-12 quarters', 'Only most recent'], correct: 2, explanation: '8-12 quarters reveals meaningful trends.' },
    { q: 'Company that consistently beats earnings:', options: ['Warning sign', 'Often a quality company', 'Always overvalued', 'Will miss next time'], correct: 1, explanation: 'Consistent beats indicate conservative guidance and quality.' },
    { q: 'Red flag in earnings history:', options: ['One bad quarter', 'Consistent margin decline', 'One-time charges', 'All of these'], correct: 1, explanation: 'Consistent deterioration over multiple quarters is a warning.' }
  ], takeaway: 'Look at 8-12 quarters for trends. Consistent patterns matter more than single quarters.' },

  42: { title: 'Post-Earnings Move Analysis', subtitle: 'Understanding and predicting earnings reactions', sections: [
    { id: 'intro', title: '💡 Post-Earnings Drift', type: 'text', content: `**Post-Earnings Drift** = Stocks tend to continue in the direction of earnings surprise.\n\n**The pattern:**\n• Beat expectations → Often continues rising\n• Miss expectations → Often continues falling\n\nThis drift can continue for 1-3 months!` },
    { id: 'demo', title: '📊 Interactive: Earnings Moves', type: 'demo', demoType: 'post-earnings' },
    { id: 'keypoint', title: '⭐ Key Insight', type: 'highlight', content: `**The initial reaction isn't always right.**\n\nStocks often overreact initially, then find equilibrium.\n\n**Best practice:** Wait 1-2 days for dust to settle before deciding.` }
  ], quiz: [
    { q: 'Post-earnings drift is:', options: ['Random movement', 'Tendency to continue in direction of surprise', 'Always reversal', 'No pattern'], correct: 1, explanation: 'Stocks tend to drift in direction of surprise for weeks.' },
    { q: 'If actual move less than implied:', options: ['Big profits for buyers', 'IV crush hurts buyers', 'No effect', 'Stock reverses'], correct: 1, explanation: 'Smaller move means IV crush hurts option buyers.' },
    { q: 'After big earnings move:', options: ['Buy/sell immediately', 'Wait 1-2 days for dust to settle', 'Do opposite', 'Ignore'], correct: 1, explanation: 'Waiting lets you see if reaction makes sense.' }
  ], takeaway: 'Stocks drift in direction of surprise. Wait for dust to settle. Compare to implied expectations.' },

  43: {
    title: 'SMC: Market Structure (BOS & CHoCH)',
    subtitle: 'How institutions create and break market structure — the foundation of all SMC trading',
    sections: [
      { id: 'intro', title: '🏗️ Why Market Structure is Everything', type: 'text', content: `Before you can identify any SMC setup, you must understand market structure. It tells you WHO is in control — buyers or sellers — and WHEN that control is shifting.\n\nThe two most important concepts:\nBOS (Break of Structure) — confirms the current trend continues\nCHoCH (Change of Character) — warns that the trend may be reversing\n\nEverything else in SMC — order blocks, FVGs, liquidity — is only valid once you understand market structure.` },
      { id: 'bullish', title: '📈 Bullish Market Structure', type: 'text', content: `In an uptrend, price makes Higher Highs (HH) and Higher Lows (HL).\n\nBOS in an uptrend: Price breaks above a previous Higher High — confirms buyers are still in control. Look for buys on the next pullback.\n\nWhy it matters: Every HH broken is a BOS. Institutions use these breaks to confirm their positions are working.` },
      { id: 'bearish', title: '📉 Bearish Market Structure', type: 'text', content: `In a downtrend, price makes Lower Lows (LL) and Lower Highs (LH).\n\nBOS in a downtrend: Price breaks below a previous Lower Low — confirms sellers are still in control. Look for sells on the next bounce.\n\nKey rule: Always trade WITH the structure, not against it.` },
      { id: 'choch', title: '⚠️ CHoCH — The Reversal Warning', type: 'text', content: `Change of Character (CHoCH) is the first sign that market structure is shifting.\n\nIn a downtrend — bullish CHoCH: Price breaks above the most recent Lower High for the first time. This does NOT confirm reversal yet, but warns you the selling pressure is weakening.\n\nCHoCH vs BOS:\nBOS = trend continuation (same direction)\nCHoCH = potential trend reversal (opposite direction)\n\nPro tip: Wait for a BOS in the new direction AFTER a CHoCH to confirm the full reversal.` },
      { id: 'premium', title: '💎 Premium vs Discount Zones', type: 'highlight', content: `Draw a range from last swing low to last swing high.\nTop 50% = Premium (expensive) — where you SELL\nBottom 50% = Discount (cheap) — where you BUY\n\nInstitutions always buy at discount and sell at premium. Retail traders do the opposite and lose.\n\nRule: Only look for buys when price is in discount. Only look for sells when price is in premium.` }
    ],
    quiz: [
      { q: 'What does BOS (Break of Structure) confirm?', options: ['Trend reversal', 'Trend continuation', 'Sideways market', 'Volume spike'], correct: 1, explanation: 'BOS confirms the existing trend continues — price breaking a HH in uptrend or LL in downtrend.' },
      { q: 'What is a CHoCH (Change of Character)?', options: ['A confirmed trend reversal', 'The first warning that trend may be reversing', 'A BOS in reverse', 'When volume changes'], correct: 1, explanation: 'CHoCH is the FIRST warning — not a confirmed reversal yet.' },
      { q: 'In an uptrend, where should you look for buy entries?', options: ['At new highs', 'In the premium zone', 'In the discount zone', 'At equilibrium only'], correct: 2, explanation: 'Always buy in discount (bottom 50% of range) — where institutions accumulate.' },
      { q: 'What defines a bullish market structure?', options: ['Lower highs and lower lows', 'Higher highs and higher lows', 'Equal highs and equal lows', 'Random price movement'], correct: 1, explanation: 'Bullish structure = HH + HL. Each peak and trough is higher than the previous.' }
    ],
    takeaway: 'Market structure is the foundation. Identify BOS to trade with the trend, watch CHoCH for reversals, and always trade buys in discount and sells in premium.'
  },

  44: {
    title: 'SMC: Liquidity & Stop Hunts',
    subtitle: 'How institutions hunt retail stop losses before making their real move',
    sections: [
      { id: 'intro', title: '💧 What is Liquidity?', type: 'text', content: `In SMC, liquidity means clusters of stop loss orders sitting in the market.\n\nRetail traders place their stops at obvious places:\nAbove recent highs (stop losses of short sellers)\nBelow recent lows (stop losses of long buyers)\n\nInstitutions NEED these orders to fill their massive positions. So they deliberately push price into these zones — this is called a liquidity grab or stop hunt.\n\nAfter grabbing liquidity, they reverse price in the opposite direction.` },
      { id: 'buyside', title: '📈 Buy-Side Liquidity (BSL)', type: 'text', content: `Buy-side liquidity sits ABOVE price — specifically above recent swing highs, equal highs, trendline highs, and round numbers.\n\nWhat institutions do: Push price up through these highs, triggering all the buy stops. This creates the volume institutions need to fill large sell positions.\n\nThe trap: Retail sees price breaking a high and thinks it is a breakout. They buy. Institutions use their buying to exit their sells.` },
      { id: 'sellside', title: '📉 Sell-Side Liquidity (SSL)', type: 'text', content: `Sell-side liquidity sits BELOW price — specifically below recent swing lows, equal lows, trendline lows, and round numbers.\n\nWhat institutions do: Push price down through these lows, triggering all the sell stops. This creates volume to fill large buy positions.\n\nThe trap: Retail sees price breaking a low and thinks it is a breakdown. They sell. Institutions use their selling to enter buys.` },
      { id: 'equalhighs', title: '⚖️ Equal Highs & Equal Lows', type: 'text', content: `Equal Highs (EQH) and Equal Lows (EQL) are among the most reliable liquidity targets.\n\nWhen price tests the SAME level twice without breaking it, retail traders see it as strong support/resistance. They pile on stops just above/below these levels.\n\nEQH = Target for price to hunt upward then reverse down\nEQL = Target for price to hunt downward then reverse up\n\nHow to trade it:\n1. Identify equal highs or lows on your chart\n2. Wait for price to sweep through them\n3. Look for a quick rejection/reversal candle\n4. Enter in the direction of the reversal` },
      { id: 'inducement', title: '🪤 Inducement — The Ultimate Trap', type: 'highlight', content: `Inducement is when institutions create a FAKE low or high to attract retail traders before the real move.\n\nExample in a downtrend:\n1. Price makes a series of lower lows\n2. Price suddenly bounces and creates a small higher low\n3. Retail bulls see this and think CHoCH — they buy\n4. Price sweeps below their stops (the inducement low)\n5. THEN the real move up begins from the actual order block\n\nRule: Always ask — is this a real CHoCH or is it inducement?` }
    ],
    quiz: [
      { q: 'Where does buy-side liquidity sit?', options: ['Below recent lows', 'Above recent highs', 'At equilibrium', 'At moving averages'], correct: 1, explanation: 'BSL sits above recent highs — these are stop losses of short sellers (buy orders).' },
      { q: 'Why do institutions hunt stop losses?', options: ['To manipulate markets illegally', 'To fill large orders using retail volume', 'To confuse retail traders', 'To create news'], correct: 1, explanation: 'Institutions need massive volume to fill positions. Retail stop losses provide that volume.' },
      { q: 'What are equal highs a sign of?', options: ['Strong resistance that will hold', 'A target for a liquidity grab upward', 'A trend continuation signal', 'Market indecision only'], correct: 1, explanation: 'Equal highs attract retail stops above them — institutions will hunt that liquidity before reversing.' },
      { q: 'What is inducement?', options: ['A fake breakout used to trap retail', 'A real CHoCH signal', 'A buy signal at support', 'Volume divergence'], correct: 0, explanation: 'Inducement is a deliberate fake move to attract retail entries before sweeping their stops.' }
    ],
    takeaway: 'Liquidity is where retail stops are. Institutions hunt BSL above highs and SSL below lows before reversing. Equal highs/lows are prime targets. Always wait for the sweep before entering.'
  },

  45: {
    title: 'SMC: Order Blocks & Fair Value Gaps',
    subtitle: 'The two most powerful entry tools in Smart Money Concepts',
    sections: [
      { id: 'intro', title: '📦 What is an Order Block?', type: 'text', content: `An Order Block (OB) is the last candle before a strong impulsive move. It represents where institutions placed large orders before driving price aggressively in one direction.\n\nBullish Order Block: The last BEARISH candle before a strong move UP\nBearish Order Block: The last BULLISH candle before a strong move DOWN\n\nWhy price returns to it: Institutions cannot fill all their orders at once. They place some orders and drive price away, then let price pull back to the OB to fill the rest.\n\nThe golden rule: When price returns to a valid OB, institutions are adding to their position — you should too.` },
      { id: 'valid_ob', title: '✅ How to Identify a Valid Order Block', type: 'text', content: `What makes an order block VALID:\n\n1. Strong displacement after it — the move away must be impulsive, big candles, closes near high/low\n2. Break of Structure follows — the move from the OB must create a BOS\n3. Liquidity swept before it — the best OBs form AFTER a liquidity grab\n4. Higher timeframe alignment — an OB on 15m inside a discount zone on 4H is far more powerful\n\nEntry: Enter when price returns to 50%-100% of the OB candle body.\nStop loss: Just below the OB low (bullish) or above OB high (bearish).` },
      { id: 'fvg', title: '⚡ Fair Value Gap (FVG)', type: 'text', content: `A Fair Value Gap (FVG) is a gap in price where only one side of the market traded. Price moved so fast that buy and sell orders did not fully match.\n\nHow to identify: Look at three consecutive candles. If the WICK of candle 1 and the WICK of candle 3 do not overlap — that middle space is the FVG.\n\nBullish FVG: Forms during a strong up move\nBearish FVG: Forms during a strong down move\n\nWhy price fills FVGs: The market returns to fill inefficiencies. Institutions use these as entry points.\n\nBest FVGs: Found inside order blocks, after liquidity sweeps, on higher timeframes.` },
      { id: 'breaker', title: '🔄 Breaker Blocks', type: 'text', content: `A Breaker Block is an order block that FAILED — price broke through it. But it now acts as a level for the OPPOSITE direction.\n\nHow it forms:\n1. A bullish OB forms, price rallies, then price comes back and BREAKS through the OB\n2. That failed bullish OB is now a bearish Breaker\n3. When price rallies back into it — institutions sell from it\n\nBreakers are often stronger than regular OBs because there is double the institutional activity.` },
      { id: 'mitigation', title: '🎯 Mitigation Blocks', type: 'highlight', content: `A Mitigation Block forms when institutions partially fill an order block but need to come back to fully exit.\n\nThink of it: Institution sells 1 million shares at $100. They only got 600K filled. Price drops. They need price to come back to $100 to sell the remaining 400K.\n\nKey difference from OB:\nOB = institutions adding to position\nMitigation = institutions exiting position\n\nBoth create tradeable reactions, but mitigation blocks tend to be one-touch only.` }
    ],
    quiz: [
      { q: 'What is a bullish Order Block?', options: ['The last green candle before a drop', 'The last red candle before a strong rally', 'Any support level', 'A candle with a long wick'], correct: 1, explanation: 'Bullish OB = last bearish candle before strong impulsive move up. Institutions entered buys on that candle.' },
      { q: 'What makes a Fair Value Gap?', options: ['A single big candle', 'Two candles with opposite colors', 'A gap between wick of candle 1 and wick of candle 3', 'Volume spike'], correct: 2, explanation: 'FVG = gap between wick of first candle and wick of third candle — an imbalance price will return to fill.' },
      { q: 'What is a Breaker Block?', options: ['A very strong order block', 'A failed order block that flips direction', 'An OB on a higher timeframe', 'A support turned resistance'], correct: 1, explanation: 'Breaker = failed OB. It flips to act for the opposite direction.' },
      { q: 'Where is the ideal entry on an order block?', options: ['Above the OB high', 'At the OB low', '50%-100% of the OB candle body', 'At the candle open'], correct: 2, explanation: 'Enter at 50%-100% of the OB body — this is where institutional orders are concentrated.' }
    ],
    takeaway: 'Order Blocks and FVGs are your primary entry tools. OBs show where institutions entered. FVGs show market imbalances price will return to fill. Always combine both for high-probability entries.'
  },

  46: {
    title: 'SMC: Institutional Concepts & Kill Zones',
    subtitle: 'Trade like a hedge fund — timing, Power of 3, and optimal entries',
    sections: [
      { id: 'intro', title: '🏦 Thinking Like an Institution', type: 'text', content: `Retail traders think in terms of indicators and patterns. Institutions think in terms of time, price delivery, and liquidity.\n\nOnce you understand this, you stop fighting the market and start trading WITH the people who move it.` },
      { id: 'killzones', title: '⏰ Kill Zones — When Markets Really Move', type: 'text', content: `Kill Zones are specific time windows when institutional order flow is highest. These are the BEST times to trade.\n\nAsian Kill Zone: 8:00 PM - 12:00 AM EST\nLow volume, sets the range for the day. Often creates the high or low that gets swept during London/NY.\n\nLondon Kill Zone: 2:00 AM - 5:00 AM EST\nMost active session globally. Often creates the high or low of the day.\n\nNew York Kill Zone: 8:00 AM - 11:00 AM EST\nHighest volume, most volatile. Best for US stocks and indices.\n\nRule: Only trade during kill zones. Avoid the dead zones between sessions.` },
      { id: 'power3', title: '⚡ Power of 3 — AMD', type: 'text', content: `The Power of 3 describes how institutions structure price movement in three phases:\n\nA — Accumulation: Institutions quietly build positions. Price moves sideways. This is the Asian session range.\n\nM — Manipulation: Price makes a FALSE move to trap retail. In a day that will ultimately go UP, institutions first push price DOWN to grab sell-side liquidity. This is the London session sweep.\n\nD — Distribution: The real move happens. NY session drives the direction.\n\nHow to use it:\n1. Note the Asian range (accumulation)\n2. Watch London for the fake move/sweep (manipulation)\n3. Enter during NY in the OPPOSITE direction of the London sweep (distribution)` },
      { id: 'ote', title: '📐 OTE — Optimal Trade Entry', type: 'text', content: `Optimal Trade Entry (OTE) uses Fibonacci to find the precise entry point within a retracement.\n\nSetup:\n1. Identify a swing low and swing high from an impulsive move\n2. Draw Fibonacci from the swing point\n3. The OTE zone is between 0.618 and 0.705\n\nWhy these levels: Institutions place their orders in this zone. It is deep enough to shake out weak hands but not deep enough to break structure.\n\nEntry: Enter at 0.618-0.705 with stop loss below swing low (for buys).` },
      { id: 'ipda', title: '🤖 IPDA — How Price Delivers', type: 'highlight', content: `IPDA (Institutional Price Delivery Algorithm) describes how price moves between levels.\n\nPrice follows a logical path:\n1. From liquidity to liquidity — price moves from one pool to the next\n2. From imbalance to imbalance — price fills FVGs in sequence\n3. In 20-day, 40-day, 60-day cycles\n\nPractical use: Look 20 days back for key swing highs/lows. These become targets for the next 20-day cycle.` }
    ],
    quiz: [
      { q: 'What is the New York Kill Zone time?', options: ['8 PM - 12 AM EST', '2 AM - 5 AM EST', '8 AM - 11 AM EST', '12 PM - 3 PM EST'], correct: 2, explanation: 'NY Kill Zone = 8 AM - 11 AM EST. Highest volume, most institutional activity for US markets.' },
      { q: 'In Power of 3, what happens during Manipulation?', options: ['Price makes the real move', 'Price makes a false move to trap retail', 'Institutions accumulate quietly', 'Volume spikes upward'], correct: 1, explanation: 'Manipulation = the fake move. Institutions sweep liquidity in the WRONG direction before the real move.' },
      { q: 'What is the OTE Fibonacci zone?', options: ['0.236 - 0.382', '0.5 - 0.618', '0.618 - 0.705', '0.786 - 0.886'], correct: 2, explanation: 'OTE zone = 0.618 to 0.705. This is where institutional orders cluster on retracements.' },
      { q: 'What does IPDA stand for?', options: ['International Price Delivery Algorithm', 'Institutional Price Delivery Algorithm', 'Index Price Distribution Average', 'Intraday Price Direction Analysis'], correct: 1, explanation: 'IPDA = Institutional Price Delivery Algorithm — the logic behind how price moves between levels algorithmically.' }
    ],
    takeaway: 'Trade only during Kill Zones. Use Power of 3 to identify the real direction vs the fake move. Enter with OTE at 0.618-0.705. Think algorithmically — price moves from liquidity to liquidity.'
  },

  47: {
    title: 'SMC: Building a Full Trade Setup',
    subtitle: 'Combine everything into one repeatable, high-probability trading framework',
    sections: [
      { id: 'intro', title: '🎯 The Complete SMC Framework', type: 'text', content: `The 5-Step SMC Trade Setup:\n\n1. HTF Structure — What direction is the higher timeframe telling me?\n2. Liquidity Target — Where is price headed to next?\n3. Wait for Manipulation — Did price sweep liquidity?\n4. Find the Entry Zone — OB, FVG, or OTE in the right area?\n5. Execute during Kill Zone — Is this happening at the right time?\n\nMiss any step and your win rate drops significantly.` },
      { id: 'htf', title: '🔭 HTF to LTF Confluence', type: 'text', content: `The process:\nWeekly/Daily chart: Identify overall trend, premium/discount zones, key swing levels\n4H/1H chart: Find the order blocks and FVGs in the trade direction\n15M/5M chart: Find the precise entry — the manipulation sweep plus OB/FVG reaction\n\nGolden rule: NEVER trade against the daily timeframe trend.\n\nExample:\nDaily: Bullish, price in discount zone\n4H: Price returning to bullish order block\n15M: Price sweeps a low (grabs SSL), then forms bullish FVG\nResult: Enter LONG` },
      { id: 'checklist', title: '✅ The SMC Trade Checklist', type: 'text', content: `Before entering ANY trade:\n\nStructure:\nIs HTF bullish/bearish and you agree with direction?\nHas there been a CHoCH on LTF to confirm?\n\nLiquidity:\nHas price swept a liquidity pool (BSL or SSL)?\nIs this a manipulation move (Power of 3)?\n\nEntry Zone:\nIs there a valid OB in the area?\nIs there an open FVG to target?\nAre you in OTE zone (0.618-0.705)?\n\nTiming:\nAre you in a Kill Zone (London or NY)?\n\nRisk:\nStop loss beyond the OB / swing point?\nRisk:Reward at least 1:2?` },
      { id: 'risk', title: '💰 Risk Management for SMC Traders', type: 'text', content: `The rules:\nMax 1% risk per trade\nMinimum 1:2 R:R — for every $1 you risk, target $2 profit\nMaximum 3 trades per session — quality over quantity\nNo trades outside kill zones\n\nPosition sizing example:\nAccount: $10,000\nRisk per trade: 1% = $100\nStop loss: $0.50 per share\nPosition size: $100 divided by $0.50 = 200 shares\n\nThe math of success:\n50% win rate plus 1:2 R:R = profitable\n5 wins x $200 = $1,000\n5 losses x $100 = -$500\nNet = +$500 on 10 trades` },
      { id: 'example', title: '📈 Full Trade Example — AAPL', type: 'highlight', content: `AAPL — Full SMC Setup:\n\nDaily (HTF): Bullish structure, price in discount zone\n4H: Price returning toward bullish order block at $180\n\nWatch list:\nBSL above $185 equal highs (target for sweep)\nSSL below $178 recent low (where stops are)\n\nThe setup unfolds:\n1. Asian session: Price ranges $179-$181\n2. London (2-4 AM): Price DROPS to $178.50 — sweeps the SSL\n3. Immediate rejection candle — bullish engulfing at 4H OB\n4. NY open (8:30 AM): Price gaps up, forms bullish FVG on 15M\n\nEntry: $179.50 (in the FVG / OTE zone)\nStop: $177.80 (below the sweep low)\nTarget: $185 (the BSL — equal highs)\n\nR:R = $5.50 reward / $1.70 risk = 3.2:1 — textbook SMC setup.` }
    ],
    quiz: [
      { q: 'In HTF to LTF analysis, what does the Daily chart tell you?', options: ['The exact entry point', 'The stop loss level', 'The overall trend direction', 'The position size'], correct: 2, explanation: 'Daily = direction. 4H = zone. 15M = entry. Never trade against the daily trend.' },
      { q: 'What is the minimum Risk:Reward ratio for SMC trades?', options: ['1:1', '1:1.5', '1:2', '1:3'], correct: 2, explanation: 'Minimum 1:2 R:R. Even with a 50% win rate, you are profitable.' },
      { q: 'What is the maximum risk per trade?', options: ['5% of account', '3% of account', '2% of account', '1% of account'], correct: 3, explanation: 'Max 1% risk per trade. Protects your account from a string of losses.' },
      { q: 'Which session is typically the Manipulation phase?', options: ['Asian session', 'London session', 'New York session', 'After-hours'], correct: 1, explanation: 'London session typically creates the fake move before NY makes the real move.' }
    ],
    takeaway: 'The complete SMC setup: HTF direction, liquidity target, wait for manipulation sweep, find OB/FVG entry, execute in Kill Zone with 1:2 R:R. Patience and discipline beat every indicator.'
  }
};

// ============================================
// INTERACTIVE DEMO COMPONENTS
// ============================================

const SupplyDemandDemo = () => {
  const [buyers, setBuyers] = useState(50);
  const [sellers, setSellers] = useState(50);
  const price = 100 + (buyers - sellers) * 0.8;

  return (
    <div style={{ background: '#0f172a', borderRadius: '0.75rem', padding: '1.5rem' }}>
      <p style={{ color: '#94a3b8', marginBottom: '1rem', fontSize: '0.875rem' }}>Drag sliders to see supply & demand:</p>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', marginBottom: '1.5rem' }}>
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
            <span style={{ color: '#34d399' }}>🟢 Buyers</span><span style={{ color: '#34d399', fontWeight: 'bold' }}>{buyers}</span>
          </div>
          <input type="range" min="10" max="90" value={buyers} onChange={(e) => setBuyers(Number(e.target.value))} style={{ width: '100%' }} />
        </div>
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
            <span style={{ color: '#f87171' }}>🔴 Sellers</span><span style={{ color: '#f87171', fontWeight: 'bold' }}>{sellers}</span>
          </div>
          <input type="range" min="10" max="90" value={sellers} onChange={(e) => setSellers(Number(e.target.value))} style={{ width: '100%' }} />
        </div>
      </div>
      <div style={{ textAlign: 'center', padding: '1.5rem', background: '#1e293b', borderRadius: '0.75rem' }}>
        <div style={{ color: '#64748b', fontSize: '0.875rem' }}>Stock Price</div>
        <div style={{ fontSize: '3rem', fontWeight: 'bold', color: price >= 100 ? '#34d399' : '#f87171' }}>${price.toFixed(2)}</div>
        <div style={{ marginTop: '0.5rem', color: '#94a3b8', fontSize: '0.875rem' }}>
          {buyers > sellers + 10 ? '🔥 High demand pushing price UP!' : sellers > buyers + 10 ? '📉 Selling pressure pushing price DOWN!' : '⚖️ Balanced market'}
        </div>
      </div>
    </div>
  );
};

const GenericDemo = ({ title, data }) => (
  <div style={{ background: '#0f172a', borderRadius: '0.75rem', padding: '1.5rem' }}>
    <h4 style={{ color: '#94a3b8', marginBottom: '1rem', fontSize: '0.875rem' }}>{title || 'Interactive Demo'}</h4>
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1rem' }}>
      {(data || [{ label: 'Metric 1', value: '25%', color: '#34d399' }, { label: 'Metric 2', value: '42', color: '#60a5fa' }, { label: 'Metric 3', value: '$1,250', color: '#a78bfa' }, { label: 'Metric 4', value: '3.5x', color: '#facc15' }]).map((item, i) => (
        <div key={i} style={{ background: '#1e293b', borderRadius: '0.5rem', padding: '1rem', textAlign: 'center' }}>
          <div style={{ color: '#64748b', fontSize: '0.75rem', marginBottom: '0.25rem' }}>{item.label}</div>
          <div style={{ color: item.color || '#fff', fontSize: '1.5rem', fontWeight: 'bold' }}>{item.value}</div>
        </div>
      ))}
    </div>
  </div>
);

const demoConfigs = {
  'supply-demand': { component: 'supply-demand' },
  'indices-comparison': { title: 'Live Index Comparison', data: [{ label: 'S&P 500', value: '4,892', color: '#34d399' }, { label: 'NASDAQ', value: '15,628', color: '#a78bfa' }, { label: 'Dow', value: '38,654', color: '#60a5fa' }, { label: 'Russell', value: '2,044', color: '#facc15' }] },
  'economic-indicators': { title: 'Economic Dashboard', data: [{ label: 'GDP Growth', value: '2.1%', color: '#34d399' }, { label: 'Unemployment', value: '3.7%', color: '#60a5fa' }, { label: 'Inflation', value: '3.2%', color: '#facc15' }, { label: 'Fed Rate', value: '5.25%', color: '#a78bfa' }] },
  'recession-dashboard': { title: 'Recession Indicators', data: [{ label: 'Yield Curve', value: 'Normal', color: '#34d399' }, { label: 'Jobless Claims', value: '210K', color: '#34d399' }, { label: 'PMI', value: '52.3', color: '#60a5fa' }, { label: 'Recession Prob', value: '15%', color: '#facc15' }] },
  'news-impact': { title: 'News Impact Analysis', data: [{ label: 'Earnings Beat', value: '+5.2%', color: '#34d399' }, { label: 'FDA Approval', value: '+12%', color: '#34d399' }, { label: 'CEO Departure', value: '-3.8%', color: '#f87171' }, { label: 'Lawsuit Filed', value: '-7.1%', color: '#f87171' }] },
  'fear-greed': { title: 'Fear & Greed Index', data: [{ label: 'Current', value: '62', color: '#facc15' }, { label: 'Last Week', value: '55', color: '#94a3b8' }, { label: 'VIX', value: '14.5', color: '#34d399' }, { label: 'Status', value: 'Greed', color: '#facc15' }] },
  'stock-screener': { title: 'Stock Screener', data: [{ label: 'Matches', value: '47', color: '#60a5fa' }, { label: 'Avg P/E', value: '18.5', color: '#a78bfa' }, { label: 'Avg Yield', value: '2.4%', color: '#34d399' }, { label: 'Avg Growth', value: '12%', color: '#facc15' }] },
  'stock-quote': { title: 'AAPL Stock Quote', data: [{ label: 'Price', value: '$178.50', color: '#fff' }, { label: 'Change', value: '+1.2%', color: '#34d399' }, { label: 'Volume', value: '52M', color: '#60a5fa' }, { label: 'P/E Ratio', value: '28.5', color: '#a78bfa' }] },
  'candlestick-chart': { title: 'Chart Analysis', data: [{ label: 'Trend', value: 'Uptrend', color: '#34d399' }, { label: 'Support', value: '$170', color: '#60a5fa' }, { label: 'Resistance', value: '$185', color: '#f87171' }, { label: 'RSI', value: '58', color: '#facc15' }] },
  'company-profile': { title: 'Company Analysis', data: [{ label: 'Moat', value: 'Wide', color: '#34d399' }, { label: 'Growth', value: 'Strong', color: '#34d399' }, { label: 'Mgmt', value: 'A+', color: '#60a5fa' }, { label: 'Valuation', value: 'Fair', color: '#facc15' }] },
  'financial-statements': { title: 'Financial Statements', data: [{ label: 'Revenue', value: '$95B', color: '#60a5fa' }, { label: 'Gross Margin', value: '43%', color: '#34d399' }, { label: 'Net Income', value: '$24B', color: '#34d399' }, { label: 'Free Cash Flow', value: '$22B', color: '#a78bfa' }] },
  'peer-comparison': { title: 'Peer Comparison', data: [{ label: 'AAPL P/E', value: '28.5', color: '#60a5fa' }, { label: 'MSFT P/E', value: '35.2', color: '#a78bfa' }, { label: 'GOOGL P/E', value: '25.8', color: '#34d399' }, { label: 'Sector Avg', value: '30.1', color: '#facc15' }] },
  'option-payoff': { title: 'Call Option Payoff', data: [{ label: 'Strike', value: '$100', color: '#94a3b8' }, { label: 'Premium', value: '$5.00', color: '#f87171' }, { label: 'Breakeven', value: '$105', color: '#facc15' }, { label: 'Max Loss', value: '$500', color: '#f87171' }] },
  'greeks-visual': { title: 'Option Greeks', data: [{ label: 'Delta', value: '0.55', color: '#60a5fa' }, { label: 'Gamma', value: '0.08', color: '#a78bfa' }, { label: 'Theta', value: '-0.05', color: '#f87171' }, { label: 'Vega', value: '0.12', color: '#34d399' }] },
  'gex-visual': { title: 'GEX Levels (SPY)', data: [{ label: 'Flip Point', value: '$485', color: '#facc15' }, { label: 'Max Gamma', value: '$490', color: '#34d399' }, { label: 'Current', value: '$492', color: '#60a5fa' }, { label: 'Regime', value: 'Positive', color: '#34d399' }] },
  'put-call-ratio': { title: 'Put/Call Analysis', data: [{ label: 'Equity P/C', value: '0.75', color: '#60a5fa' }, { label: 'Index P/C', value: '1.15', color: '#a78bfa' }, { label: 'VIX', value: '14.5', color: '#34d399' }, { label: 'Signal', value: 'Neutral', color: '#facc15' }] },
  'iv-chart': { title: 'Implied Volatility', data: [{ label: 'Current IV', value: '32%', color: '#60a5fa' }, { label: 'IV Rank', value: '45%', color: '#facc15' }, { label: 'Historical', value: '28%', color: '#94a3b8' }, { label: 'Premium', value: 'Fair', color: '#34d399' }] },
  'portfolio-builder': { title: 'Portfolio Allocation', data: [{ label: 'Stocks', value: '60%', color: '#60a5fa' }, { label: 'Bonds', value: '30%', color: '#34d399' }, { label: 'Cash', value: '10%', color: '#94a3b8' }, { label: 'Risk Level', value: 'Medium', color: '#facc15' }] },
  'transaction-log': { title: 'Transactions', data: [{ label: 'Buys', value: '5', color: '#34d399' }, { label: 'Sells', value: '2', color: '#f87171' }, { label: 'Total', value: '$12,450', color: '#60a5fa' }, { label: 'Fees', value: '$0', color: '#94a3b8' }] },
  'holdings-view': { title: 'Holdings', data: [{ label: 'Positions', value: '12', color: '#60a5fa' }, { label: 'Total Value', value: '$45,230', color: '#34d399' }, { label: 'Day Change', value: '+1.2%', color: '#34d399' }, { label: 'Largest', value: 'AAPL 15%', color: '#a78bfa' }] },
  'performance-chart': { title: 'Performance', data: [{ label: 'YTD Return', value: '+12.5%', color: '#34d399' }, { label: 'vs S&P 500', value: '+2.1%', color: '#34d399' }, { label: 'Sharpe', value: '1.45', color: '#60a5fa' }, { label: 'Alpha', value: '+1.8%', color: '#a78bfa' }] },
  'risk-metrics': { title: 'Risk Analysis', data: [{ label: 'Volatility', value: '18%', color: '#facc15' }, { label: 'Beta', value: '1.1', color: '#60a5fa' }, { label: 'Max DD', value: '-12%', color: '#f87171' }, { label: 'Sharpe', value: '1.3', color: '#34d399' }] },
  'rebalance-tool': { title: 'Rebalancing', data: [{ label: 'Stocks', value: '68% → 60%', color: '#f87171' }, { label: 'Bonds', value: '25% → 30%', color: '#34d399' }, { label: 'Cash', value: '7% → 10%', color: '#34d399' }, { label: 'Action', value: 'Rebalance', color: '#facc15' }] },
  'watchlist-builder': { title: 'Watchlist', data: [{ label: 'Stocks', value: '18', color: '#60a5fa' }, { label: 'Buy Zone', value: '5', color: '#34d399' }, { label: 'Research', value: '8', color: '#facc15' }, { label: 'Alerts', value: '12', color: '#a78bfa' }] },
  'price-alert': { title: 'Alerts', data: [{ label: 'Active', value: '8', color: '#60a5fa' }, { label: 'Price Below', value: '5', color: '#34d399' }, { label: 'Price Above', value: '3', color: '#f87171' }, { label: 'Triggered', value: '0', color: '#94a3b8' }] },
  'congress-trades': { title: 'Congress Activity', data: [{ label: 'Trades (30d)', value: '127', color: '#60a5fa' }, { label: 'Buys', value: '89', color: '#34d399' }, { label: 'Sells', value: '38', color: '#f87171' }, { label: 'Top Stock', value: 'NVDA', color: '#a78bfa' }] },
  'insider-trades': { title: 'Insider Activity', data: [{ label: 'Buys (7d)', value: '234', color: '#34d399' }, { label: 'Sells (7d)', value: '156', color: '#f87171' }, { label: 'Clusters', value: '12', color: '#facc15' }, { label: 'Notable', value: 'CEO Buy', color: '#a78bfa' }] },
  'whale-holdings': { title: '13F Holdings', data: [{ label: 'New Positions', value: '45', color: '#34d399' }, { label: 'Increased', value: '128', color: '#60a5fa' }, { label: 'Decreased', value: '67', color: '#f87171' }, { label: 'Closed', value: '23', color: '#94a3b8' }] },
  'anomaly-scanner': { title: 'Anomalies', data: [{ label: 'Vol Spikes', value: '12', color: '#facc15' }, { label: 'Unusual Options', value: '8', color: '#a78bfa' }, { label: 'Dark Pool', value: '5', color: '#60a5fa' }, { label: 'Price Gaps', value: '3', color: '#f87171' }] },
  'social-sentiment': { title: 'Social Sentiment', data: [{ label: 'Mentions', value: '2.4K', color: '#60a5fa' }, { label: 'Sentiment', value: '72% Bull', color: '#34d399' }, { label: 'Trending', value: '#3', color: '#facc15' }, { label: 'Change', value: '+45%', color: '#34d399' }] },
  'etf-holdings': { title: 'SPY Holdings', data: [{ label: 'AAPL', value: '7.2%', color: '#60a5fa' }, { label: 'MSFT', value: '6.8%', color: '#a78bfa' }, { label: 'AMZN', value: '3.4%', color: '#facc15' }, { label: 'Other 497', value: '82.6%', color: '#94a3b8' }] },
  'etf-xray': { title: 'ETF X-Ray', data: [{ label: 'Holdings', value: '500', color: '#60a5fa' }, { label: 'Top 10', value: '31%', color: '#facc15' }, { label: 'Expense', value: '0.03%', color: '#34d399' }, { label: 'Overlap', value: '78%', color: '#a78bfa' }] },
  'etf-comparison': { title: 'ETF Comparison', data: [{ label: 'SPY Fee', value: '0.09%', color: '#facc15' }, { label: 'VOO Fee', value: '0.03%', color: '#34d399' }, { label: 'SPY Vol', value: '80M', color: '#60a5fa' }, { label: 'Winner', value: 'VOO', color: '#34d399' }] },
  'india-market': { title: 'India Markets', data: [{ label: 'NIFTY 50', value: '22,147', color: '#34d399' }, { label: 'SENSEX', value: '72,568', color: '#60a5fa' }, { label: 'INR/USD', value: '₹83.2', color: '#facc15' }, { label: 'Change', value: '+0.8%', color: '#34d399' }] },
  'forex-rates': { title: 'Currency Rates', data: [{ label: 'EUR/USD', value: '1.085', color: '#60a5fa' }, { label: 'GBP/USD', value: '1.265', color: '#a78bfa' }, { label: 'USD/JPY', value: '150.2', color: '#facc15' }, { label: 'DXY', value: '104.5', color: '#34d399' }] },
  'crypto-prices': { title: 'Crypto Prices', data: [{ label: 'BTC', value: '$67,450', color: '#f7931a' }, { label: 'ETH', value: '$3,520', color: '#627eea' }, { label: '24h Change', value: '+2.3%', color: '#34d399' }, { label: 'Dominance', value: '52%', color: '#facc15' }] },
  'paper-trade': { title: 'Paper Portfolio', data: [{ label: 'Balance', value: '$100,000', color: '#60a5fa' }, { label: 'Positions', value: '8', color: '#a78bfa' }, { label: 'Day P&L', value: '+$1,250', color: '#34d399' }, { label: 'Win Rate', value: '62%', color: '#facc15' }] },
  'earnings-calendar': { title: 'Earnings This Week', data: [{ label: 'Reporting', value: '45', color: '#60a5fa' }, { label: 'Your Holdings', value: '3', color: '#facc15' }, { label: 'S&P 500', value: '12', color: '#a78bfa' }, { label: 'Next', value: 'AAPL Tue', color: '#34d399' }] },
  'earnings-history': { title: 'AAPL Earnings', data: [{ label: 'Last 8', value: '8 Beats', color: '#34d399' }, { label: 'Avg Beat', value: '+4.2%', color: '#34d399' }, { label: 'Rev Growth', value: '+8%', color: '#60a5fa' }, { label: 'Reliability', value: 'High', color: '#a78bfa' }] },
  'post-earnings': { title: 'Post-Earnings', data: [{ label: 'Expected', value: '±5.2%', color: '#facc15' }, { label: 'Actual', value: '+7.8%', color: '#34d399' }, { label: 'Drift', value: 'Likely Up', color: '#34d399' }, { label: 'IV Crush', value: '-35%', color: '#f87171' }] }
};

const DemoComponent = ({ type }) => {
  if (type === 'supply-demand') return <SupplyDemandDemo />;
  const config = demoConfigs[type] || demoConfigs['economic-indicators'];
  return <GenericDemo title={config.title} data={config.data} />;
};

// ============================================
// QUIZ COMPONENT
// ============================================

const QuizComponent = ({ quiz, onComplete, tutor }) => {
  const [current, setCurrent] = useState(0);
  const [selected, setSelected] = useState(null);
  const [showAnswer, setShowAnswer] = useState(false);
  const [score, setScore] = useState(0);
  const [finished, setFinished] = useState(false);

  const handleCheck = () => { if (selected !== null) { setShowAnswer(true); if (selected === quiz[current].correct) setScore(s => s + 1); } };
  const handleNext = () => {
    if (current < quiz.length - 1) { setCurrent(c => c + 1); setSelected(null); setShowAnswer(false); }
    else { setFinished(true); onComplete(score + (selected === quiz[current].correct ? 1 : 0), quiz.length); }
  };

  if (finished) {
    const finalScore = score + (selected === quiz[quiz.length - 1]?.correct ? 1 : 0);
    const pct = Math.round((finalScore / quiz.length) * 100);
    return (
      <div style={{ textAlign: 'center', padding: '2rem' }}>
        <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>{pct >= 66 ? '🎉' : '📚'}</div>
        <div style={{ fontSize: '2rem', fontWeight: 'bold', color: pct >= 66 ? '#34d399' : '#facc15' }}>{finalScore} / {quiz.length}</div>
        <div style={{ color: '#94a3b8', marginBottom: '1rem' }}>{pct >= 66 ? 'Great job!' : 'Review and try again!'}</div>
        {pct < 66 && tutor && (
          <div style={{ background: 'rgba(99,102,241,0.1)', border: '1px solid rgba(99,102,241,0.3)', borderRadius: '0.5rem', padding: '1rem', marginTop: '1rem' }}>
            <p style={{ color: '#a5b4fc', fontSize: '0.9rem' }}>💡 Tip: Open the AI tutor chat and ask {tutor.name} for help!</p>
          </div>
        )}
      </div>
    );
  }

  const q = quiz[current];
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '1rem' }}>
        <span style={{ color: '#94a3b8' }}>Q{current + 1}/{quiz.length}</span>
        <span style={{ color: '#818cf8' }}>{score} correct</span>
      </div>
      <h4 style={{ fontSize: '1.125rem', marginBottom: '1.5rem', lineHeight: 1.4 }}>{q.q}</h4>
      {q.options.map((opt, i) => {
        let bg = '#1e293b', border = '#334155', color = '#fff';
        if (showAnswer) {
          if (i === q.correct) { bg = 'rgba(34,197,94,0.2)'; border = '#34d399'; color = '#34d399'; }
          else if (i === selected) { bg = 'rgba(239,68,68,0.2)'; border = '#f87171'; color = '#f87171'; }
        } else if (i === selected) { bg = 'rgba(99,102,241,0.2)'; border = '#6366f1'; }
        return (
          <button key={i} onClick={() => !showAnswer && setSelected(i)} style={{ display: 'block', width: '100%', textAlign: 'left', padding: '1rem', marginBottom: '0.5rem', background: bg, border: `1px solid ${border}`, borderRadius: '0.5rem', color, cursor: showAnswer ? 'default' : 'pointer' }}>{opt}</button>
        );
      })}
      {showAnswer && <div style={{ background: 'rgba(99,102,241,0.1)', border: '1px solid rgba(99,102,241,0.3)', borderRadius: '0.5rem', padding: '1rem', margin: '1rem 0' }}>
        <div style={{ fontWeight: '600', color: selected === q.correct ? '#34d399' : '#f87171', marginBottom: '0.5rem' }}>{selected === q.correct ? '✓ Correct!' : '✗ Incorrect'}</div>
        <div style={{ color: '#94a3b8', fontSize: '0.875rem' }}>{q.explanation}</div>
      </div>}
      <button onClick={showAnswer ? handleNext : handleCheck} disabled={selected === null} style={{ width: '100%', padding: '1rem', background: selected !== null ? '#6366f1' : '#334155', border: 'none', borderRadius: '0.5rem', color: '#fff', fontWeight: '600', cursor: selected !== null ? 'pointer' : 'not-allowed' }}>
        {showAnswer ? (current < quiz.length - 1 ? 'Next →' : 'See Results') : 'Check Answer'}
      </button>
    </div>
  );
};

// ===================== MODULE PAGE COMPONENT =====================
const ModulePage = ({ module, onBack, onComplete, completed, currentTutor, onOpenChat }) => {
  const [activeTab, setActiveTab] = useState('learn');
  const [expandedSection, setExpandedSection] = useState(0);
  const content = MODULE_CONTENT[module.id] || { sections: [{ type: 'intro', title: 'Coming Soon', content: 'This module content is being developed.' }], quiz: [], takeaway: 'Check back soon!' };
  const tutor = currentTutor || getTutorForCategory(module.category);
  const category = CATEGORIES.find(c => c.id === module.category);

  const handleQuizComplete = (score, total) => {
    if (score >= total * 0.7 && !completed) onComplete(module.id);
  };

  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(180deg, #0f172a 0%, #1e1b4b 50%, #0f172a 100%)', color: '#e2e8f0' }}>
      {/* Premium Header */}
      <div style={{ 
        background: 'linear-gradient(135deg, rgba(99,102,241,0.1) 0%, rgba(168,85,247,0.05) 100%)', 
        borderBottom: '1px solid rgba(255,255,255,0.08)', 
        padding: '1.5rem 1.5rem 2rem',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Background decoration */}
        <div style={{ position: 'absolute', top: '-100px', right: '-100px', width: '300px', height: '300px', borderRadius: '50%', background: `radial-gradient(circle, ${category?.color || '#6366f1'}20 0%, transparent 70%)`, filter: 'blur(60px)' }} />
        
        <div style={{ position: 'relative', zIndex: 1, maxWidth: '1000px', margin: '0 auto' }}>
          {/* Back button */}
          <button 
            onClick={onBack} 
            className="btn-hover"
            style={{ 
              background: 'rgba(255,255,255,0.08)', 
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255,255,255,0.1)', 
              borderRadius: '0.75rem', 
              padding: '0.625rem 1.25rem', 
              color: '#94a3b8', 
              cursor: 'pointer', 
              marginBottom: '1.5rem',
              fontSize: '0.9rem',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.5rem',
              transition: 'all 0.2s ease'
            }}
          >
            ← Back to Academy
          </button>
          
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1.5rem', flexWrap: 'wrap' }}>
            {/* Module Icon */}
            <div style={{ 
              width: '80px', 
              height: '80px', 
              borderRadius: '1.25rem',
              background: `linear-gradient(135deg, ${category?.color || '#6366f1'}40, ${category?.color || '#6366f1'}20)`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '2.5rem',
              border: `1px solid ${category?.color || '#6366f1'}30`,
              boxShadow: `0 8px 32px ${category?.color || '#6366f1'}20`,
              flexShrink: 0
            }}>
              {module.icon}
            </div>
            
            <div style={{ flex: 1, minWidth: '280px' }}>
              {/* Category badge */}
              <div style={{ 
                display: 'inline-flex', 
                alignItems: 'center', 
                gap: '0.375rem',
                background: `${category?.color || '#6366f1'}20`,
                border: `1px solid ${category?.color || '#6366f1'}30`,
                padding: '0.375rem 0.875rem',
                borderRadius: '2rem',
                fontSize: '0.75rem',
                color: category?.color || '#a5b4fc',
                marginBottom: '0.75rem'
              }}>
                {category?.icon} {category?.name}
              </div>
              
              <h1 style={{ 
                fontSize: '1.75rem', 
                fontWeight: '700', 
                margin: '0 0 0.5rem',
                letterSpacing: '-0.02em',
                lineHeight: '1.2'
              }}>{module.title}</h1>
              
              <p style={{ 
                color: '#94a3b8', 
                margin: '0 0 1rem',
                fontSize: '1rem',
                lineHeight: '1.5'
              }}>{module.description}</p>
              
              {/* Meta badges */}
              <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
                <span style={{ 
                  background: 'rgba(99,102,241,0.15)', 
                  padding: '0.5rem 1rem', 
                  borderRadius: '0.5rem', 
                  fontSize: '0.85rem',
                  color: '#a5b4fc',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.375rem'
                }}>
                  ⏱ {module.duration}
                </span>
                <span style={{ 
                  background: module.difficulty === 'Beginner' ? 'rgba(34,197,94,0.15)' : module.difficulty === 'Intermediate' ? 'rgba(251,191,36,0.15)' : 'rgba(239,68,68,0.15)', 
                  padding: '0.5rem 1rem', 
                  borderRadius: '0.5rem', 
                  fontSize: '0.85rem',
                  color: module.difficulty === 'Beginner' ? '#4ade80' : module.difficulty === 'Intermediate' ? '#fbbf24' : '#f87171'
                }}>
                  {module.difficulty === 'Beginner' ? '🌱' : module.difficulty === 'Intermediate' ? '📈' : '🚀'} {module.difficulty}
                </span>
                {completed && (
                  <span style={{ 
                    background: 'linear-gradient(135deg, rgba(34,197,94,0.2), rgba(34,197,94,0.1))', 
                    padding: '0.5rem 1rem', 
                    borderRadius: '0.5rem', 
                    fontSize: '0.85rem', 
                    color: '#4ade80',
                    border: '1px solid rgba(34,197,94,0.3)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.375rem'
                  }}>
                    ✓ Completed
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* AI Tutor Banner - Redesigned */}
      <div style={{ 
        background: `linear-gradient(90deg, ${tutor.color}15 0%, transparent 50%, ${tutor.color}10 100%)`, 
        padding: '1rem 1.5rem', 
        borderBottom: '1px solid rgba(255,255,255,0.05)', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        gap: '2rem',
        flexWrap: 'wrap'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <div style={{ 
            width: '48px', 
            height: '48px', 
            borderRadius: '50%',
            background: `linear-gradient(135deg, ${tutor.color}50, ${tutor.color}30)`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '1.5rem',
            border: `2px solid ${tutor.color}40`,
            position: 'relative'
          }}>
            {tutor.avatar}
            {/* Online dot */}
            <div style={{ position: 'absolute', bottom: '2px', right: '2px', width: '10px', height: '10px', borderRadius: '50%', background: '#22c55e', border: '2px solid #0f172a' }} />
          </div>
          <div>
            <div style={{ fontWeight: '600', fontSize: '0.95rem' }}>{tutor.name}</div>
            <div style={{ fontSize: '0.8rem', color: '#94a3b8' }}>Your AI tutor • Ready to help</div>
          </div>
        </div>
        <button 
          onClick={() => onOpenChat(tutor)} 
          className="btn-hover"
          style={{ 
            background: `linear-gradient(135deg, ${tutor.color}, ${tutor.color}cc)`, 
            border: 'none', 
            borderRadius: '0.625rem', 
            padding: '0.75rem 1.5rem', 
            color: '#fff', 
            fontWeight: '600', 
            cursor: 'pointer', 
            fontSize: '0.9rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            boxShadow: `0 4px 15px ${tutor.color}40`
          }}
        >
          💬 Ask {tutor.name.split(' ')[0]}
        </button>
      </div>

      {/* Tabs - Redesigned */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center',
        padding: '0.75rem 1.5rem',
        background: 'rgba(15,23,42,0.5)',
        borderBottom: '1px solid rgba(255,255,255,0.05)'
      }}>
        <div style={{ 
          display: 'inline-flex', 
          background: 'rgba(30,41,59,0.8)', 
          borderRadius: '0.75rem', 
          padding: '0.375rem',
          gap: '0.25rem'
        }}>
          {[
            { id: 'learn', icon: '📚', label: 'Learn' },
            { id: 'quiz', icon: '❓', label: 'Quiz' },
            { id: 'practice', icon: '🎯', label: 'Practice' }
          ].map(tab => (
            <button 
              key={tab.id} 
              onClick={() => setActiveTab(tab.id)} 
              style={{ 
                background: activeTab === tab.id ? 'linear-gradient(135deg, #6366f1, #8b5cf6)' : 'transparent', 
                border: 'none', 
                borderRadius: '0.5rem',
                padding: '0.75rem 1.5rem', 
                color: activeTab === tab.id ? '#fff' : '#94a3b8', 
                fontWeight: '600', 
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                fontSize: '0.9rem'
              }}
            >
              {tab.icon} {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Content with Sidebar */}
      <div style={{ padding: '2rem 1.5rem', maxWidth: '1200px', margin: '0 auto', display: 'flex', gap: '2rem', flexWrap: 'wrap' }}>
        {/* Main Content */}
        <div style={{ flex: '1', minWidth: '300px', maxWidth: '900px' }}>
        {activeTab === 'learn' && (
          <div>
            {content.sections.map((section, idx) => (
              <div key={idx} style={{ marginBottom: '1rem', background: 'rgba(30,41,59,0.5)', borderRadius: '0.75rem', overflow: 'hidden', border: '1px solid rgba(255,255,255,0.05)' }}>
                <button onClick={() => setExpandedSection(expandedSection === idx ? -1 : idx)} style={{ width: '100%', background: 'transparent', border: 'none', padding: '1rem 1.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', color: '#e2e8f0', cursor: 'pointer', textAlign: 'left' }}>
                  <span style={{ fontWeight: '600' }}>{section.type === 'intro' ? '📖' : section.type === 'analogy' ? '💡' : section.type === 'demo' ? '🎮' : section.type === 'keypoint' ? '🎯' : section.type === 'warning' ? '⚠️' : '📝'} {section.title}</span>
                  <span style={{ transform: expandedSection === idx ? 'rotate(180deg)' : 'rotate(0)', transition: 'transform 0.2s' }}>▼</span>
                </button>
                {expandedSection === idx && (
                  <div style={{ padding: '0 1.5rem 1.5rem' }}>
                    <p style={{ color: '#94a3b8', lineHeight: '1.7', margin: 0 }}>{section.content}</p>
                    {section.type === 'demo' && <div style={{ marginTop: '1rem' }}><DemoComponent type={section.demoType || module.id} /></div>}
                  </div>
                )}
              </div>
            ))}
            {content.takeaway && (
              <div style={{ background: 'linear-gradient(135deg, rgba(99,102,241,0.2) 0%, rgba(139,92,246,0.2) 100%)', borderRadius: '0.75rem', padding: '1.5rem', marginTop: '1.5rem', border: '1px solid rgba(99,102,241,0.3)' }}>
                <h3 style={{ margin: '0 0 0.75rem', fontSize: '1rem' }}>🎓 Key Takeaway</h3>
                <p style={{ margin: 0, color: '#cbd5e1', lineHeight: '1.6' }}>{content.takeaway}</p>
              </div>
            )}
            
            {/* Inline Feature Teaser - After content */}
            <div style={{ marginTop: '2rem' }}>
              <InlineFeatureTeaser moduleId={module.id} onTryFeature={() => {}} />
            </div>
          </div>
        )}
        {activeTab === 'quiz' && <QuizComponent quiz={content.quiz} onComplete={handleQuizComplete} tutor={tutor} />}
        {activeTab === 'practice' && <PracticeScenario category={module.category} tutor={tutor} />}
        </div>
        
        {/* Upgrade Sidebar - Full width on mobile, sidebar on desktop */}
        <div style={{ 
          width: '100%',
          maxWidth: '400px',
          margin: '0 auto',
          display: 'flex', 
          flexDirection: 'column', 
          gap: '1rem'
        }}>
          <div style={{
            background: 'linear-gradient(135deg, rgba(99,102,241,0.15) 0%, rgba(168,85,247,0.1) 100%)',
            borderRadius: '1rem',
            padding: '1.25rem',
            border: '1px solid rgba(99,102,241,0.3)',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>🚀</div>
            <div style={{ fontWeight: '700', color: '#fff', marginBottom: '0.25rem' }}>Ready to Apply This?</div>
            <div style={{ fontSize: '0.85rem', color: '#94a3b8', marginBottom: '1rem' }}>Use real tools with live data</div>
            <button 
              onClick={() => window.location.href = '/settings?tab=subscription'}
              style={{
                width: '100%',
                padding: '0.75rem',
                background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                border: 'none',
                borderRadius: '0.5rem',
                color: 'white',
                fontWeight: '600',
                cursor: 'pointer',
                fontSize: '0.9rem'
              }}
            >
              Start Free Trial
            </button>
          </div>
          
          {/* Related Premium Feature */}
          <FeatureDataPreview 
            featureType={module.category === 'options' ? 'optionsChain' : module.category === 'altdata' ? 'congressTrades' : 'marketDashboard'} 
            onUpgrade={() => window.location.href = '/settings?tab=subscription'}
          />
        </div>
      </div>
    </div>
  );
};

// ===================== MAIN LEARN ACADEMY COMPONENT =====================
const LearnAcademy = () => {
  const [view, setView] = useState('home');
  const [selectedModule, setSelectedModule] = useState(null);
  const [selectedPath, setSelectedPath] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [completed, setCompleted] = useState(() => JSON.parse(localStorage.getItem('iq_learn_completed') || '[]'));
  const [search, setSearch] = useState('');
  const [showChat, setShowChat] = useState(false);
  const [currentTutor, setCurrentTutor] = useState(AI_TUTORS.main);
  
  // Conversion system state
  const [showUpgradePrompt, setShowUpgradePrompt] = useState(false);
  const [showMilestone, setShowMilestone] = useState(null);
  const [showPricing, setShowPricing] = useState(false);
  const [lastCompletedModule, setLastCompletedModule] = useState(null);
  const [claimedMilestones, setClaimedMilestones] = useState(() => JSON.parse(localStorage.getItem('iq_learn_milestones') || '[]'));

  useEffect(() => { localStorage.setItem('iq_learn_completed', JSON.stringify(completed)); }, [completed]);
  useEffect(() => { localStorage.setItem('iq_learn_milestones', JSON.stringify(claimedMilestones)); }, [claimedMilestones]);
  
  // Scroll to top when navigating to module, category, or path views
  useEffect(() => {
    if (selectedModule || selectedCategory || selectedPath) {
      window.scrollTo(0, 0);
    }
  }, [selectedModule, selectedCategory, selectedPath]);
  
  // Inject CSS animations
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes slideUp { from { transform: translateY(100px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
      @keyframes bounce { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-10px); } }
      @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.7; } }
      @keyframes float { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-5px); } }
      @keyframes glow { 0%, 100% { box-shadow: 0 0 20px rgba(99,102,241,0.3); } 50% { box-shadow: 0 0 40px rgba(99,102,241,0.6); } }
      @keyframes shimmer { 0% { background-position: -200% 0; } 100% { background-position: 200% 0; } }
      @keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
      @keyframes scaleIn { from { transform: scale(0.95); opacity: 0; } to { transform: scale(1); opacity: 1; } }
      .card-hover { transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1); }
      .card-hover:hover { transform: translateY(-4px); box-shadow: 0 20px 40px rgba(0,0,0,0.3); }
      .btn-hover { transition: all 0.2s ease; }
      .btn-hover:hover { transform: translateY(-2px); box-shadow: 0 10px 20px rgba(99,102,241,0.3); }
      .glass { backdrop-filter: blur(12px); -webkit-backdrop-filter: blur(12px); }
    `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  // Enhanced completion handler with milestone check
  const handleComplete = (moduleId) => { 
    if (!completed.includes(moduleId)) {
      const newCompleted = [...completed, moduleId];
      setCompleted(newCompleted);
      setLastCompletedModule(moduleId);
      
      // Check for milestone
      const milestone = MILESTONES.find(m => m.modules === newCompleted.length && !claimedMilestones.includes(m.modules));
      if (milestone) {
        setTimeout(() => setShowMilestone(milestone), 500);
      } else {
        // Show upgrade prompt after a short delay
        setTimeout(() => setShowUpgradePrompt(true), 1000);
      }
    }
  };
  
  const openChat = (tutor) => { setCurrentTutor(tutor || AI_TUTORS.main); setShowChat(true); };
  const filteredModules = search ? MODULES.filter(m => m.title.toLowerCase().includes(search.toLowerCase()) || m.description.toLowerCase().includes(search.toLowerCase())) : MODULES;

  // Navigate directly to settings subscription page
  const goToSubscription = () => {
    window.location.href = '/settings?tab=subscription';
  };

  // Handle trying a feature
  const handleTryFeature = (feature) => {
    // In real app, this would navigate to the feature or show a preview
    // For now, go to subscription if it's a pro feature
    if (feature.tier === 'pro') {
      goToSubscription();
    } else {
      // Navigate to the feature (you'd integrate with your router)
      window.location.href = feature.route;
    }
    setShowUpgradePrompt(false);
  };

  // Handle upgrade actions
  const handleUpgrade = (action) => {
    if (action === 'pricing' || action === 'trial') {
      goToSubscription();
    }
    setShowUpgradePrompt(false);
  };

  // Handle selecting a tier (keeping for compatibility but shouldn't be reached)
  const handleSelectTier = (tier) => {
    setShowPricing(false);
    if (tier !== 'free') {
      goToSubscription();
    }
  };

  // Handle claiming milestone reward
  const handleClaimMilestone = () => {
    if (showMilestone) {
      setClaimedMilestones([...claimedMilestones, showMilestone.modules]);
      
      // Based on reward type, go to subscription
      if (showMilestone.type === 'trial' || showMilestone.type === 'discount' || showMilestone.type === 'free') {
        goToSubscription();
      }
      setShowMilestone(null);
    }
  };

  // Module selected
  if (selectedModule) {
    return (
      <>
        <ModulePage module={selectedModule} onBack={() => setSelectedModule(null)} onComplete={handleComplete} completed={completed.includes(selectedModule.id)} currentTutor={currentTutor} onOpenChat={openChat} />
        <FloatingChatButton onClick={() => openChat(getTutorForCategory(selectedModule.category))} tutor={getTutorForCategory(selectedModule.category)} />
        {showChat && <AIChatWidget tutor={currentTutor} onClose={() => setShowChat(false)} moduleContext={`Module: ${selectedModule.title}`} />}
      </>
    );
  }

  // Category view
  if (selectedCategory) {
    const catModules = MODULES.filter(m => m.category === selectedCategory);
    const cat = CATEGORIES.find(c => c.id === selectedCategory);
    return (
      <div style={{ minHeight: '100vh', background: '#0f172a', color: '#e2e8f0', padding: '1.5rem' }}>
        <button onClick={() => setSelectedCategory(null)} style={{ background: 'rgba(255,255,255,0.1)', border: 'none', borderRadius: '0.5rem', padding: '0.5rem 1rem', color: '#94a3b8', cursor: 'pointer', marginBottom: '1.5rem' }}>← Back</button>
        <h1 style={{ fontSize: '1.75rem', marginBottom: '0.5rem' }}>{cat?.icon} {cat?.name}</h1>
        <p style={{ color: '#94a3b8', marginBottom: '1.5rem' }}>{catModules.length} modules • {catModules.filter(m => completed.includes(m.id)).length} completed</p>
        <div style={{ display: 'grid', gap: '1rem' }}>
          {catModules.map(m => (
            <div key={m.id} onClick={() => setSelectedModule(m)} style={{ background: 'rgba(30,41,59,0.5)', borderRadius: '0.75rem', padding: '1.25rem', cursor: 'pointer', border: '1px solid rgba(255,255,255,0.05)', display: 'flex', gap: '1rem', alignItems: 'center' }}>
              <span style={{ fontSize: '2rem' }}>{m.icon}</span>
              <div style={{ flex: 1 }}>
                <h3 style={{ margin: 0, fontSize: '1rem' }}>{m.title}</h3>
                <p style={{ margin: '0.25rem 0 0', color: '#94a3b8', fontSize: '0.875rem' }}>{m.description}</p>
              </div>
              {completed.includes(m.id) && <span style={{ color: '#34d399' }}>✓</span>}
            </div>
          ))}
        </div>
        <FloatingChatButton onClick={() => openChat(getTutorForCategory(selectedCategory))} tutor={getTutorForCategory(selectedCategory)} />
        {showChat && <AIChatWidget tutor={currentTutor} onClose={() => setShowChat(false)} moduleContext={`Category: ${cat?.name}`} />}
      </div>
    );
  }

  // Path view
  if (selectedPath) {
    const path = LEARNING_PATHS.find(p => p.id === selectedPath);
    const pathModules = MODULES.filter(m => path?.modules.includes(m.id));
    return (
      <div style={{ minHeight: '100vh', background: '#0f172a', color: '#e2e8f0', padding: '1.5rem' }}>
        <button onClick={() => setSelectedPath(null)} style={{ background: 'rgba(255,255,255,0.1)', border: 'none', borderRadius: '0.5rem', padding: '0.5rem 1rem', color: '#94a3b8', cursor: 'pointer', marginBottom: '1.5rem' }}>← Back</button>
        <div style={{ background: `linear-gradient(135deg, ${path?.color}30 0%, transparent 100%)`, borderRadius: '1rem', padding: '1.5rem', marginBottom: '1.5rem' }}>
          <span style={{ fontSize: '2.5rem' }}>{path?.icon}</span>
          <h1 style={{ fontSize: '1.5rem', margin: '0.75rem 0 0.25rem' }}>{path?.name}</h1>
          <p style={{ color: '#94a3b8', margin: 0 }}>{path?.description}</p>
          <div style={{ marginTop: '1rem', display: 'flex', gap: '1rem', fontSize: '0.875rem', color: '#94a3b8' }}>
            <span>📚 {pathModules.length} modules</span>
            <span>⏱️ {path?.totalTime}</span>
            <span>✓ {pathModules.filter(m => completed.includes(m.id)).length}/{pathModules.length} done</span>
          </div>
        </div>
        <div style={{ display: 'grid', gap: '0.75rem' }}>
          {pathModules.map((m, idx) => (
            <div key={m.id} onClick={() => setSelectedModule(m)} style={{ background: 'rgba(30,41,59,0.5)', borderRadius: '0.75rem', padding: '1rem', cursor: 'pointer', border: '1px solid rgba(255,255,255,0.05)', display: 'flex', gap: '1rem', alignItems: 'center' }}>
              <span style={{ width: '2rem', height: '2rem', borderRadius: '50%', background: completed.includes(m.id) ? '#34d399' : 'rgba(99,102,241,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.875rem', fontWeight: '700' }}>{completed.includes(m.id) ? '✓' : idx + 1}</span>
              <div style={{ flex: 1 }}>
                <h3 style={{ margin: 0, fontSize: '0.9375rem' }}>{m.title}</h3>
                <p style={{ margin: 0, color: '#64748b', fontSize: '0.8125rem' }}>{m.duration}</p>
              </div>
            </div>
          ))}
        </div>
        <FloatingChatButton onClick={() => openChat(AI_TUTORS.main)} tutor={AI_TUTORS.main} />
        {showChat && <AIChatWidget tutor={currentTutor} onClose={() => setShowChat(false)} moduleContext={`Learning Path: ${path?.name}`} />}
      </div>
    );
  }

  // Home view
  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(180deg, #0f172a 0%, #1e1b4b 50%, #0f172a 100%)', color: '#e2e8f0' }}>
      {/* Hero with animated background */}
      <div style={{ 
        background: 'linear-gradient(135deg, rgba(99,102,241,0.15) 0%, rgba(168,85,247,0.1) 50%, rgba(236,72,153,0.1) 100%)', 
        padding: '3rem 1.5rem 2.5rem', 
        textAlign: 'center', 
        borderBottom: '1px solid rgba(255,255,255,0.08)',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Decorative circles */}
        <div style={{ position: 'absolute', top: '-50px', left: '-50px', width: '200px', height: '200px', borderRadius: '50%', background: 'radial-gradient(circle, rgba(99,102,241,0.2) 0%, transparent 70%)', filter: 'blur(40px)' }} />
        <div style={{ position: 'absolute', bottom: '-30px', right: '-30px', width: '150px', height: '150px', borderRadius: '50%', background: 'radial-gradient(circle, rgba(168,85,247,0.2) 0%, transparent 70%)', filter: 'blur(30px)' }} />
        
        <div style={{ position: 'relative', zIndex: 1 }}>
          {/* Logo/Badge */}
          <div style={{ 
            display: 'inline-flex', 
            alignItems: 'center', 
            gap: '0.5rem', 
            background: 'rgba(99,102,241,0.15)', 
            border: '1px solid rgba(99,102,241,0.3)', 
            borderRadius: '2rem', 
            padding: '0.5rem 1rem', 
            marginBottom: '1rem',
            fontSize: '0.875rem',
            color: '#a5b4fc'
          }}>
            <Sparkles size={16} /> Free Learning for Everyone
          </div>
          
          <h1 style={{ 
            fontSize: '2.5rem', 
            fontWeight: '800', 
            margin: '0 0 0.75rem',
            background: 'linear-gradient(135deg, #fff 0%, #a5b4fc 50%, #c4b5fd 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            letterSpacing: '-0.02em'
          }}>
            InvestorQ Academy
          </h1>
          
          <p style={{ 
            color: '#94a3b8', 
            margin: '0 0 1.5rem', 
            maxWidth: '550px', 
            marginLeft: 'auto', 
            marginRight: 'auto',
            fontSize: '1.1rem',
            lineHeight: '1.6'
          }}>
            Master investing with <span style={{ color: '#fff', fontWeight: '600' }}>42 interactive lessons</span>, 
            AI tutors & hands-on practice — completely free
          </p>
          
          {/* Stats */}
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(4, 1fr)',
            gap: '0.75rem', 
            marginBottom: '2rem',
            maxWidth: '500px',
            marginLeft: 'auto',
            marginRight: 'auto'
          }}>
            {[
              { icon: '📚', value: MODULES.length, label: 'Modules' },
              { icon: '✓', value: completed.length, label: 'Done' },
              { icon: '🤖', value: '10', label: 'AI Tutors' },
              { icon: '⏱️', value: '7hrs', label: 'Content' }
            ].map((stat, i) => (
              <div key={i} style={{ 
                background: 'rgba(255,255,255,0.05)', 
                borderRadius: '0.75rem', 
                padding: '0.75rem 0.5rem',
                textAlign: 'center',
                border: '1px solid rgba(255,255,255,0.08)'
              }}>
                <div style={{ fontSize: '1.25rem', marginBottom: '0.25rem' }}>{stat.icon}</div>
                <div style={{ fontSize: '1.25rem', fontWeight: '700', color: '#fff' }}>{stat.value}</div>
                <div style={{ fontSize: '0.625rem', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{stat.label}</div>
              </div>
            ))}
          </div>
          
          {/* Search */}
          <div style={{ maxWidth: '500px', marginLeft: 'auto', marginRight: 'auto', position: 'relative', padding: '0 1rem' }}>
            <div style={{ position: 'absolute', left: '1.75rem', top: '50%', transform: 'translateY(-50%)', color: '#64748b' }}>🔍</div>
            <input 
              type="text" 
              placeholder="Search for any topic..." 
              value={search} 
              onChange={e => setSearch(e.target.value)} 
              style={{ 
                width: '100%', 
                padding: '1rem 1rem 1rem 3rem', 
                background: 'rgba(15,23,42,0.8)', 
                border: '1px solid rgba(255,255,255,0.15)', 
                borderRadius: '1rem', 
                color: '#fff', 
                fontSize: '1rem',
                outline: 'none',
                transition: 'all 0.3s ease',
                boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
                boxSizing: 'border-box'
              }} 
            />
          </div>
        </div>
      </div>

      <div style={{ padding: '2rem 1.5rem 5rem', maxWidth: '1200px', margin: '0 auto' }}>
        {/* Progress Tracker with Upgrade CTA */}
        {completed.length > 0 && (
          <ProgressWithUpgrade 
            completed={completed.length} 
            total={MODULES.length} 
            onUpgrade={() => window.location.href = '/settings?tab=subscription'} 
          />
        )}
        
        {/* Search Results */}
        {search && (
          <div style={{ marginBottom: '2rem' }}>
            <h2 style={{ fontSize: '1.25rem', marginBottom: '1rem' }}>Search Results ({filteredModules.length})</h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '1rem' }}>
              {filteredModules.map(m => (
                <div key={m.id} onClick={() => setSelectedModule(m)} style={{ background: 'rgba(30,41,59,0.5)', borderRadius: '0.75rem', padding: '1rem', cursor: 'pointer', border: '1px solid rgba(255,255,255,0.05)' }}>
                  <span style={{ fontSize: '1.5rem' }}>{m.icon}</span>
                  <h3 style={{ fontSize: '0.9375rem', margin: '0.5rem 0 0.25rem' }}>{m.title}</h3>
                  <p style={{ fontSize: '0.8125rem', color: '#64748b', margin: 0 }}>{m.description}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {!search && (
          <>
            {/* Learning Paths - Redesigned */}
            <section style={{ marginBottom: '3rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem' }}>
                <div>
                  <h2 style={{ fontSize: '1.5rem', fontWeight: '700', margin: '0 0 0.25rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span style={{ background: 'linear-gradient(135deg, #6366f1, #a855f7)', borderRadius: '0.5rem', padding: '0.5rem', display: 'flex' }}><Crown size={20} /></span>
                    Learning Paths
                  </h2>
                  <p style={{ color: '#64748b', margin: 0, fontSize: '0.9rem' }}>Structured journeys from beginner to expert</p>
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: '1.25rem' }}>
                {LEARNING_PATHS.map(path => {
                  const pathModules = MODULES.filter(m => path.modules.includes(m.id));
                  const done = pathModules.filter(m => completed.includes(m.id)).length;
                  const progress = (done / pathModules.length) * 100;
                  return (
                    <div 
                      key={path.id} 
                      onClick={() => setSelectedPath(path.id)} 
                      className="card-hover"
                      style={{ 
                        background: `linear-gradient(135deg, ${path.color}15 0%, rgba(15,23,42,0.9) 100%)`, 
                        borderRadius: '1.25rem', 
                        padding: '1.5rem', 
                        cursor: 'pointer', 
                        border: `1px solid ${path.color}25`,
                        position: 'relative',
                        overflow: 'hidden'
                      }}
                    >
                      {/* Decorative glow */}
                      <div style={{ position: 'absolute', top: '-20px', right: '-20px', width: '100px', height: '100px', background: `radial-gradient(circle, ${path.color}30 0%, transparent 70%)`, filter: 'blur(20px)' }} />
                      
                      <div style={{ position: 'relative', zIndex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem', marginBottom: '1rem' }}>
                          <div style={{ 
                            width: '60px', 
                            height: '60px', 
                            borderRadius: '1rem', 
                            background: `linear-gradient(135deg, ${path.color}40, ${path.color}20)`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '2rem',
                            border: `1px solid ${path.color}30`
                          }}>
                            {path.icon}
                          </div>
                          <div style={{ flex: 1 }}>
                            <h3 style={{ margin: '0 0 0.25rem', fontSize: '1.125rem', fontWeight: '600' }}>{path.name}</h3>
                            <p style={{ margin: 0, fontSize: '0.85rem', color: '#94a3b8', lineHeight: '1.4' }}>{path.description}</p>
                          </div>
                        </div>
                        
                        {/* Progress bar */}
                        <div style={{ marginBottom: '0.75rem' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem', fontSize: '0.8rem' }}>
                            <span style={{ color: '#94a3b8' }}>{path.totalTime}</span>
                            <span style={{ color: path.color, fontWeight: '600' }}>{Math.round(progress)}%</span>
                          </div>
                          <div style={{ background: 'rgba(0,0,0,0.3)', borderRadius: '1rem', height: '8px', overflow: 'hidden' }}>
                            <div style={{ 
                              background: `linear-gradient(90deg, ${path.color}, ${path.color}cc)`, 
                              height: '100%', 
                              width: `${progress}%`, 
                              transition: 'width 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
                              borderRadius: '1rem'
                            }} />
                          </div>
                        </div>
                        
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{ fontSize: '0.8rem', color: '#64748b' }}>{done}/{pathModules.length} modules</span>
                          <span style={{ color: path.color, fontSize: '0.85rem', fontWeight: '500', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                            Start learning <ArrowRight size={14} />
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>

            {/* Categories - Redesigned */}
            <section style={{ marginBottom: '3rem' }}>
              <div style={{ marginBottom: '1.5rem' }}>
                <h2 style={{ fontSize: '1.5rem', fontWeight: '700', margin: '0 0 0.25rem' }}>📂 Browse by Category</h2>
                <p style={{ color: '#64748b', margin: 0, fontSize: '0.9rem' }}>Find exactly what you want to learn</p>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '1rem' }}>
                {CATEGORIES.map(cat => {
                  const catModules = MODULES.filter(m => m.category === cat.id);
                  const done = catModules.filter(m => completed.includes(m.id)).length;
                  const isComplete = done === catModules.length && catModules.length > 0;
                  return (
                    <div 
                      key={cat.id} 
                      onClick={() => setSelectedCategory(cat.id)} 
                      className="card-hover"
                      style={{ 
                        background: 'rgba(30,41,59,0.5)', 
                        borderRadius: '1rem', 
                        padding: '1.25rem', 
                        cursor: 'pointer', 
                        border: isComplete ? `2px solid ${cat.color}` : '1px solid rgba(255,255,255,0.08)', 
                        textAlign: 'center',
                        position: 'relative'
                      }}
                    >
                      {isComplete && <div style={{ position: 'absolute', top: '-8px', right: '-8px', background: '#22c55e', borderRadius: '50%', width: '24px', height: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.75rem', boxShadow: '0 2px 8px rgba(34,197,94,0.4)' }}>✓</div>}
                      <div style={{ 
                        width: '50px', 
                        height: '50px', 
                        margin: '0 auto 0.75rem',
                        borderRadius: '0.75rem',
                        background: `linear-gradient(135deg, ${cat.color}30, ${cat.color}10)`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '1.5rem'
                      }}>
                        {cat.icon}
                      </div>
                      <h3 style={{ fontSize: '0.9rem', margin: '0 0 0.5rem', fontWeight: '600' }}>{cat.name}</h3>
                      <div style={{ 
                        display: 'inline-flex', 
                        alignItems: 'center', 
                        gap: '0.25rem',
                        background: 'rgba(255,255,255,0.05)',
                        padding: '0.25rem 0.75rem',
                        borderRadius: '1rem',
                        fontSize: '0.75rem',
                        color: '#94a3b8'
                      }}>
                        <span style={{ color: done > 0 ? '#22c55e' : '#64748b' }}>{done}</span>
                        <span>/</span>
                        <span>{catModules.length}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>

            {/* AI Tutors - Redesigned */}
            <section style={{ marginBottom: '3rem' }}>
              <div style={{ marginBottom: '1.5rem' }}>
                <h2 style={{ fontSize: '1.5rem', fontWeight: '700', margin: '0 0 0.25rem' }}>🤖 Meet Your AI Tutors</h2>
                <p style={{ color: '#64748b', margin: 0, fontSize: '0.9rem' }}>Personal experts ready to answer your questions</p>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '1rem' }}>
                {Object.values(AI_TUTORS).slice(0, 6).map(tutor => (
                  <div 
                    key={tutor.id} 
                    onClick={() => openChat(tutor)} 
                    className="card-hover"
                    style={{ 
                      background: `linear-gradient(180deg, ${tutor.color}15 0%, rgba(15,23,42,0.9) 100%)`, 
                      borderRadius: '1rem', 
                      padding: '1.5rem', 
                      cursor: 'pointer', 
                      border: `1px solid ${tutor.color}20`, 
                      textAlign: 'center',
                      position: 'relative',
                      overflow: 'hidden'
                    }}
                  >
                    {/* Online indicator */}
                    <div style={{ position: 'absolute', top: '1rem', right: '1rem', width: '8px', height: '8px', borderRadius: '50%', background: '#22c55e', boxShadow: '0 0 8px rgba(34,197,94,0.6)' }} />
                    
                    <div style={{ 
                      width: '70px', 
                      height: '70px', 
                      margin: '0 auto 1rem',
                      borderRadius: '50%',
                      background: `linear-gradient(135deg, ${tutor.color}50, ${tutor.color}30)`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '2.25rem',
                      border: `3px solid ${tutor.color}40`,
                      boxShadow: `0 8px 24px ${tutor.color}30`
                    }}>
                      {tutor.avatar}
                    </div>
                    <h3 style={{ fontSize: '1rem', margin: '0 0 0.25rem', fontWeight: '600' }}>{tutor.name}</h3>
                    <p style={{ fontSize: '0.8rem', color: '#64748b', margin: '0 0 1rem' }}>
                      {tutor.id === 'main' ? 'General Guide' : tutor.id.charAt(0).toUpperCase() + tutor.id.slice(1) + ' Expert'}
                    </p>
                    <button 
                      className="btn-hover"
                      style={{ 
                        width: '100%',
                        padding: '0.625rem',
                        background: `linear-gradient(135deg, ${tutor.color}, ${tutor.color}cc)`,
                        border: 'none',
                        borderRadius: '0.5rem',
                        color: 'white',
                        fontWeight: '600',
                        fontSize: '0.8rem',
                        cursor: 'pointer'
                      }}
                    >
                      💬 Chat Now
                    </button>
                  </div>
                ))}
              </div>
            </section>

            {/* All Modules - Redesigned */}
            <section>
              <div style={{ marginBottom: '1.5rem' }}>
                <h2 style={{ fontSize: '1.5rem', fontWeight: '700', margin: '0 0 0.25rem' }}>📚 All Modules</h2>
                <p style={{ color: '#64748b', margin: 0, fontSize: '0.9rem' }}>{MODULES.length} lessons covering everything you need</p>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '1rem' }}>
                {MODULES.map((m, index) => {
                  const isCompleted = completed.includes(m.id);
                  const category = CATEGORIES.find(c => c.id === m.category);
                  return (
                    <div 
                      key={m.id} 
                      onClick={() => setSelectedModule(m)} 
                      className="card-hover"
                      style={{ 
                        background: 'rgba(30,41,59,0.5)', 
                        borderRadius: '1rem', 
                        padding: '1.25rem', 
                        cursor: 'pointer', 
                        border: isCompleted ? '1px solid rgba(34,197,94,0.3)' : '1px solid rgba(255,255,255,0.08)', 
                        position: 'relative',
                        animation: `fadeIn 0.3s ease ${index * 0.02}s both`
                      }}
                    >
                      {isCompleted && (
                        <div style={{ 
                          position: 'absolute', 
                          top: '1rem', 
                          right: '1rem', 
                          background: 'linear-gradient(135deg, #22c55e, #16a34a)',
                          borderRadius: '50%',
                          width: '28px',
                          height: '28px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'white',
                          fontSize: '0.875rem',
                          fontWeight: 'bold',
                          boxShadow: '0 4px 12px rgba(34,197,94,0.3)'
                        }}>✓</div>
                      )}
                      
                      <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start' }}>
                        <div style={{ 
                          width: '50px', 
                          height: '50px', 
                          borderRadius: '0.75rem',
                          background: `linear-gradient(135deg, ${category?.color || '#6366f1'}25, ${category?.color || '#6366f1'}10)`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '1.5rem',
                          flexShrink: 0
                        }}>
                          {m.icon}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <h3 style={{ fontSize: '1rem', margin: '0 0 0.375rem', fontWeight: '600', paddingRight: isCompleted ? '2rem' : 0 }}>{m.title}</h3>
                          <p style={{ fontSize: '0.85rem', color: '#94a3b8', margin: '0 0 0.75rem', lineHeight: '1.4' }}>{m.description}</p>
                          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                            <span style={{ 
                              background: 'rgba(99,102,241,0.15)', 
                              color: '#a5b4fc',
                              padding: '0.25rem 0.625rem', 
                              borderRadius: '0.375rem',
                              fontSize: '0.75rem',
                              fontWeight: '500'
                            }}>⏱ {m.duration}</span>
                            <span style={{ 
                              background: m.difficulty === 'Beginner' ? 'rgba(34,197,94,0.15)' : m.difficulty === 'Intermediate' ? 'rgba(251,191,36,0.15)' : 'rgba(239,68,68,0.15)', 
                              color: m.difficulty === 'Beginner' ? '#4ade80' : m.difficulty === 'Intermediate' ? '#fbbf24' : '#f87171',
                              padding: '0.25rem 0.625rem', 
                              borderRadius: '0.375rem',
                              fontSize: '0.75rem',
                              fontWeight: '500'
                            }}>{m.difficulty}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
          </>
        )}

{/* Podcast Section */}
            <section style={{ marginTop: '3rem' }}>
              <div style={{ 
                padding: '2rem',
                background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.08), rgba(139, 92, 246, 0.05))',
                borderRadius: '1.5rem',
                border: '1px solid rgba(99, 102, 241, 0.15)'
              }}>
                <div style={{ marginBottom: '1.5rem' }}>
                  <h2 style={{ 
                    margin: '0 0 0.5rem', 
                    fontSize: '1.5rem', 
                    fontWeight: '700',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem'
                  }}>
                    🎙️ Learning Podcasts
                    <span style={{
                      padding: '0.25rem 0.75rem',
                      background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                      borderRadius: '1rem',
                      fontSize: '0.7rem',
                      fontWeight: '600',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em'
                    }}>
                      NEW
                    </span>
                  </h2>
                  <p style={{ margin: 0, color: '#94a3b8', fontSize: '0.95rem' }}>
                    Learn on the go with Aryan's expert investment insights
                  </p>
                </div>
                
                <PodcastLibrary />
              </div>
            </section>

            {/* Video Library Section */}
            <section style={{ marginBottom: '3rem', marginTop: '1rem' }}>
              <div style={{
                background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.1) 0%, rgba(99, 102, 241, 0.05) 100%)',
                borderRadius: '1rem',
                padding: '1rem',
                border: '1px solid rgba(139, 92, 246, 0.2)',
                overflow: 'hidden'
              }}>
                <div style={{ marginBottom: '1rem' }}>
                  <h2 style={{ 
                    margin: '0 0 0.5rem', 
                    fontSize: '1.25rem', 
                    fontWeight: '700',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    flexWrap: 'wrap'
                  }}>
                    🎬 Video Library
                    <span style={{
                      background: 'linear-gradient(135deg, #8b5cf6, #a855f7)',
                      padding: '0.25rem 0.5rem',
                      borderRadius: '0.375rem',
                      fontSize: '0.65rem',
                      fontWeight: '600',
                      letterSpacing: '0.05em'
                    }}>
                      NEW
                    </span>
                  </h2>
                  <p style={{ margin: 0, color: '#94a3b8', fontSize: '0.85rem' }}>
                    Watch tutorials and feature walkthroughs for InvestorQ
                  </p>
                </div>
                
                <VideoLibrary />
              </div>
            </section>

        
      </div>

      {/* Floating Chat */}
      <FloatingChatButton onClick={() => openChat(AI_TUTORS.main)} tutor={AI_TUTORS.main} />
      {showChat && <AIChatWidget tutor={currentTutor} onClose={() => setShowChat(false)} moduleContext="Learning Academy Home" />}
      
      {/* Conversion Components */}
      {showUpgradePrompt && lastCompletedModule && (
        <UpgradePrompt 
          moduleId={lastCompletedModule} 
          onClose={() => setShowUpgradePrompt(false)} 
          onTryFeature={handleTryFeature}
          onUpgrade={handleUpgrade}
        />
      )}
      
      {showMilestone && (
        <MilestoneCelebration 
          milestone={showMilestone} 
          onClose={() => setShowMilestone(null)} 
          onClaimReward={handleClaimMilestone}
        />
      )}
      
      {showPricing && (
        <PricingTable 
          onSelectTier={handleSelectTier} 
          onClose={() => setShowPricing(false)}
        />
      )}
      
      {/* Sticky Upgrade Banner - ALWAYS VISIBLE */}
      <StickyUpgradeBanner 
        onUpgrade={() => window.location.href = '/settings?tab=subscription'} 
        completedCount={completed.length}
      />
    </div>
  );
};

export default LearnAcademy;